import * as THREE from "three";
import { disposeObject3D } from "../assets/disposeObject3D";
import { getCurrentGraphicsPreset } from "../game/GraphicsQuality";
import { mergeGeometries as mergeBufferGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { CharacterLoader } from "../assets/CharacterLoader";
import { ModelLoader } from "../assets/ModelLoader";
import { getFurnitureDef } from "../data/furnitureCatalog";
import { CharacterAnimator, type AnimatedCharacter, type CharacterAction, type SkeletalDriver } from "./CharacterAnimator";
import { sharedRiggedLoader, riggedStaffModel } from "./RiggedCharacter";
import { fitFurniture, snapToAdjacentWall } from "../assets/fitFurniture";
import { WeatherEffects, type WeatherKind } from "./WeatherEffects";
import { TextureService } from "./TextureService";
import { buildPerimeterWallSegments } from "./wallBuilder";
import { buildStaircaseFlight, buildBrokenStairwell, buildSupplyCounterMesh } from "./interiorPieces";

/** Plaque visual catalogs — each id is a small string the modal exposes
 * as a radio button. Picked to read as warm cosy bistro defaults but
 * give the player enough variety for a personal vibe. Exported for the
 * RestaurantSignModal so it can render matching swatches. */
export const FONT_FAMILIES: Record<string, string> = {
  serif:   `'Georgia', 'Times New Roman', serif`,
  sans:    `'Helvetica Neue', 'Arial', sans-serif`,
  script:  `'Brush Script MT', 'Lucida Handwriting', cursive`,
  display: `'Impact', 'Arial Black', sans-serif`,
};
export const FONT_LABELS: Record<string, string> = {
  serif:   "Classic Serif",
  sans:    "Modern Sans",
  script:  "Handwritten",
  display: "Bold Display",
};
/** Hex strings for canvas2d. */
export const TEXT_COLORS: Record<string, string> = {
  cream:    "#fff5dc",
  gold:     "#f5c14a",
  white:    "#ffffff",
  red:      "#e85a5a",
  mint:     "#a8e2c0",
  lavender: "#d2c5ec",
};
export const PLAQUE_BG: Record<string, string> = {
  dark:  "#1d1813",
  wood:  "#5a3a22",
  slate: "#384047",
  brass: "#3a2e1a",
};
/** Frame mesh colour as a 0xRRGGBB integer — pairs visually with the
 * matching plaque-background id. */
export const PLAQUE_FRAME: Record<string, number> = {
  dark:  0x2a1f17,
  wood:  0x3a2410,
  slate: 0x232a32,
  brass: 0xc8a050,
};
export const PLAQUE_LABELS: Record<string, string> = {
  dark:  "Dark Wood",
  wood:  "Walnut",
  slate: "Slate Blue",
  brass: "Brass Trim",
};

/** Linearly interpolate between two RGB integers by t in [0,1]. */
/** Tiny pseudo-RNG used for stable procedural city layout — same
 * seed → same town across reloads. Algorithm: mulberry32 (one of
 * the smallest decent 32-bit PRNGs). */
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return (): number => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function mixColors(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

/**
 * The 3D world. A minimal demo restaurant: floor, walls, and a few pieces
 * of Kenney furniture placed at fixed positions so we can verify the GLTF
 * loader and confirm the overall scene reads as "restaurant".
 *
 * Phase 3+ will replace the hard-coded placements with a proper
 * grid-driven building system that mirrors the 2D game.
 */
/** Perimeter wall direction — picks which axis the wall runs along
 * (front / back are X-axis at z=±5.5; left / right are Z-axis at
 * x=±5.5 / -4.5) and which face of the box geometry is the outdoor
 * face when building the multi-material array. */
type WallDir = "front" | "back" | "left" | "right";

/** Live state for one perimeter wall. Walls are rebuilt as a stack of
 * segment + lintel + sill meshes around each door (full-height cut)
 * and window (sill + lintel partial cut). currentMat tracks whether
 * the wall is currently rendered solid (interior+exterior multi-mat)
 * or as the ghost see-through, so the camera-driven swap doesn't
 * thrash. */
interface PerimeterWallState {
  meshes: THREE.Mesh[];
  currentMat: "solid" | "ghost";
  doors: number[];
  windows: number[];
  /** World-space Y offset added to every wall segment's centre Y.
   * 0 for the ground floor (Y=1.5 centre, 0..3 bounds). For storey
   * idx≥1 this is idx * STOREY_HEIGHT, so floor 1 walls sit at Y=3..6
   * centred at 4.5 and the same window-cut maths still works. */
  yOffset?: number;
  /** Object3D each wall segment is parented to. Ground floor goes
   * straight into the scene; upper floors go into their storey group
   * so visibility + theming track the focused floor. */
  parent?: THREE.Object3D;
  /** The MeshStandardMaterial for solid segments on this floor.
   * Ground floor uses the shared wallMat; upper storeys use the
   * per-storey clone so DecorModal can theme each floor. */
  wallMatRef?: THREE.MeshStandardMaterial;
}

export class WorldScene {
  readonly threeScene = new THREE.Scene();
  readonly loader = new ModelLoader();
  readonly characterLoader = new CharacterLoader(this.loader);
  readonly animator = new CharacterAnimator();
  /** Off-thread procedural-texture painter. Used to push ~100 shop-sign
   * canvas draws onto a worker at boot — that paint pass otherwise
   * burns ~300 ms on the main thread during city construction. */
  private readonly textureService = new TextureService();
  /** Exposed for StaffRouter to drive their state machines. Populated
   * asynchronously during populateCharacters — may be undefined for the
   * first frame or two while GLBs load. */
  chefChar?: AnimatedCharacter;
  waiterChar?: AnimatedCharacter;
  errandChar?: AnimatedCharacter;
  /** Per-station visual effects, keyed by furniture uid. Every cook
   * station gets a small group pinned to the top of its model;
   * Engine.update reconciles the map each frame via syncStationEffects,
   * then setActiveStations(uids) flips the per-station visibility based
   * on which chefs are actively working there. Each variant has its own
   * mesh / light layout and animation in update(dt):
   *  - gas stove   → orange flame + flicker
   *  - electric    → blue induction glow + flicker
   *  - toaster     → red coil glow on top, pulses while in use
   *  - coffee      → 3 white steam puffs rising in sequence
   *  - blender     → small wobble of the model's top accessory
   *  - microwave   → soft yellow inside-light glow (currently unused —
   *                  no recipe gates on microwave yet) */
  private stationEffects = new Map<string, {
    group: THREE.Group;
    variant: "gas" | "electric" | "toaster" | "coffee" | "blender" | "microwave" | "hood";
    flameMesh?: THREE.Mesh;
    flameLight?: THREE.PointLight;
    // Steam puffs for coffee: each has a phase offset so they rise in
    // sequence rather than as a single blob.
    steamPuffs?: { mesh: THREE.Mesh; phase: number }[];
    // Wobble accessory for blender: a small disc that rotates + pulses.
    wobbleMesh?: THREE.Mesh;
  }>();
  private stationEffectPhase = 0;
  /** The hinged door panel (sub-object of the procedural front-door
   * group, exposed via userData.panel). Rotating this swings the door
   * around its hinge while the frame stays put. */
  private doorPanel?: THREE.Object3D;
  /** Door open amount [0,1] — lerps toward doorOpenTarget every frame. */
  private doorOpenAmount = 0;
  private doorOpenTarget = 0;
  /** World position of the stove and the plate-pickup spot. Used by the
   * StaffRouter to send chef/waiter to the right places. The chef stands
   * here (z=-3, one tile in front of the stove furniture) to "cook". */
  readonly stovePos = new THREE.Vector2(0, -3.0);
  readonly pickupPos = new THREE.Vector2(0.5, -2.8);
  /** The actual stove furniture's world position (z=-4, back wall). Used
   * to position the cooking flame ON the stove, not in front of where
   * the chef stands. */
  readonly stoveFurniturePos = new THREE.Vector2(0, -4);
  /** Where the errand helper drops off groceries when they return —
   * a small supply counter at the back-left of the room (see
   * buildSupplyCounter in addBuilding). */
  readonly supplyCounterPos = new THREE.Vector2(-3, -3.2);
  /** Front door world spot. Used both for the "anyone near door" check
   * (which swings the door open) and as the errand helper's entry/exit
   * waypoint — they walk through here on their way to the pavement
   * and again on the way back. */
  readonly doorPos = new THREE.Vector2(0, 5);
  /** Resolves once the staff characters are loaded — so Engine can build
   * the StaffRouter at the right moment. Created synchronously in the
   * constructor so any code that grabs the reference right after
   * `new WorldScene()` waits for the REAL load, not an already-resolved
   * sentinel. (We hit this exact bug — Engine's `.then(...)` was firing
   * on the resolved placeholder before chefChar/waiterChar even existed,
   * leaving the stub router active forever.) */
  staffReady!: Promise<void>;
  private resolveStaffReady!: () => void;
  /** Resolves once the demo restaurant furniture is in the scene — so
   * Engine can register every demo piece in the FurnitureRegistry and
   * therefore make them moveable / sellable like player-placed items.
   * Same constructor-init pattern as staffReady — see above. */
  demoReady!: Promise<void>;
  private resolveDemoReady!: () => void;
  /** Snapshot of the demo placements (id + cell + rotation + model)
   * filled in by populateDemoRestaurant. */
  readonly demoPlacements: { defId: string; x: number; z: number; rotY: number; model: THREE.Object3D }[] = [];

  /** Holds the placeholder shells for OTHER players' buildings on
   * the shared city map. Player's own restaurant continues to
   * render at world origin via the legacy code path; this group
   * adds visual markers for every other claimed/unclaimed plot
   * so the player sees the city around them. P2.4 stops here;
   * full per-other-player restaurant interiors come later. */
  cityBuildings = new THREE.Group();
  /** Parent group for all SHARED city content (grass, avenues,
   * scenery houses, other plots' shells, pedestrians). The player's
   * own restaurant + characters stay at the threeScene root in their
   * legacy origin-centred coordinates. setOwnedPlotOffset(x, z)
   * positions this group at (-x, 0, -z) so the player's plot lines
   * up with the local-origin restaurant — visually, the player IS
   * standing on the plot they claimed, with everyone else arrayed
   * around them at their absolute world coordinates. */
  worldRoot = new THREE.Group();
  /** Camera anchor for the player's own plot — used by IsoCamera
   * to point at the correct spot when the legacy hardcoded (0,0)
   * doesn't match the claimed building's coordinates. */
  ownedPlotAnchor: THREE.Vector2 = new THREE.Vector2(0, 0);

  constructor() {
    // Create the staff/demo "ready" promises SYNCHRONOUSLY here, before
    // any other code can grab a reference. The async populate functions
    // resolve them when their work is done. We used to lazily assign new
    // promises inside the async functions, which left any synchronous
    // consumer holding a permanently-resolved `Promise.resolve()` sentinel
    // — Engine then fired its staffReady.then(...) immediately, found no
    // chefChar/waiterChar yet, and pinned itself to the stub router.
    this.staffReady = new Promise((r) => { this.resolveStaffReady = r; });
    this.demoReady = new Promise((r) => { this.resolveDemoReady = r; });

    // Fog pushed out from 30..80 → 100..250 so the expanded city
    // doesn't disappear into the haze 80m from the camera. At the
    // current iso zoom the player can see ~150m around them; the
    // city ends at ±140 so the fog now starts beyond the buildings
    // and only kicks in for the void past the terrain edge.
    this.threeScene.fog = new THREE.Fog(0xd8c4a3, 100, 250);
    this.addLighting();
    // World root: shared city geometry parented here so a single
    // position assignment can shift the whole map relative to the
    // restaurant's local-origin coordinate system.
    this.threeScene.add(this.worldRoot);
    this.weatherEffects = new WeatherEffects(this.threeScene);
    this.addBuilding();
    // City buildings (other players' plots) live in their own group so
    // they can be re-populated when SpacetimeClient pushes updates
    // without disturbing the player's own restaurant. Parented to
    // worldRoot so they shift with the rest of the shared city.
    this.worldRoot.add(this.cityBuildings);
    // Per-station effects (flames, toaster glow, coffee steam, etc.)
    // are created lazily by syncStationEffects() once each station
    // is placed — no global state to set up here.
    void this.populateDemoRestaurant();
  }

  update(dt: number): void {
    this.animator.update(dt);
    // Per-station effect animation — dispatched by variant so a stove
    // flickers, a coffee machine puffs steam, a blender wobbles, etc.
    // Single phase drives them all so kitchen visuals feel synchronized.
    if (this.stationEffects.size > 0) {
      this.stationEffectPhase += dt;
      const flick = 0.85 + Math.sin(this.stationEffectPhase * 22) * 0.1 + Math.random() * 0.1;
      for (const e of this.stationEffects.values()) {
        if (!e.group.visible) continue;
        switch (e.variant) {
          case "gas":
          case "electric": {
            if (e.flameMesh) e.flameMesh.scale.setScalar(flick);
            break;
          }
          case "toaster": {
            // Pulse the coil glow — slow breathing on top of the burner
            // colour so it reads as "heating" rather than just "on".
            if (e.flameMesh) {
              const mat = e.flameMesh.material as THREE.MeshStandardMaterial;
              if (mat && "emissiveIntensity" in mat) {
                mat.emissiveIntensity = 1.6 + Math.sin(this.stationEffectPhase * 7) * 0.6;
              }
            }
            break;
          }
          case "coffee": {
            // Steam puffs rise + fade + reset. Each puff has its own
            // phase offset so they leave the spout in sequence.
            if (e.steamPuffs) {
              for (const puff of e.steamPuffs) {
                puff.phase += dt * 0.8;
                if (puff.phase > 1) puff.phase -= 1;
                const p = puff.phase;
                puff.mesh.position.y = 0.1 + p * 0.45;
                puff.mesh.scale.setScalar(0.6 + p * 0.9);
                const mat = puff.mesh.material as THREE.MeshStandardMaterial;
                if (mat && "opacity" in mat) {
                  mat.opacity = Math.max(0, 0.55 * (1 - p));
                }
              }
            }
            break;
          }
          case "blender": {
            // Spin the accessory disc + a slight Y-bounce so the
            // model reads as actively blending.
            if (e.wobbleMesh) {
              e.wobbleMesh.rotation.y = this.stationEffectPhase * 14;
              e.wobbleMesh.position.y = 0.08 + Math.sin(this.stationEffectPhase * 24) * 0.015;
            }
            break;
          }
          case "microwave":
            // Emissive-only now (no cast light); nothing to animate until
            // a microwave recipe + glow mesh is added.
            break;
        }
      }
    }
    // Lerp door open amount toward target and apply rotation to the
    // hinged panel only (the frame stays put).
    if (this.doorPanel) {
      const lerpSpeed = 3.0; // ~0.3s open/close
      const diff = this.doorOpenTarget - this.doorOpenAmount;
      const step = Math.sign(diff) * Math.min(Math.abs(diff), lerpSpeed * dt);
      this.doorOpenAmount += step;
      // Panel hinge swings -90° (opens inward). Negative because the hinge
      // is at the left edge and we want the right edge to swing into the room.
      this.doorPanel.rotation.y = -this.doorOpenAmount * Math.PI / 2;
    }
  }

  /** Tell the front door to open (true) or close (false). Engine calls
   * this every frame based on whether any character is near the door. */
  setDoorOpen(open: boolean): void {
    this.doorOpenTarget = open ? 1 : 0;
  }

  /** Re-capture the door panel reference from a freshly-loaded door
   * model. Needed when a save is restored: the demo's door (and its
   * panel) is removed from the scene, and the save's restored door
   * comes in without going through populateDemoRestaurant — so without
   * this call, setDoorOpen mutates an orphaned mesh and the door looks
   * stuck shut. */
  /** Per-doorway state for the placed `int-doorway` items — each one
   * is animated independently based on whether any character is
   * standing close to it. The panel ref is captured lazily from the
   * model's userData (same convention as the front door). */
  private internalDoorState = new Map<string, { openAmount: number }>();

  /** Reconcile the placed-interior-doorway list and animate each
   * panel toward the requested open / closed state. Engine calls
   * this once per frame after walking the registry — we don't poll
   * the registry from inside the scene because Engine already has a
   * cheap proximity test against guests + staff. */
  updateInternalDoors(doors: readonly { uid: string; model: THREE.Object3D; open: boolean }[], dt: number): void {
    const live = new Set<string>();
    for (const d of doors) {
      live.add(d.uid);
      // Re-resolve the hinge panel from the CURRENT model every frame. A door
      // model can be re-created underneath a stable uid — e.g. a neighborhood
      // re-render (favorite/unfavorite → refreshNeighborhood) rebuilds the plot
      // and swaps in a fresh door object. If we cached the panel on first sight
      // (the old behaviour), we'd keep rotating the now-detached old panel while
      // the live door sat frozen shut — exactly the "doors stopped opening" bug.
      // The hinge is normally on the model's own userData, but the registry may
      // wrap the door in a positioning/rotation container, so fall back to a
      // descendant scan (that's why the front door needs it).
      let panel = (d.model.userData as { panel?: THREE.Object3D }).panel;
      if (!panel) {
        d.model.traverse((o) => {
          if (panel) return;
          const p = (o.userData as { panel?: THREE.Object3D } | undefined)?.panel;
          if (p) panel = p;
        });
      }
      if (!panel) continue;
      let entry = this.internalDoorState.get(d.uid);
      if (!entry) {
        entry = { openAmount: 0 };
        this.internalDoorState.set(d.uid, entry);
      }
      const target = d.open ? 1 : 0;
      // Lerp toward the target. 6/sec means the door is open in ~0.17s
      // — feels snappy enough that the guest doesn't visibly clip it.
      const speed = 6;
      entry.openAmount += (target - entry.openAmount) * Math.min(1, dt * speed);
      panel.rotation.y = -entry.openAmount * Math.PI / 2;
    }
    for (const uid of [...this.internalDoorState.keys()]) {
      if (!live.has(uid)) this.internalDoorState.delete(uid);
    }
  }

  /** True once the front-door's hinge panel has been captured, so Engine can
   * stop retrying the lazy capture. */
  hasDoorPanel(): boolean {
    return this.doorPanel != null;
  }

  attachDoorPanel(model: THREE.Object3D): void {
    let panel = model.userData?.panel as THREE.Object3D | undefined;
    if (!panel) {
      // The hinge may sit on a NESTED child if the registry wrapped the door
      // model in a positioning/rotation container — search descendants so the
      // capture still succeeds.
      model.traverse((o) => {
        if (panel) return;
        const p = (o.userData as { panel?: THREE.Object3D } | undefined)?.panel;
        if (p) panel = p;
      });
    }
    if (panel) {
      this.doorPanel = panel;
      // Reset open state so the new panel doesn't snap to whatever
      // angle the previous one was at.
      this.doorOpenAmount = 0;
      this.doorOpenTarget = 0;
      panel.rotation.y = 0;
    }
  }

  /** Reconcile the per-station effect map with the registry's current
   * cook-station list. Engine calls this each frame; it builds the
   * right variant effect for every newly-placed station and removes
   * effects for stations that have been sold or moved. */
  syncStationEffects(stations: readonly { uid: string; defId: string; model: THREE.Object3D }[]): void {
    const live = new Set(stations.map((s) => s.uid));
    for (const uid of [...this.stationEffects.keys()]) {
      if (!live.has(uid)) {
        const f = this.stationEffects.get(uid)!;
        this.threeScene.remove(f.group);
        disposeObject3D(f.group); // free the effect's fresh geo + materials
        this.stationEffects.delete(uid);
      }
    }
    for (const s of stations) {
      if (this.stationEffects.has(s.uid)) {
        // Already have an effect for this station — but the model
        // might have been moved (move-mode) or its host repositioned
        // (surface placement). Refresh the world anchor.
        this.alignEffectToModel(this.stationEffects.get(s.uid)!.group, s.model);
        continue;
      }
      const effect = this.buildStationEffect(s.defId);
      if (!effect) continue;
      this.alignEffectToModel(effect.group, s.model);
      this.threeScene.add(effect.group);
      this.stationEffects.set(s.uid, effect);
    }
  }

  /** Flip per-station effect visibility. `uids` is the set of stations
   * with a chef ACTIVELY cooking (router.getCookingStoveUids()).
   * Stations outside the set go dark / idle. */
  setActiveStations(uids: ReadonlySet<string>): void {
    for (const [uid, f] of this.stationEffects) {
      f.group.visible = uids.has(uid);
    }
  }

  /** True if at least one station effect is currently visible — i.e.
   * a chef is cooking somewhere right now. Engine uses this to drive
   * the kitchen sizzle SFX loop. */
  isAnyStationActive(): boolean {
    for (const f of this.stationEffects.values()) if (f.group.visible) return true;
    return false;
  }

  /** The set of station variants currently active (visible flame /
   * glow / steam / wobble). Engine reads this each tick and toggles
   * the per-variant SFX loops (gas vs electric stove vs coffee vs
   * blender etc.) so the player hears whichever appliances are in
   * use, not a single generic "cooking" hiss. */
  getActiveStationVariants(): Set<"gas" | "electric" | "toaster" | "coffee" | "blender" | "microwave" | "hood"> {
    const out = new Set<"gas" | "electric" | "toaster" | "coffee" | "blender" | "microwave" | "hood">();
    for (const f of this.stationEffects.values()) {
      if (f.group.visible) out.add(f.variant);
    }
    return out;
  }

  /** Build the per-variant effect group for a given station defId.
   * Returns undefined for ids without a built-in visual (e.g. counter
   * — the chef just stands there chopping, no glow needed). */
  private buildStationEffect(defId: string): {
    group: THREE.Group;
    variant: "gas" | "electric" | "toaster" | "coffee" | "blender" | "microwave" | "hood";
    flameMesh?: THREE.Mesh;
    flameLight?: THREE.PointLight;
    steamPuffs?: { mesh: THREE.Mesh; phase: number }[];
    wobbleMesh?: THREE.Mesh;
  } | undefined {
    switch (defId) {
      case "stove": return this.buildStoveFlameEffect("gas");
      case "stove-electric": return this.buildStoveFlameEffect("electric");
      case "toaster": return this.buildToasterGlowEffect();
      case "coffee-machine": return this.buildCoffeeSteamEffect();
      case "blender": return this.buildBlenderWobbleEffect();
      case "microwave": return this.buildMicrowaveGlowEffect();
      case "kitchen-hood":
      case "kitchen-hood-l":
        return this.buildHoodLightEffect();
      default: return undefined;
    }
  }

  /** Range hood — a static warm-white downlight that switches on while
   * a chef cooks below. No flicker (user explicitly asked for "a static
   * one"). Engine.update walks the registry each frame, finds active
   * stoves, then turns on any hood that's positioned directly above an
   * active one (same X column, close Z).
   *
   * Uses a SpotLight (not PointLight) so the illumination is contained
   * BELOW the hood's bottom surface — a real range-hood bulb shines
   * down onto the burners, not up into the kitchen ceiling. The cone
   * is angled wide enough to cover the full stove tile underneath. */
  private buildHoodLightEffect() {
    const group = new THREE.Group();
    group.visible = false;
    // alignEffectToModel anchors the group at the model's TOP. We push
    // the spotlight source CLEARLY below the hood mesh — a position
    // overlapping the geometry is fine for a PointLight (it radiates
    // out anyway) but for a SpotLight whose cone direction depends on
    // the source-to-target vector it's safer to put the source in open
    // air. ~0.65 m below the top puts it just under a typical hood,
    // close to where a real range bulb hangs.
    const SRC_Y = -0.65;
    // Perf: no SpotLight. A cast light here would add a NUM_SPOT_LIGHTS
    // axis to every lit material's shader AND swing the active light
    // count whenever a hood switches on (cooking) or its floor is
    // revealed — a full recompile each time (minutes on a 1050 Ti).
    // The self-lit bulb carries the visual; emissive is boosted so it
    // still reads as a lit range-hood bulb.
    const bulb = new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 12, 12),
      new THREE.MeshStandardMaterial({
        color: 0xfff8e0,
        emissive: 0xfff0c8,
        emissiveIntensity: 2.6,
      }),
    );
    bulb.position.set(0, SRC_Y, 0);
    group.add(bulb);
    // No flameLight reference — hood doesn't flicker, and the stored
    // type is THREE.PointLight (used by stove / toaster animation).
    // The group's visible flag is enough to switch the light on/off.
    return { group, variant: "hood" as const };
  }

  /** Gas / electric stove flame — sphere + point light. Gas reads
   * warm-orange, electric reads cool-blue induction so the player can
   * tell at a glance which appliance is which. */
  private buildStoveFlameEffect(variant: "gas" | "electric") {
    const palette = variant === "electric"
      ? { color: 0x4d8eff, emissive: 0x1d4fc8, light: 0x88aaff }
      : { color: 0xff7a3c, emissive: 0xff5500, light: 0xff8844 };
    const group = new THREE.Group();
    group.visible = false;
    const flameMesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 12, 12),
      new THREE.MeshStandardMaterial({
        color: palette.color,
        emissive: palette.emissive,
        emissiveIntensity: 1.8,
        transparent: true,
        opacity: 0.85,
      }),
    );
    group.add(flameMesh);
    // Perf: no dedicated PointLight. A cast light here would change the
    // scene's active light count every time a stove starts/stops cooking,
    // forcing a full shader recompile (minutes on a 1050 Ti). The emissive
    // flameMesh carries the visual; the animator pulses its scale.
    return { group, variant, flameMesh };
  }

  /** Toaster glow — a thin red plane on top of the slot, pulsing as
   * if the coils inside are heating up. Plus a small warm point light
   * so the surrounding counter top picks up the colour. */
  private buildToasterGlowEffect() {
    const group = new THREE.Group();
    group.visible = false;
    const flameMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(0.16, 0.08),
      new THREE.MeshStandardMaterial({
        color: 0xff5a30,
        emissive: 0xff3a10,
        emissiveIntensity: 2.0,
        transparent: true,
        opacity: 0.85,
        side: THREE.DoubleSide,
      }),
    );
    // Lie flat looking up; sits a hair above the toaster's top.
    flameMesh.rotation.x = -Math.PI / 2;
    group.add(flameMesh);
    // Perf: emissive-only (see buildStoveFlameEffect). The animator pulses
    // the mesh's emissiveIntensity so it still reads as "heating".
    return { group, variant: "toaster" as const, flameMesh };
  }

  /** Coffee steam — three small white spheres that rise + fade in
   * sequence so the spout looks like it's continuously puffing. */
  private buildCoffeeSteamEffect() {
    const group = new THREE.Group();
    group.visible = false;
    const geom = new THREE.SphereGeometry(0.05, 8, 8);
    const steamPuffs: { mesh: THREE.Mesh; phase: number }[] = [];
    for (let i = 0; i < 3; i += 1) {
      const mesh = new THREE.Mesh(
        geom,
        new THREE.MeshStandardMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.55,
          emissive: 0xf0f0f0,
          emissiveIntensity: 0.4,
        }),
      );
      // Initial vertical offset so puffs aren't stacked.
      mesh.position.set(0, 0.05 + i * 0.12, 0);
      group.add(mesh);
      steamPuffs.push({ mesh, phase: i / 3 });
    }
    return { group, variant: "coffee" as const, steamPuffs };
  }

  /** Blender wobble — a small dark accessory disc on top of the model
   * that spins while in use. */
  private buildBlenderWobbleEffect() {
    const group = new THREE.Group();
    group.visible = false;
    const wobbleMesh = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.07, 0.02, 12),
      new THREE.MeshStandardMaterial({
        color: 0x2a2a2a,
        emissive: 0x444444,
        emissiveIntensity: 0.3,
      }),
    );
    wobbleMesh.position.set(0, 0.08, 0);
    group.add(wobbleMesh);
    return { group, variant: "blender" as const, wobbleMesh };
  }

  /** Microwave inside-glow — a soft yellow point light just above the
   * model's top surface. Currently no recipe gates on microwave so
   * this rarely fires, but having it ready means the visual lights up
   * the moment we add a microwave recipe later. */
  private buildMicrowaveGlowEffect() {
    const group = new THREE.Group();
    group.visible = false;
    // Perf: emissive-only effects only. The microwave glow was a lone
    // cast PointLight with no companion mesh; no recipe currently gates
    // on a microwave, so it never fired. Dropping the light keeps the
    // scene's active light count constant (no recompile). If a microwave
    // recipe is added later, give it an emissive mesh like the toaster.
    return { group, variant: "microwave" as const };
  }

  /** Pin an effect group to a station model's measured top. Same
   * bounding-box maths as the previous per-stove flame anchoring;
   * works for stoves, toasters, coffee machines, etc. regardless of
   * how tall the model is. */
  private alignEffectToModel(group: THREE.Group, model: THREE.Object3D): void {
    model.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(model);
    const cx = (box.min.x + box.max.x) / 2;
    const cz = (box.min.z + box.max.z) / 2;
    const topY = box.max.y + 0.03;
    group.position.set(cx, topY, cz);
  }

  /** Exposed for Engine to drive the day-night cycle each frame. */
  ambientLight!: THREE.AmbientLight;
  sunLight!: THREE.DirectionalLight;
  fillLight!: THREE.DirectionalLight;
  /** Inside-facing sky sphere — fills any pixel where the camera ray
   * exits world geometry (e.g., the bottom of the screen at low
   * elevation where rays escape past the ground). Color tracks the
   * day-night sky in Engine via setSkyColor(). Material is unlit and
   * fog-disabled so the dome reads as a continuous horizon haze
   * rather than a faceted sphere with shading. */
  skyDome!: THREE.Mesh;

  private addLighting(): void {
    this.ambientLight = new THREE.AmbientLight(0xfff1d6, 0.55);
    this.threeScene.add(this.ambientLight);

    this.sunLight = new THREE.DirectionalLight(0xffeac2, 1.1);
    this.sunLight.position.set(8, 14, 6);
    this.sunLight.castShadow = true;
    // 1024² (was 2048²) — quarters the per-frame shadow texel count.
    // At iso distance the visible shadow edges are still soft (we use
    // PCFSoftShadowMap which blurs them on top), so the difference
    // is hard to spot in practice. Cuts shadow-pass GPU cost ~3-4×.
    this.sunLight.shadow.mapSize.set(1024, 1024);
    this.sunLight.shadow.camera.left = -20;
    this.sunLight.shadow.camera.right = 20;
    this.sunLight.shadow.camera.top = 20;
    this.sunLight.shadow.camera.bottom = -20;
    this.sunLight.shadow.camera.near = 0.5;
    this.sunLight.shadow.camera.far = 60;
    this.sunLight.shadow.bias = -0.0005;
    this.threeScene.add(this.sunLight);

    this.fillLight = new THREE.DirectionalLight(0xb8c8e0, 0.25);
    this.fillLight.position.set(-6, 10, -4);
    this.threeScene.add(this.fillLight);

    // Phase I (perf) — pre-allocated pool of PointLights for placed
    // lamps.  Three.js recompiles every shader using lighting whenever
    // the active light count in the scene graph changes — that's a
    // 50-500 ms GPU stall.  Without the pool, EACH lamp placement
    // changed the count by 1 and triggered the stall — the lag the
    // user notices when placing lights.
    //
    // With the pool: lights are created ONCE at boot (paying the
    // recompile cost during the loading screen, when nobody sees it)
    // and never added/removed again, so the active-light count is
    // constant and nothing recompiles at runtime.  Each frame
    // updatePlacedLampLights() repositions this fixed pool onto the
    // lamps nearest the camera (mirrors updateStreetLamps), so only
    // nearby lamps cast light while every lamp keeps its glowing bulb.
    // Pool size (this.placedLampPoolSize) is tiered by graphics quality and
    // kept small — it's the biggest factor in how long that one-time boot
    // shader compile takes on a weak GPU. 0 (Low) means no cast halos at
    // all, just the glowing bulbs. High favours placed lamps (28) over
    // street (4) so the player's own indoor lamps light up.
    for (let i = 0; i < this.placedLampPoolSize; i += 1) {
      const light = new THREE.PointLight(0xffd6a0, 0, 4.5, 1.7);
      light.castShadow = false;
      // Park far below the ground while idle so the light contributes
      // nothing visible but the shader still compiles for the right
      // active-light count.
      light.position.set(0, -100, 0);
      this.worldRoot.add(light);
      this.placedLampLightPool.push({ light, inUse: false });
    }

    // Stove / appliance cast-light pool — mirrors the lamp pool but follows
    // the ACTIVE cooking stations instead of lamps, and is NOT night-gated
    // (a lit stove glows whenever a chef is cooking, day or night). The
    // per-station flame meshes stay emissive-only; a light PER station would
    // change the active-light count every time a stove starts/stops cooking
    // and recompile the lit-material shader (the multi-minute floor-reveal
    // stall). This FIXED pool keeps the count constant. Parented to
    // threeScene (world space) to match the station effects; the warm colour
    // reads as cooking heat.
    for (let i = 0; i < this.stoveLightPoolSize; i += 1) {
      const light = new THREE.PointLight(0xff8a4c, 0, 3.2, 1.8);
      light.castShadow = false;
      light.position.set(0, -100, 0);
      this.threeScene.add(light);
      this.stoveLightPool.push(light);
    }

    // Sky dome — sphere radius 600 (well inside the camera's far
    // plane of 1000) with BackSide material so the camera sees its
    // inner surface, depthWrite false so it never occludes scene
    // geometry. MeshBasicMaterial is unlit + fog-disabled → renders
    // as a uniform colour matching the renderer's clear colour.
    // Engine.tick pins this.skyDome.position to the camera every
    // frame so wherever the player pans, the dome is always centred
    // on them — any ray that escapes the world geometry (e.g., the
    // bottom of the screen at low elevation rays that miss the
    // ground plane) hits the dome's inner surface and renders sky.
    const skyGeom = new THREE.SphereGeometry(600, 24, 12);
    const skyMat = new THREE.MeshBasicMaterial({
      color: 0xd8c4a3,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });
    this.skyDome = new THREE.Mesh(skyGeom, skyMat);
    // Render the sky FIRST so other geometry overdraws it correctly.
    this.skyDome.renderOrder = -1000;
    this.threeScene.add(this.skyDome);
  }

  /** Phase I (UX) — return every perimeter wall mesh currently
   * rendered in SOLID mode (i.e. not ghosted by the see-through
   * camera-aware swap).  StatusBubbles raycasts against this list
   * to hide bubbles whose character is behind a wall the player
   * can't see through.  Walls in "ghost" mode are translucent
   * (the player CAN see through them), so we skip them — the
   * bubble should still render in that case.
   *
   * Floor slabs are NOT included; the storey-focus filter already
   * handles the case where a Floor 1 character's bubble would
   * leak down into Floor 0's view.  Adding slabs here would
   * needlessly trigger a raycast hit on the slab the character is
   * standing on.
   *
   * Output is a fresh array each call (no shared mutation hazard
   * for the caller).  Walk is O(#walls); at typical N≈30 segments
   * this is sub-millisecond. */
  getSolidWallOccluders(): THREE.Mesh[] {
    const out: THREE.Mesh[] = [];
    for (const floorMap of this.perimeterWalls.values()) {
      for (const wallState of floorMap.values()) {
        if (wallState.currentMat !== "solid") continue;
        for (const mesh of wallState.meshes) {
          if (mesh.visible) out.push(mesh);
        }
      }
    }
    return out;
  }

  /** Engine calls this every frame as the day-night cycle changes the
   * sky tint. We update the dome material colour so the void matches
   * the fog haze at every time of day. */
  setSkyColor(hex: number): void {
    if (!this.skyDome) return;
    const mat = this.skyDome.material as THREE.MeshBasicMaterial;
    mat.color.setHex(hex);
  }

  // === Placed lamps (lighting items registered via registerLamp) ===
  // Each entry holds the placed model and the warm point-light child
  // we attach to it. updateLamps() walks the list every frame and
  // ramps the intensity with how dark the sky is. Cap is enforced
  // implicitly by the player's furniture budget.
  private placedLamps: { model: THREE.Object3D; bulb: THREE.Mesh; pos: THREE.Vector3 }[] = [];

  /** Phase I (perf) — pool of pre-allocated PointLights for placed
   * lamps.  See the comment in addLighting() for the rationale (avoids
   * shader recompile on every lamp placement).  Each entry is either
   * idle (parented to worldRoot, intensity 0, far below ground) or
   * in-use (parented to a lamp model, intensity ramped to current
   * darkness).  registerLamp claims a free slot; unregisterLamp
   * releases it. */
  private placedLampLightPool: { light: THREE.PointLight; inUse: boolean }[] = [];
  // Reused each frame in updatePlacedLampLights to avoid a per-frame array alloc.
  private placedLampClosestI: number[] = [];
  private placedLampClosestD: number[] = [];
  /** Cast-light pool for ACTIVE cooking stations — the stove/appliance
   * analogue of placedLampLightPool. Fixed count (stoveLightPoolSize) so the
   * lit-material shader compiles once at boot and never recompiles when a
   * stove starts/stops cooking. Repositioned each frame by updateStoveLights. */
  private stoveLightPool: THREE.PointLight[] = [];
  // Reused each frame in updateStoveLights (flat x,y,z triples + closest scan).
  private stoveActivePos: number[] = [];
  private stoveClosestI: number[] = [];
  private stoveClosestD: number[] = [];
  /** Footprints of every procedurally-placed scenery house in the city
   * (populated by addCityScenery). The scatter passes that follow
   * (grass blades, wildflowers, trees, rocks) consult this list via
   * isOnRoadOrBuildingForScatter so nothing spawns inside or on top of
   * a neighbour building. halfSize is the box half-extent on each
   * horizontal axis (scenery houses are square in footprint). */
  private placedSceneryHouses: { x: number; z: number; halfSize: number }[] = [];
  /** Most recently computed nightAmount in [0, 1] — used so a freshly
   * registered lamp picks up the current darkness immediately, not on
   * the next applyDayNight tick. */
  private currentNightAmount = 0;

  // === Street lamps (pavement lamp-posts symmetrically along every avenue) ===
  // ~140 lamp posts. The meshes are InstancedMesh (one draw call per
  // part type) so even at this count the cost is negligible. ACTUAL
  // lighting is a fixed pool of N point lights repositioned each tick
  // to the N closest lamps to the camera — three.js' shader compiles
  // with NUM_POINT_LIGHTS = N, so the pool size determines per-pixel
  // cost regardless of how many lamp POSTS exist on the map.
  /** XZ positions (in worldRoot-local space) of every placed street
   * lamp, in the same order as the InstancedMesh instance indices.
   * Consulted by updateStreetLamps to pick the N closest to camera. */
  private streetLampPositions: { x: number; z: number }[] = [];
  /** The lantern's emissive bulb mesh — one InstancedMesh shared by
   * all lamps. We mutate the SHARED material's emissiveIntensity /
   * opacity each tick to make all bulbs glow at night. */
  private streetLampBulbMat: THREE.MeshStandardMaterial | null = null;
  /** Pool of point lights that follow the camera. Sized small enough
   * to keep the shader cheap; the bulbs themselves give the visual
   * presence of light at distance. */
  private streetLampLightPool: THREE.PointLight[] = [];
  /** Active lamp-light pool sizes, tiered by graphics quality and split by
   * type so the budget favours the player's PLACED lamps over street lamps
   * (high: 28 placed / 4 street). Read once at construction — they fix the
   * scene's active-light count (and thus the lit-material shader's compile
   * cost — the thing that froze weak GPUs at high counts). A quality change
   * takes effect on the next reload. */
  private readonly placedLampPoolSize = getCurrentGraphicsPreset().placedLampPool;
  private readonly streetLampPoolSize = getCurrentGraphicsPreset().streetLampPool;
  private readonly stoveLightPoolSize = getCurrentGraphicsPreset().stoveLightPool;
  /** Cached camera position from the last light-pool reposition. Skip
   * the re-sort when the camera hasn't moved enough to change which
   * lamps are closest. */
  private streetLampLastCamX = Number.NEGATIVE_INFINITY;
  private streetLampLastCamZ = Number.NEGATIVE_INFINITY;
  /** Procedural rain / snow / confetti overlay + per-weather lighting
   * modifiers. Engine pushes the current weather id via setWeather and
   * ticks update() every frame so particles follow the camera. */
  private weatherEffects?: WeatherEffects;
  /** Most recent weather id passed from Engine. Default sunny so day 1
   * always renders the warm path even before Engine has wired the
   * weather callback. */
  private currentWeather: WeatherKind = "sunny";

  /** Attach a warm point light + small emissive bulb to a placed lamp
   * model so it actually illuminates the area at night. Returns silently
   * if the same model is already registered.
   *
   * Critical: the bulb + light are children of the model, which already
   * has fitFurniture's non-uniform scale baked in (often 3-8x for the
   * small Kenney lamp GLBs). A hardcoded local Y of 1.5 would get
   * multiplied by that scale and throw the bulb 5-12 units into the
   * sky. We instead anchor the bulb just below the model's measured
   * top in WORLD units and translate that back into the model's local
   * frame, and inverse-scale the bulb mesh so it stays the authored
   * 0.08 radius regardless of how stretched the parent model is. */
  registerLamp(model: THREE.Object3D): void {
    if (this.placedLamps.some((lp) => lp.model === model)) return;
    // Measure the lamp's world-space top so we know where "bulb height"
    // really is for THIS model. Wall sconces are mounted at y≈1.5
    // (BuildMenu pre-positions them), floor lamps have their feet at
    // y=0; either way the top of the model is the natural bulb anchor.
    model.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(model);
    const worldTopY = Number.isFinite(box.max.y) ? box.max.y : 1.5;
    const bulbWorldY = Math.max(0.3, worldTopY - 0.12);
    // For wall sconces fitFurniture shifts the model so its +Z face
    // sits flush with the wall — that means the model origin is at
    // the WALL plane, and the candle / sconce body sticks out in -Z.
    // Anchoring the bulb at local (0, _, 0) parked it at the wall
    // plane, so the light pooled on the wall right behind the candle
    // instead of on the candle itself. Use the model's measured
    // bounding-box centre in world space, converted back to local,
    // so the bulb sits inside the sconce body for wall lamps and
    // stays at the model origin for floor / table / ceiling lamps
    // (whose centroid is already at the origin).
    const worldCentre = new THREE.Vector3(
      Number.isFinite(box.min.x) ? (box.min.x + box.max.x) / 2 : model.position.x,
      bulbWorldY,
      Number.isFinite(box.min.z) ? (box.min.z + box.max.z) / 2 : model.position.z,
    );
    const localCentre = model.worldToLocal(worldCentre.clone());

    const sx = model.scale.x || 1;
    const sy = model.scale.y || 1;
    const sz = model.scale.z || 1;

    // Anti-freeze — placed lamps do NOT each get a dedicated PointLight.
    // The scene's ACTIVE point-light count drives shader compilation; with
    // dozens of lamps the lit-material shader grew so large that any change
    // to the count (e.g. a floor reveal) recompiled every material — a
    // multi-minute main-thread stall on a low-end GPU. Instead a small
    // FIXED pool (placedLampLightPool, PLACED_LAMP_POOL_SIZE lights) follows
    // the camera and lights only the nearest few lamps — exactly the trick
    // the street lamps already use. updatePlacedLampLights() does that
    // per-frame assignment; here we just record the lamp's world position
    // (worldRoot is translation-only, so world − worldRoot.position is its
    // local pos) as a candidate. The glowing BULB mesh is still per-lamp and
    // parents under the model, so EVERY lamp reads as "on" and hides/shows
    // with its floor — only the cast-light halo is budgeted to the nearest.
    const lightWorldPos = worldCentre.clone().sub(this.worldRoot.position);
    const bulb = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 10, 10),
      new THREE.MeshStandardMaterial({
        color: 0xfff4cc, emissive: 0xfff4cc, emissiveIntensity: 0,
        transparent: true, opacity: 0.0,
      }),
    );
    bulb.position.copy(localCentre);
    // Inverse-scale so the bulb's WORLD radius stays at 0.08 even when
    // the parent lamp is stretched 8x vertically by fitFurniture.
    bulb.scale.set(1 / sx, 1 / sy, 1 / sz);
    model.add(bulb);
    this.placedLamps.push({ model, bulb, pos: lightWorldPos });
    // Light the bulb immediately at the current darkness; the cast light
    // (if this lamp is among the nearest) lands on the next frame.
    this.applyBulbGlow(bulb, this.currentNightAmount);
  }

  /** Remove a lamp from the active list (sell mode, undo, etc.). The
   * point light is returned to the pool (or disposed if it was a
   * pool-overflow fallback); the bulb mesh is removed and disposed. */
  unregisterLamp(model: THREE.Object3D): void {
    const i = this.placedLamps.findIndex((lp) => lp.model === model);
    if (i < 0) return;
    const lp = this.placedLamps[i];
    // Only the bulb belongs to the lamp now — the cast lights are a shared
    // camera-following pool (updatePlacedLampLights), not per-lamp, so
    // there's nothing to release. Next frame's pool assignment simply has
    // one fewer candidate.
    model.remove(lp.bulb);
    lp.bulb.geometry.dispose();
    (lp.bulb.material as THREE.Material).dispose();
    this.placedLamps.splice(i, 1);
  }

  /** Phase 9.49 — drop EVERY registered placed-lamp (returning pool
   * lights to the pool, disposing bulbs). Used before re-registering
   * after a cloud restore, which wipes + rebuilds all furniture models:
   * the lamp entries from the earlier save-restore then point at
   * detached models (their pool lights stranded off-scene), and the
   * fresh cloud lamp models are unregistered — i.e. dark at night, the
   * recurring "lights off on reload" bug. Reset here, re-register from
   * the fresh items, and night illumination works again. */
  clearAllLamps(): void {
    // Snapshot models first — unregisterLamp mutates placedLamps as it
    // splices, so iterating it directly would skip entries.
    const models = this.placedLamps.map((lp) => lp.model);
    for (const m of models) this.unregisterLamp(m);
  }

  /** Walk every registered lamp and ramp its intensity + bulb emissive
   * with the current darkness. */
  private updateLamps(nightAmount: number): void {
    this.currentNightAmount = nightAmount;
    // Bulb glow for EVERY lamp (so all read as "on" at night); the actual
    // cast lights are budgeted to the nearest few by updatePlacedLampLights.
    for (const lp of this.placedLamps) {
      this.applyBulbGlow(lp.bulb, nightAmount);
    }
  }

  /** Ramp a lamp BULB's emissive glow with darkness. Every placed lamp
   * gets this; the cast PointLight is handled separately by the
   * camera-following pool (updatePlacedLampLights). */
  private applyBulbGlow(bulb: THREE.Mesh, nightAmount: number): void {
    const bulbMat = bulb.material as THREE.MeshStandardMaterial;
    bulbMat.emissiveIntensity = nightAmount * 1.4;
    bulbMat.opacity = Math.min(1, nightAmount * 1.5);
  }

  /** Camera-following cast-light pool for placed lamps — mirrors
   * updateStreetLamps. Each frame it lights only the PLACED_LAMP_POOL_SIZE
   * lamps nearest the camera; every other lamp keeps its glowing bulb but
   * casts no halo. This keeps the scene's active point-light count small
   * AND constant, so the lit-material shader stays cheap to compile — the
   * fix for the multi-minute floor-reveal stall. Called from Engine.tick. */
  updatePlacedLampLights(cameraPos: THREE.Vector3): void {
    const pool = this.placedLampLightPool;
    if (pool.length === 0) return;
    const nightAmount = this.currentNightAmount;
    if (nightAmount < 0.05 || this.placedLamps.length === 0) {
      for (const e of pool) e.light.intensity = 0;
      return;
    }
    // worldRoot is translation-only; compare distances in its local space.
    const cx = cameraPos.x - this.worldRoot.position.x;
    const cz = cameraPos.z - this.worldRoot.position.z;
    const POOL = pool.length;
    // Reuse instance arrays instead of allocating two new arrays every frame.
    const closestI = this.placedLampClosestI;
    const closestD = this.placedLampClosestD;
    closestI.length = POOL; closestD.length = POOL;
    closestI.fill(-1); closestD.fill(Infinity);
    let worstSlot = 0;
    for (let i = 0; i < this.placedLamps.length; i += 1) {
      const p = this.placedLamps[i].pos;
      const ddx = p.x - cx;
      const ddz = p.z - cz;
      const d = ddx * ddx + ddz * ddz;
      if (d < closestD[worstSlot]) {
        closestI[worstSlot] = i;
        closestD[worstSlot] = d;
        let w = 0;
        for (let k = 1; k < POOL; k += 1) if (closestD[k] > closestD[w]) w = k;
        worstSlot = w;
      }
    }
    const peak = nightAmount * 1.8;
    for (let s = 0; s < POOL; s += 1) {
      const light = pool[s].light;
      const idx = closestI[s];
      if (idx >= 0) {
        light.position.copy(this.placedLamps[idx].pos);
        light.intensity = peak;
      } else {
        light.intensity = 0;
      }
    }
  }

  /** Camera-following cast-light pool for ACTIVE cooking stations — the
   * stove/appliance analogue of updatePlacedLampLights. Each frame it lights
   * only the nearest stoveLightPoolSize stations that are currently cooking
   * (visible flame); every other active station keeps its emissive glow but
   * casts no halo. The pool never grows, so the lit-material shader's active-
   * light count stays constant — the same trick that fixed the lamp freeze.
   * UNLIKE lamps this is NOT night-gated: a lit stove casts whenever a chef is
   * cooking, day or night. Called from Engine.tick. */
  updateStoveLights(cameraPos: THREE.Vector3): void {
    const pool = this.stoveLightPool;
    const POOL = pool.length;
    if (POOL === 0) return;
    // Gather currently-active (visible) stations' world positions. Station
    // effects live in threeScene, so group.position is already world space.
    const pos = this.stoveActivePos;
    pos.length = 0;
    for (const e of this.stationEffects.values()) {
      if (!e.group.visible) continue;
      const p = e.group.position;
      pos.push(p.x, p.y, p.z);
    }
    const count = pos.length / 3;
    if (count === 0) {
      for (let s = 0; s < POOL; s += 1) pool[s].intensity = 0;
      return;
    }
    // Pick the POOL active stations nearest the camera (same worst-slot scan
    // as updatePlacedLampLights). cameraPos is world space, matching pos.
    const closestI = this.stoveClosestI;
    const closestD = this.stoveClosestD;
    closestI.length = POOL; closestD.length = POOL;
    closestI.fill(-1); closestD.fill(Infinity);
    let worstSlot = 0;
    for (let i = 0; i < count; i += 1) {
      const dx = pos[i * 3] - cameraPos.x;
      const dz = pos[i * 3 + 2] - cameraPos.z;
      const d = dx * dx + dz * dz;
      if (d < closestD[worstSlot]) {
        closestI[worstSlot] = i;
        closestD[worstSlot] = d;
        let w = 0;
        for (let k = 1; k < POOL; k += 1) if (closestD[k] > closestD[w]) w = k;
        worstSlot = w;
      }
    }
    for (let s = 0; s < POOL; s += 1) {
      const light = pool[s];
      const idx = closestI[s];
      if (idx >= 0) {
        // Lift the light a touch above the burner so it spills onto the
        // counter and hood rather than sitting inside the flame mesh.
        light.position.set(pos[idx * 3], pos[idx * 3 + 1] + 0.3, pos[idx * 3 + 2]);
        light.intensity = 1.4;
      } else {
        light.intensity = 0;
      }
    }
  }

  /** Build the city's street lamp grid — one Parisian cast-iron post
   * on each pavement of every avenue, symmetric across the road.
   * Posts + bulbs are InstancedMesh (shared geometry / material) so
   * even 140 lamps cost ~6 draw calls total. Real PointLights are
   * NOT placed per post — see updateStreetLamps for the camera-
   * following pool that does the actual illumination. */
  private addStreetLamps(): void {
    // Pavement extends ±PAVEMENT_HALF (5.5m) from each avenue centerline.
    // Place the lamp post at perp ±4m so it sits ON the pavement,
    // ~1.5m back from the curb edge — between pedestrians and the
    // scenery houses' front walls.
    const PAVEMENT_HALF = 5.5;
    const LAMP_PERP = 4.0;
    // ~18m spacing along the avenue reads as a planned city without
    // packing the pavement edge-to-edge. At ±130m walk per avenue
    // that gives ~14-15 lamps per side per avenue.
    const LAMP_STEP = 18;
    const LAMP_HALF = 130;
    // Skip a candidate that would sit on a perpendicular avenue's
    // pavement — at intersections both avenues claim the same patch,
    // so we'd otherwise drop a lamp post on the cross street. Only
    // perpendicular crossings can overlap; parallel avenues never do.
    const onPerpCrossing = (x: number, z: number, axis: "ew" | "ns"): boolean => {
      if (axis === "ew") {
        for (const ax of WorldScene.NS_AVENUES) {
          if (Math.abs(x - ax) < PAVEMENT_HALF + 0.5) return true;
        }
      } else {
        for (const az of WorldScene.EW_AVENUES) {
          if (Math.abs(z - az) < PAVEMENT_HALF + 0.5) return true;
        }
      }
      return false;
    };

    type LampPlacement = { x: number; z: number; rotY: number };
    const placements: LampPlacement[] = [];
    // EW avenues — lamps line up along the X axis on the south (-Z)
    // and north (+Z) pavements. rotY rotates the lantern arm to face
    // the road (so the post pole sits flush with the building side).
    for (const az of WorldScene.EW_AVENUES) {
      for (const side of [-1, +1] as const) {
        const z = az + side * LAMP_PERP;
        // side=+1 (south pavement, post is south of road) → face north (-Z)
        // side=-1 (north pavement, post is north of road) → face south (+Z)
        const rotY = side > 0 ? Math.PI : 0;
        for (let x = -LAMP_HALF; x <= LAMP_HALF + 0.001; x += LAMP_STEP) {
          if (onPerpCrossing(x, z, "ew")) continue;
          placements.push({ x, z, rotY });
        }
      }
    }
    // NS avenues — same idea, axes swapped. Lamp arm faces the road.
    for (const ax of WorldScene.NS_AVENUES) {
      for (const side of [-1, +1] as const) {
        const x = ax + side * LAMP_PERP;
        const rotY = side > 0 ? -Math.PI / 2 : Math.PI / 2;
        for (let z = -LAMP_HALF; z <= LAMP_HALF + 0.001; z += LAMP_STEP) {
          if (onPerpCrossing(x, z, "ns")) continue;
          placements.push({ x, z, rotY });
        }
      }
    }
    if (placements.length === 0) return;

    // === Shared geometries + materials ===
    const ironMat = new THREE.MeshStandardMaterial({
      color: 0x18130f, roughness: 0.55, metalness: 0.45,
    });
    const lanternMat = new THREE.MeshStandardMaterial({
      color: 0x2a2218, roughness: 0.40, metalness: 0.55,
    });
    // Bulb material — shared across every lamp. updateStreetLamps
    // mutates emissiveIntensity + opacity once per tick to drive
    // every bulb's glow simultaneously.
    this.streetLampBulbMat = new THREE.MeshStandardMaterial({
      color: 0xfff4cc, emissive: 0xffd99a, emissiveIntensity: 0,
      transparent: true, opacity: 0.0,
    });

    // Base disc (slight flare at ground level)
    const baseGeo = new THREE.CylinderGeometry(0.20, 0.24, 0.18, 10);
    baseGeo.translate(0, 0.09, 0);
    // Pole — tapered cylinder 3.0m tall
    const poleGeo = new THREE.CylinderGeometry(0.07, 0.10, 3.0, 8);
    poleGeo.translate(0, 1.68, 0);
    // Decorative cap where pole meets lantern
    const capGeo = new THREE.CylinderGeometry(0.13, 0.10, 0.18, 8);
    capGeo.translate(0, 3.27, 0);
    // Lantern housing (square iron box, glass implied)
    const lanternGeo = new THREE.BoxGeometry(0.34, 0.40, 0.34);
    lanternGeo.translate(0, 3.60, 0);
    // Pyramidal roof on top of lantern
    const roofGeo = new THREE.ConeGeometry(0.26, 0.20, 4);
    roofGeo.translate(0, 3.92, 0);
    // Bulb sphere (inside the lantern housing)
    const bulbGeo = new THREE.SphereGeometry(0.13, 12, 10);
    bulbGeo.translate(0, 3.60, 0);

    const count = placements.length;
    const baseInst = new THREE.InstancedMesh(baseGeo, ironMat, count);
    const poleInst = new THREE.InstancedMesh(poleGeo, ironMat, count);
    const capInst = new THREE.InstancedMesh(capGeo, ironMat, count);
    const lanternInst = new THREE.InstancedMesh(lanternGeo, lanternMat, count);
    const roofInst = new THREE.InstancedMesh(roofGeo, ironMat, count);
    const bulbInst = new THREE.InstancedMesh(bulbGeo, this.streetLampBulbMat, count);
    // Street lamps do NOT cast shadows. The 140-lamp InstancedMesh
    // has a bounding box that spans ±130 m (the full street grid),
    // so three.js renders ALL 140 instances into the shadow pass
    // even though only ~6 near origin fall inside the sun's ±20 m
    // shadow frustum. Net effect was a lot of wasted shadow texels
    // for shadows that get clipped immediately. Tiny visual loss
    // (the streaks were short at iso angle anyway).
    poleInst.castShadow = false;
    lanternInst.castShadow = false;

    const tmp = new THREE.Object3D();
    for (let i = 0; i < count; i += 1) {
      const p = placements[i];
      tmp.position.set(p.x, 0, p.z);
      tmp.rotation.set(0, p.rotY, 0);
      tmp.scale.set(1, 1, 1);
      tmp.updateMatrix();
      baseInst.setMatrixAt(i, tmp.matrix);
      poleInst.setMatrixAt(i, tmp.matrix);
      capInst.setMatrixAt(i, tmp.matrix);
      lanternInst.setMatrixAt(i, tmp.matrix);
      roofInst.setMatrixAt(i, tmp.matrix);
      bulbInst.setMatrixAt(i, tmp.matrix);
      this.streetLampPositions.push({ x: p.x, z: p.z });
    }
    this.worldRoot.add(baseInst);
    this.worldRoot.add(poleInst);
    this.worldRoot.add(capInst);
    this.worldRoot.add(lanternInst);
    this.worldRoot.add(roofInst);
    this.worldRoot.add(bulbInst);

    // === Light pool — N point lights repositioned each tick ===
    for (let i = 0; i < this.streetLampPoolSize; i += 1) {
      // Warm sodium-vapour glow. Distance ~14m gives a clear pool
      // under each lit lamp without bleeding across the whole map.
      // Decay 1.5 keeps the centre bright while falloff still hits
      // zero at the distance edge.
      const light = new THREE.PointLight(0xffe1a0, 0, 14, 1.5);
      light.castShadow = false;
      light.position.set(0, 3.6, 0);
      this.worldRoot.add(light);
      this.streetLampLightPool.push(light);
    }
  }

  /** Build the city's street planters — square / round / long
   * concrete boxes scattered along every avenue's pavement, SLOTTED
   * BETWEEN the lamp posts so the two grids interlock instead of
   * overlapping. Three styles cycle in sequence (square → round →
   * long → square …) so a single pavement run reads as a curated
   * garden strip, not a copy-pasted row.
   *
   * All meshes use InstancedMesh keyed on style so the entire grid
   * (~50 planters per style) costs ~3-4 draw calls per style. None
   * cast shadows — same reasoning as the lamps (the InstancedMesh
   * union bounding box spans the whole map so per-instance frustum
   * culling can't kick in, and a small concrete-box shadow is
   * imperceptible at iso distance anyway). */
  private addStreetPlanters(): void {
    const PAVEMENT_HALF = 5.5;
    // Same perp position as the lamps (4 m off centerline = ~1 m
    // from curb, ~1.5 m from outer pavement edge). Stagger ALONG
    // the avenue: lamps step every 18 m starting at -130 → planters
    // step every 18 m starting at -121 (= -130 + 9). End result:
    // lamp · planter · lamp · planter · …
    const PERP = 4.0;
    const STEP = 18;
    const PHASE_OFFSET = 9;
    const HALF = 130;
    // Same intersection-aware skip the lamps use — otherwise a
    // planter would land on the cross-street's asphalt.
    const onPerpCrossing = (x: number, z: number, axis: "ew" | "ns"): boolean => {
      if (axis === "ew") {
        for (const ax of WorldScene.NS_AVENUES) {
          if (Math.abs(x - ax) < PAVEMENT_HALF + 0.5) return true;
        }
      } else {
        for (const az of WorldScene.EW_AVENUES) {
          if (Math.abs(z - az) < PAVEMENT_HALF + 0.5) return true;
        }
      }
      return false;
    };

    type PlanterStyle = "square" | "round" | "long";
    type Placement = { x: number; z: number; rotY: number };
    const placements: Record<PlanterStyle, Placement[]> = {
      square: [], round: [], long: [],
    };
    const cycleStyle = (i: number): PlanterStyle => ([
      "square", "round", "long",
    ] as const)[i % 3];

    // EW avenues — planters line up along X on both pavements.
    for (const az of WorldScene.EW_AVENUES) {
      for (const side of [-1, +1] as const) {
        const z = az + side * PERP;
        const rotY = side > 0 ? Math.PI : 0;
        let cycle = 0;
        for (let x = -HALF + PHASE_OFFSET; x <= HALF + 0.001; x += STEP) {
          if (onPerpCrossing(x, z, "ew")) continue;
          placements[cycleStyle(cycle)].push({ x, z, rotY });
          cycle += 1;
        }
      }
    }
    // NS avenues — same idea, axes swapped.
    for (const ax of WorldScene.NS_AVENUES) {
      for (const side of [-1, +1] as const) {
        const x = ax + side * PERP;
        const rotY = side > 0 ? -Math.PI / 2 : Math.PI / 2;
        let cycle = 0;
        for (let z = -HALF + PHASE_OFFSET; z <= HALF + 0.001; z += STEP) {
          if (onPerpCrossing(x, z, "ns")) continue;
          placements[cycleStyle(cycle)].push({ x, z, rotY });
          cycle += 1;
        }
      }
    }

    // Shared materials — warm beige concrete to harmonize with the
    // pavement tone, dark soil that matches the player's own garden
    // patch east of the restaurant.
    const concrete = new THREE.MeshStandardMaterial({
      color: 0xb3a890, roughness: 0.85, metalness: 0.05,
    });
    const soil = new THREE.MeshStandardMaterial({
      color: 0x3a2818, roughness: 0.95,
    });

    if (placements.square.length > 0) this.buildSquarePlanters(placements.square, concrete, soil);
    if (placements.round.length > 0) this.buildRoundPlanters(placements.round, concrete, soil);
    if (placements.long.length > 0) this.buildLongPlanters(placements.long, concrete, soil);
  }

  /** Square concrete planter with a single bushy plant on top. ~0.9 m
   * box, 0.45 m tall. */
  private buildSquarePlanters(
    placements: { x: number; z: number; rotY: number }[],
    concrete: THREE.MeshStandardMaterial,
    soil: THREE.MeshStandardMaterial,
  ): void {
    const bushMat = new THREE.MeshStandardMaterial({
      color: 0x4a8a4a, roughness: 0.85,
    });
    const boxGeo = new THREE.BoxGeometry(0.9, 0.45, 0.9);
    boxGeo.translate(0, 0.225, 0);
    const soilGeo = new THREE.BoxGeometry(0.78, 0.04, 0.78);
    soilGeo.translate(0, 0.46, 0);
    const bushGeo = new THREE.SphereGeometry(0.42, 10, 8);
    bushGeo.translate(0, 0.78, 0);

    const count = placements.length;
    const box = new THREE.InstancedMesh(boxGeo, concrete, count);
    const soilM = new THREE.InstancedMesh(soilGeo, soil, count);
    const bush = new THREE.InstancedMesh(bushGeo, bushMat, count);
    const tmp = new THREE.Object3D();
    for (let i = 0; i < count; i += 1) {
      const p = placements[i];
      tmp.position.set(p.x, 0, p.z);
      tmp.rotation.set(0, p.rotY, 0);
      tmp.scale.set(1, 1, 1);
      tmp.updateMatrix();
      box.setMatrixAt(i, tmp.matrix);
      soilM.setMatrixAt(i, tmp.matrix);
      bush.setMatrixAt(i, tmp.matrix);
    }
    this.worldRoot.add(box, soilM, bush);
  }

  /** Round concrete planter holding a small tree. ~0.55 m radius, 0.5 m
   * tall, with a trunk + cone canopy on top. */
  private buildRoundPlanters(
    placements: { x: number; z: number; rotY: number }[],
    concrete: THREE.MeshStandardMaterial,
    soil: THREE.MeshStandardMaterial,
  ): void {
    const trunkMat = new THREE.MeshStandardMaterial({
      color: 0x5a3a22, roughness: 0.9,
    });
    const canopyMat = new THREE.MeshStandardMaterial({
      color: 0x3a7a3a, roughness: 0.85,
    });
    const tubGeo = new THREE.CylinderGeometry(0.55, 0.50, 0.50, 14);
    tubGeo.translate(0, 0.25, 0);
    const soilGeo = new THREE.CylinderGeometry(0.46, 0.46, 0.04, 12);
    soilGeo.translate(0, 0.51, 0);
    const trunkGeo = new THREE.CylinderGeometry(0.07, 0.10, 0.85, 8);
    trunkGeo.translate(0, 0.93, 0);
    const canopyGeo = new THREE.ConeGeometry(0.50, 1.10, 8);
    canopyGeo.translate(0, 1.92, 0);

    const count = placements.length;
    const tub = new THREE.InstancedMesh(tubGeo, concrete, count);
    const soilM = new THREE.InstancedMesh(soilGeo, soil, count);
    const trunk = new THREE.InstancedMesh(trunkGeo, trunkMat, count);
    const canopy = new THREE.InstancedMesh(canopyGeo, canopyMat, count);
    const tmp = new THREE.Object3D();
    for (let i = 0; i < count; i += 1) {
      const p = placements[i];
      tmp.position.set(p.x, 0, p.z);
      tmp.rotation.set(0, p.rotY, 0);
      tmp.scale.set(1, 1, 1);
      tmp.updateMatrix();
      tub.setMatrixAt(i, tmp.matrix);
      soilM.setMatrixAt(i, tmp.matrix);
      trunk.setMatrixAt(i, tmp.matrix);
      canopy.setMatrixAt(i, tmp.matrix);
    }
    this.worldRoot.add(tub, soilM, trunk, canopy);
  }

  /** Long rectangular trough with a row of three flowers across the
   * top. Rotated to lie ALONG the avenue (not across it) so the
   * silhouette reads as a planter rather than a kerb obstacle. Flowers
   * cycle through three warm colours per planter so the city has a
   * little floral variety. */
  private buildLongPlanters(
    placements: { x: number; z: number; rotY: number }[],
    concrete: THREE.MeshStandardMaterial,
    soil: THREE.MeshStandardMaterial,
  ): void {
    // Long planters point ALONG the avenue. EW pavements: long axis
    // is X (rotY = 0 / π). NS pavements: long axis is Z (rotY = ±π/2).
    // The original placement loop already passes a rotY that points
    // toward the road for the lamps; we OVERRIDE here so the trough
    // sits parallel to the kerb instead of across it.
    const FLOWER_COLORS = [0xff6f8c, 0xffd06f, 0xa8e2a8] as const;
    const flowerMats = FLOWER_COLORS.map((c) => new THREE.MeshStandardMaterial({
      color: c, roughness: 0.6,
      emissive: c, emissiveIntensity: 0.10,
    }));

    const boxGeo = new THREE.BoxGeometry(1.7, 0.35, 0.5);
    boxGeo.translate(0, 0.175, 0);
    const soilGeo = new THREE.BoxGeometry(1.55, 0.04, 0.36);
    soilGeo.translate(0, 0.36, 0);
    // Flower = small sphere (poppy-style) at ~0.10 m radius. Three per
    // trough at x = -0.6, 0, +0.6. Stem omitted at this distance.
    const flowerGeo = new THREE.SphereGeometry(0.11, 10, 8);

    const count = placements.length;
    const box = new THREE.InstancedMesh(boxGeo, concrete, count);
    const soilM = new THREE.InstancedMesh(soilGeo, soil, count);
    // One flower-mesh per colour, holding all the flowers OF THAT
    // colour across every long planter (3 flowers per planter).
    const flowerCount = count * 3;
    const flowers = flowerMats.map((mat) => new THREE.InstancedMesh(flowerGeo, mat, Math.ceil(flowerCount / FLOWER_COLORS.length) + 1));
    const flowerCursors = [0, 0, 0]; // next free slot in each colour bucket
    const tmp = new THREE.Object3D();
    const flowerTmp = new THREE.Object3D();
    for (let i = 0; i < count; i += 1) {
      const p = placements[i];
      // Long axis runs along the avenue — for an EW-side row the
      // placement rotY is 0 or π (planter faces ±Z which means the
      // long box currently extends ±X, perfect). For NS-side rows
      // rotY is ±π/2 so the box's local X (length) rotates to be
      // along world Z — also along the avenue. Either way the
      // length-axis ends up parallel to the avenue, no override
      // needed.
      tmp.position.set(p.x, 0, p.z);
      tmp.rotation.set(0, p.rotY, 0);
      tmp.scale.set(1, 1, 1);
      tmp.updateMatrix();
      box.setMatrixAt(i, tmp.matrix);
      soilM.setMatrixAt(i, tmp.matrix);
      // Flowers — three per planter at x = -0.55, 0, +0.55 (local).
      // Pick a colour by planter index so neighbouring troughs alternate.
      const colourIdx = i % FLOWER_COLORS.length;
      for (const localX of [-0.55, 0, 0.55]) {
        // Position each flower in the planter's LOCAL frame then bake
        // through the planter's world transform.
        flowerTmp.position.set(localX, 0.49, 0);
        flowerTmp.rotation.set(0, 0, 0);
        flowerTmp.scale.set(1, 1, 1);
        flowerTmp.updateMatrix();
        const m = new THREE.Matrix4().multiplyMatrices(tmp.matrix, flowerTmp.matrix);
        const cursor = flowerCursors[colourIdx];
        flowers[colourIdx].setMatrixAt(cursor, m);
        flowerCursors[colourIdx] = cursor + 1;
      }
    }
    // Trim each flower InstancedMesh to its actual used count so the
    // renderer doesn't draw unset zero-matrix instances at origin.
    for (let k = 0; k < flowers.length; k += 1) flowers[k].count = flowerCursors[k];
    this.worldRoot.add(box, soilM, ...flowers);
  }

  /** Per-frame: ramp every lamp bulb's emissive with the current
   * night amount (cheap — one shared material), then reposition the
   * pool of point lights to the N closest lamps to the camera so the
   * player walks through pools of light wherever they go.
   *
   * Engine.update calls this AFTER applyDayNight so currentNightAmount
   * is fresh. Skips the full distance re-sort when the camera hasn't
   * moved enough to change the closest set. */
  updateStreetLamps(cameraPos: THREE.Vector3): void {
    if (this.streetLampPositions.length === 0 || !this.streetLampBulbMat) return;
    const nightAmount = this.currentNightAmount;
    // === Bulb glow (all bulbs share one material) ===
    this.streetLampBulbMat.emissiveIntensity = nightAmount * 2.2;
    this.streetLampBulbMat.opacity = Math.min(1, nightAmount * 1.6);
    // === Light pool ===
    if (nightAmount < 0.05) {
      // Pure daytime — kill the pool entirely so we don't waste
      // shader work computing zero-intensity contributions.
      for (const light of this.streetLampLightPool) light.intensity = 0;
      return;
    }
    // Convert camera position into worldRoot-local coords (worldRoot
    // is offset to keep the player's plot at local origin; lamps live
    // in worldRoot-local space, so we subtract the offset to compare
    // distances in the same frame).
    const cx = cameraPos.x - this.worldRoot.position.x;
    const cz = cameraPos.z - this.worldRoot.position.z;
    // Skip the resort if the camera hasn't moved enough. Threshold =
    // ½ the average lamp spacing — moving less than that can't flip
    // which lamps are nearest.
    const dx = cx - this.streetLampLastCamX;
    const dz = cz - this.streetLampLastCamZ;
    const moved = dx * dx + dz * dz > 6 * 6;
    if (!moved) {
      // Just refresh intensity (in case nightAmount changed) without
      // re-shuffling which lamps are lit.
      const peak = nightAmount * 1.7;
      for (const light of this.streetLampLightPool) {
        if (light.intensity > 0) light.intensity = peak;
      }
      return;
    }
    this.streetLampLastCamX = cx;
    this.streetLampLastCamZ = cz;
    // Find the POOL_SIZE closest lamps by squared distance. Maintain
    // a small heap-like array of [index, dSq] sorted by dSq ascending.
    const POOL = this.streetLampLightPool.length;
    const closestI: number[] = new Array(POOL).fill(-1);
    const closestD: number[] = new Array(POOL).fill(Infinity);
    let worstSlot = 0; // index in closest* arrays holding the largest dSq
    for (let i = 0; i < this.streetLampPositions.length; i += 1) {
      const l = this.streetLampPositions[i];
      const ddx = l.x - cx;
      const ddz = l.z - cz;
      const d = ddx * ddx + ddz * ddz;
      if (d < closestD[worstSlot]) {
        closestI[worstSlot] = i;
        closestD[worstSlot] = d;
        // Recompute the new worst slot.
        let w = 0;
        for (let k = 1; k < POOL; k += 1) {
          if (closestD[k] > closestD[w]) w = k;
        }
        worstSlot = w;
      }
    }
    // Hard distance gate — beyond ~80m the lamp would be off-screen
    // for most reasonable camera angles, so don't waste the slot.
    const MAX_RANGE_SQ = 80 * 80;
    const peakIntensity = nightAmount * 1.7;
    for (let p = 0; p < POOL; p += 1) {
      const light = this.streetLampLightPool[p];
      const idx = closestI[p];
      if (idx >= 0 && closestD[p] < MAX_RANGE_SQ) {
        const lamp = this.streetLampPositions[idx];
        light.position.set(lamp.x, 3.6, lamp.z);
        light.intensity = peakIntensity;
      } else {
        light.intensity = 0;
      }
    }
  }

  /** Drive lighting + sky tint by time of day. progress is 0..1 over a
   * 24h game-day, divided as 8h night + 4h dawn/dusk + 12h day:
   *   0.000 – 0.083  dawn  (2h, brightening)
   *   0.083 – 0.583  day   (12h, full sun)
   *   0.583 – 0.667  dusk  (2h, darkening)
   *   0.667 – 1.000  night (8h, dark)
   * Engine ticks this every frame. */
  applyDayNight(progress: number): { skyColor: number } {
    const DAWN_END = 0.083;
    const DAY_END = 0.583;
    const DUSK_END = 0.667;
    // "dayness" is 0 in deep night, 1 in full daylight, ramping on
    // dawn + ramping off dusk. Drives sun + ambient.
    let dayness: number;
    if (progress < DAWN_END) {
      dayness = progress / DAWN_END;            // 0 → 1
    } else if (progress < DAY_END) {
      dayness = 1;
    } else if (progress < DUSK_END) {
      dayness = 1 - (progress - DAY_END) / (DUSK_END - DAY_END); // 1 → 0
    } else {
      dayness = 0;
    }

    // Sun: bright during day, dim during dawn/dusk, near-black at
    // night. Floor dropped to 0.04 (was 0.12) so streetlamps actually
    // pop against the dark instead of fighting the residual sun.
    const sunIntensity = 0.04 + dayness * 1.78;
    this.sunLight.intensity = sunIntensity;
    this.sunLight.color.setHex(mixColors(0xaab8d6, 0xfff4d8, dayness));

    // Phase 9.51 — swing the sun across the sky with the time of day so
    // SHADOWS sweep + lengthen toward dawn/dusk instead of only dimming.
    // Azimuth runs east→west over the day; height peaks at midday. Kept
    // well inside the shadow frustum (±20) so shadows never clip. The
    // sun's near-black night intensity already hides shadows then, so
    // the low night position is invisible — it's the day arc that reads.
    const sunAzimuth = (progress - 0.5) * Math.PI * 0.55; // ~-0.86 → +0.86 rad
    const sunHeight = 7 + dayness * 11;                   // 7 (night) → 18 (noon)
    this.sunLight.position.set(Math.sin(sunAzimuth) * 11, sunHeight, 6);

    // Ambient: warm bright during day, cool blue at night. Floor
    // dropped to 0.14 (was 0.32) so the city genuinely feels dark
    // beyond the lamps' pools of light. The night ambient still has
    // SOME value so the player can navigate without lamps; we just
    // shouldn't paint the entire scene a flat bluish glow.
    this.ambientLight.color.setHex(mixColors(0x707a92, 0xfff1d6, dayness));
    this.ambientLight.intensity = 0.14 + dayness * 0.86;

    // Fill (sky bounce) — fades with daylight. Floor dropped to 0.06
    // so back-lit surfaces don't read as "the sun's still up a bit."
    this.fillLight.intensity = 0.06 + dayness * 0.44;

    // Sky color — sunrise/sunset orange during the transitions, deep
    // navy at night, cream during the day.
    let skyColor: number;
    if (progress < DAWN_END) {
      // night → orange dawn → day
      const t = progress / DAWN_END;
      skyColor = t < 0.5
        ? mixColors(0x12162a, 0xb27a52, t / 0.5)
        : mixColors(0xb27a52, 0xd8c4a3, (t - 0.5) / 0.5);
    } else if (progress < DAY_END) {
      skyColor = 0xd8c4a3;
    } else if (progress < DUSK_END) {
      // day → orange dusk → night
      const t = (progress - DAY_END) / (DUSK_END - DAY_END);
      skyColor = t < 0.5
        ? mixColors(0xd8c4a3, 0xb27a52, t / 0.5)
        : mixColors(0xb27a52, 0x12162a, (t - 0.5) / 0.5);
    } else {
      skyColor = 0x12162a; // darker than the old 0x1f2a48
    }

    // Weather tints — layered on top of the day/night base. Rainy and
    // cold tint everything cooler regardless of time of day (they're
    // "all-time" weathers per the user spec). Cloudy and sunny only
    // bite during the day window — at night they're invisible anyway
    // because dayness == 0 collapses their effect to nothing.
    skyColor = this.applyWeatherTints(skyColor, dayness);

    // Lamp intensity tracks `1 - dayness` so bulbs warm up over the
    // dusk window, stay lit through deep night, then dim out across
    // the dawn window. Bad weather bumps the lamp glow a little so the
    // interior reads as the cozy refuge from the storm.
    let lampBoost = 0;
    if (this.currentWeather === "heavy-rain") lampBoost = 0.30;
    else if (this.currentWeather === "rainy") lampBoost = 0.18;
    else if (this.currentWeather === "snowy") lampBoost = 0.20;
    else if (this.currentWeather === "cold") lampBoost = 0.10;
    else if (this.currentWeather === "cloudy") lampBoost = 0.05;
    this.updateLamps(Math.min(1, (1 - dayness) + lampBoost));
    // Floor wetness — drives the rainy / heavy-rain "wet pavement"
    // look. Driven by WeatherEffects so the renderer + the particles
    // share one source of truth.
    this.applyFloorWetness();
    return { skyColor };
  }

  /** Apply the WeatherEffects wetness value to the floor material.
   * Higher wetness = glossier + slightly darker = wet asphalt look.
   * The slight darkening comes from a lerp toward a cool damp brown so
   * heavy rain visibly soaks the floor compared to a light shower. */
  private applyFloorWetness(): void {
    if (!this.floorMat) return;
    const wetness = this.weatherEffects?.getWetness() ?? 0;
    // Cache the dry roughness + colour on first call so we can lerp
    // toward it whenever wetness returns to zero without re-deriving
    // it from theme state.
    if (!this.floorDryState) {
      this.floorDryState = {
        roughness: this.floorMat.roughness,
        color: this.floorMat.color.getHex(),
      };
    }
    const dry = this.floorDryState;
    // Wet floor: roughness 0.85 → 0.30 = strongly reflective. Colour
    // mixes toward a cool damp slate so the wetness reads even without
    // strong specular highlights.
    const wetRoughness = 0.30;
    const wetColor = 0x4a4238;
    this.floorMat.roughness = dry.roughness + (wetRoughness - dry.roughness) * wetness;
    this.floorMat.color.setHex(mixColors(dry.color, wetColor, wetness * 0.55));
  }
  private floorDryState?: { roughness: number; color: number };

  /** Layer weather-specific tints on top of the day/night ambient.
   * Rainy + cold cool the sun + ambient and shift the sky grey/blue.
   * Cloudy dulls the sun and tints the sky greyer (daytime only).
   * Festival warms ambient slightly + brightens lamps for a party
   * mood. Sunny passes through untouched — it IS the default.
   *
   * Returns the (possibly modified) sky colour so the renderer's
   * background also picks up the storm-grey / overcast / festive tint. */
  private applyWeatherTints(skyColor: number, dayness: number): number {
    switch (this.currentWeather) {
      case "rainy": {
        // Cool grey-blue overcast. Strong all the time — rainy nights
        // also feel storm-darkened. Sun dimmed to ~30%, ambient gets a
        // chilly tint.
        skyColor = mixColors(skyColor, 0x4a5868, 0.55);
        this.sunLight.intensity *= 0.40;
        this.sunLight.color.setHex(mixColors(this.sunLight.color.getHex(), 0x8aa0c0, 0.55));
        this.ambientLight.color.setHex(mixColors(this.ambientLight.color.getHex(), 0x7a8aa0, 0.5));
        this.ambientLight.intensity *= 0.80;
        break;
      }
      case "heavy-rain": {
        // Dramatic storm — much darker sky + cooler tint, sun nearly
        // gone behind the cloudbank. Reads as "you really don't want
        // to be outside right now".
        skyColor = mixColors(skyColor, 0x2c343e, 0.80);
        this.sunLight.intensity *= 0.18;
        this.sunLight.color.setHex(mixColors(this.sunLight.color.getHex(), 0x6680a0, 0.75));
        this.ambientLight.color.setHex(mixColors(this.ambientLight.color.getHex(), 0x5a6878, 0.7));
        this.ambientLight.intensity *= 0.65;
        break;
      }
      case "cold": {
        // Pale cool-white snowstorm tint. Snow reflects so it's not as
        // dark as rain — sun keeps ~60% intensity but its colour goes
        // toward white-blue and the sky pales.
        skyColor = mixColors(skyColor, 0xc8d4e0, 0.45);
        this.sunLight.intensity *= 0.70;
        this.sunLight.color.setHex(mixColors(this.sunLight.color.getHex(), 0xe0e8f4, 0.5));
        this.ambientLight.color.setHex(mixColors(this.ambientLight.color.getHex(), 0xcdd5e2, 0.4));
        break;
      }
      case "snowy": {
        // Heavy snowfall — pale silver-grey overcast, sun much weaker,
        // ambient gets a cooler white. Visibility through the falling
        // snow stays high because the flakes themselves reflect light.
        skyColor = mixColors(skyColor, 0xbcc4d0, 0.65);
        this.sunLight.intensity *= 0.50;
        this.sunLight.color.setHex(mixColors(this.sunLight.color.getHex(), 0xd8e0ec, 0.65));
        this.ambientLight.color.setHex(mixColors(this.ambientLight.color.getHex(), 0xc0c8d4, 0.55));
        this.ambientLight.intensity *= 0.95;
        break;
      }
      case "cloudy": {
        // Overcast — only meaningful during the day. The mix amount
        // tracks dayness so dusk + night stay night-blue, not a
        // washed-out grey.
        const t = dayness * 0.5;
        skyColor = mixColors(skyColor, 0x9aa2ad, t);
        this.sunLight.intensity *= (1 - dayness * 0.45);
        this.ambientLight.color.setHex(mixColors(this.ambientLight.color.getHex(), 0xa8aebc, t));
        break;
      }
      case "festival": {
        // Warmer ambient + a pop of pink in the sky. Subtle — confetti
        // particles carry most of the visual signature.
        skyColor = mixColors(skyColor, 0xf4b8c8, 0.20);
        this.ambientLight.color.setHex(mixColors(this.ambientLight.color.getHex(), 0xffd0b8, 0.30));
        this.ambientLight.intensity *= 1.1;
        break;
      }
      case "sunny": {
        // Bright sunshine boost — only meaningful during the day
        // window (multiplied by dayness so dusk + night stay quiet).
        // Sun gets up to +60%, ambient +35%, fill +30%, and the sky
        // mixes toward a near-white warm cream so the lit world reads
        // as "perfect summer afternoon" instead of just "the default".
        const boost = dayness;
        this.sunLight.intensity *= 1 + 0.6 * boost;
        this.sunLight.color.setHex(mixColors(this.sunLight.color.getHex(), 0xfff6e0, 0.35 * boost));
        this.ambientLight.intensity *= 1 + 0.35 * boost;
        this.ambientLight.color.setHex(mixColors(this.ambientLight.color.getHex(), 0xfff4e0, 0.30 * boost));
        this.fillLight.intensity *= 1 + 0.30 * boost;
        skyColor = mixColors(skyColor, 0xf2e1c0, 0.30 * boost);
        break;
      }
      default:
        // Unknown weather id — leave the dayness ramp alone.
        break;
    }
    return skyColor;
  }

  /** Engine pushes the active weather id each tick (cheap; idempotent).
   * Triggers a particle-system swap inside WeatherEffects and updates
   * the cached weather so the next applyDayNight call uses the new
   * tint. */
  setWeather(id: string): void {
    if (this.currentWeather === id) return;
    this.currentWeather = id;
    this.weatherEffects?.setWeather(id);
  }

  /** Tick weather particles. cameraPos is consumed so the volume
   * tracks the player's view across long sessions. */
  updateWeather(dt: number, cameraPos: THREE.Vector3): void {
    this.weatherEffects?.update(dt, cameraPos);
  }

  /** Exposed so the DecorPanel can swap colors when the player picks a
   * different theme. */
  floorMat!: THREE.MeshStandardMaterial;
  wallMat!: THREE.MeshStandardMaterial;
  /** Transparent "glass" version of wallMat used for walls between the
   * camera and the room interior. Color is intentionally not themed —
   * the peach tint reads as a CSS-cutaway hint rather than an actual
   * wall colour. */
  private wallGhostMat!: THREE.MeshStandardMaterial;
  // wallBack / wallLeft / wallRight removed — every perimeter wall
  // is now stored in the perimeterWalls map below.
  /** Exterior-facing wall material. Each wall segment uses a 6-material
   * array so the inside face is wallMat and the outside face is this.
   * Lets a window cut through and show the interior cream on the room
   * side and the building beige on the outside. */
  private wallExteriorMat!: THREE.MeshStandardMaterial;
  /** Per-direction state for the four perimeter walls — all dynamic
   * now so windows + doors can cut them. Single-mesh back/left/right
   * walls were replaced with a segmented system identical to the
   * front wall. */
  /** Per-floor perimeter wall state. Floor 0 is the ground floor (parent =
   * scene, yOffset = 0, wallMatRef defaults to the shared wallMat). Upper
   * storeys add entries at storey-build time with parent = storey group,
   * yOffset = idx * STOREY_HEIGHT, and wallMatRef = the per-storey clone
   * so theme switching from DecorModal lands on the right floor. */
  private readonly perimeterWalls: Map<number, Map<WallDir, PerimeterWallState>> = new Map([
    [0, new Map<WallDir, PerimeterWallState>([
      ["front", { meshes: [], currentMat: "ghost", doors: [] as number[], windows: [] as number[] }],
      ["back",  { meshes: [], currentMat: "solid", doors: [] as number[], windows: [] as number[] }],
      ["left",  { meshes: [], currentMat: "solid", doors: [] as number[], windows: [] as number[] }],
      ["right", { meshes: [], currentMat: "ghost", doors: [] as number[], windows: [] as number[] }],
    ])],
  ]);

  // === Multi-storey building ===========================================
  // The original building was a single ground floor. The tier-expansion
  // system extends it upward: every luxury-tier purchase past T1 unlocks
  // one more storey above the ground floor (T2 → storey 1, T3 → storey
  // 2, … T5 → storey 4). Each storey is STOREY_HEIGHT (3 m) tall and
  // sits on the same 10×10 m footprint as the ground floor.
  //
  // Phase 1 ships the geometry only — hidden by default, no per-floor
  // furniture, no camera focus, no walking between floors. Subsequent
  // phases will layer those on top.
  private static readonly STOREY_HEIGHT = 3;
  private static readonly NUM_STOREYS = 5;
  /** Per-storey geometry references keyed by storey index (1..NUM_STOREYS-1).
   * Holds the group (for visibility toggle), the floor slab (toggled
   * solid vs ghost so the player can see down through it), and the
   * four perimeter walls keyed by direction (toggled by the same
   * camera-relative ghost rule the ground floor uses). */
  private upperStoreys = new Map<number, {
    group: THREE.Group;
    slab: THREE.Mesh;
    walls: Map<WallDir, THREE.Mesh>;
    /** Per-storey material instances so DecorModal can theme each
     * upper floor independently from the ground floor. Cloned from
     * `slabMatSolid` / `wallMat` at construction. */
    slabMat: THREE.MeshStandardMaterial;
    wallMat: THREE.MeshStandardMaterial;
  }>();
  /** Staircase flights keyed by the storey index they LEAD UP TO
   * (1..NUM_STOREYS-1). Each flight is parented to the LOWER storey it
   * leaves from so it visually belongs to that floor — focusing on the
   * lower floor reveals the stair going up. For idx === 1 the parent is
   * the main scene (the ground floor has no group). Visibility is
   * gated independently by tier (only show a flight if its destination
   * storey is unlocked). */
  private stairFlights = new Map<number, THREE.Group>();
  /** Boarded-up placeholder stairwell shown ONLY at tier 1 (before Floor 1
   * unlocks the real flight-1). Public so Engine can raycast it for the
   * click-to-Expand affordance. setLuxuryTier toggles its visibility. */
  brokenStairwell?: THREE.Group;
  /** Roof cap at y = NUM_STOREYS * STOREY_HEIGHT. Visible whenever any
   * upper storey is — gives the building a finished top instead of an
   * open box. Also ghost-able so the iso camera can see down through it
   * when focused on a lower storey. */
  private buildingRoof?: THREE.Mesh;
  /** Material for upper-storey floor slabs. Only one variant needed
   * now that storeys above the focused floor are hidden outright;
   * the ghost-when-above variant we used previously is gone. */
  private slabMatSolid!: THREE.MeshStandardMaterial;
  private roofMatSolid!: THREE.MeshStandardMaterial;
  /** Which storey the camera is currently focused on (0 = ground).
   * Everything above this storey gets the ghost treatment so the player
   * can see down into the focused floor. Phase 4 will let the player
   * change this from the HUD; for now it stays at 0. */
  private focusedStorey = 0;
  /** Exterior mode — when true (player has zoomed out past 40%), the
   * building reads as a closed structure: every wall solid, every
   * unlocked storey + roof visible regardless of focus, and the
   * camera-relative ghost rule in updateWallVisibility is bypassed.
   * Engine.tick toggles this based on the camera's zoom percent. */
  private exteriorMode = false;

  private addBuilding(): void {
    // === Exterior ground + props ===
    // addGrassyExterior() owns the lawn, pavement, road, lane lines,
    // and curb. The legacy pavement/road/curb that used to live inline
    // here was a STALE COPY left over from an old refactor — it kept
    // rendering at the old z=7.5 alongside the new geometry, so every
    // attempt to move the sidewalk further from the building was
    // masked by the duplicate still sitting on the original spot.
    this.addGrassyExterior();

    // === Interior floor ===
    // Default starter colors are intentionally bare white — the warm
    // tones moved into purchasable themes the player picks in the Decor
    // menu. See data/themes.ts.
    //
    // Floor + walls + grid are all shifted by (+0.5, +0.5) so they
    // enclose the same area as the grid (which has lines at half-integer
    // coords so items placed at integer coords appear inside tiles).
    // Before this shift the floor extended 0.5 past the grid on the
    // back/left, which the player saw as "floor leaking outside the
    // restaurant".
    this.floorMat = new THREE.MeshStandardMaterial({ color: 0xf2f2f0, roughness: 0.95, metalness: 0 });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(10, 10), this.floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0.5, 0.0, 0.5); // sits above grass; +0.5 shift to align with tile-center convention
    floor.receiveShadow = true;
    this.threeScene.add(floor);

    // Grid overlay removed by player request — the floor reads cleanly
    // without the half-integer tile guides. Build / move modes still
    // snap to the same grid; the SeatMarkers + ghost previews provide
    // enough visual feedback during placement that the grid lines were
    // adding noise without information.

    // === Walls ===
    // Three materials:
    //   wallMat       — interior cream colour, room-facing face
    //   wallExtMat    — exterior beige, outside-facing face
    //   wallGhostMat  — see-through used when the camera is on this
    //                   wall's outdoor side so it doesn't block the
    //                   view of the dining room
    // updateWallVisibility picks solid vs ghost per wall every frame.
    this.wallMat = new THREE.MeshStandardMaterial({ color: 0xfafafa, roughness: 0.85 });
    this.wallExteriorMat = new THREE.MeshStandardMaterial({ color: 0xc8a878, roughness: 0.78 });
    this.wallGhostMat = new THREE.MeshStandardMaterial({
      color: 0xe8a98a, roughness: 0.6,
      transparent: true, opacity: 0.15, depthWrite: false,
    });
    // All 4 perimeter walls are dynamic segmented meshes now —
    // doors cut a full-height gap (+ lintel), windows cut a sill +
    // lintel partial gap so the player can see in / out through them
    // from either side. Default door list is empty; the engine
    // refills both lists from the registry as the player builds.
    this.rebuildPerimeterWall(0, "front", [], []);
    this.rebuildPerimeterWall(0, "back",  [], []);
    this.rebuildPerimeterWall(0, "left",  [], []);
    this.rebuildPerimeterWall(0, "right", [], []);
    // Restaurant rating sign mounted on the lintel — a small marquee
    // that shows the current ★ rating, just like a real bistro
    // entrance. Hooked up by updateRatingSign() from Engine.
    this.buildRatingSign();

    // Supply / receiving counter at the back-left wall — the errand
    // helper reports here with each delivery instead of walking out
    // the front door (which got in the customers' way and didn't read
    // as the back-of-house workflow it actually is). Procedural so it
    // shows for both new and existing saves regardless of registry state.
    this.buildSupplyCounter();

    // Upper storeys (tier expansions). Hidden by default; setLuxuryTier
    // toggles them as the player buys each expansion.
    this.addUpperStoreys();
    // Paris-style exterior decoration — cornice bands between floors,
    // iron balconies on upper storeys, mansard roof on top. Matches the
    // city's other Paris shells so the player's building reads as part
    // of the same neighbourhood. Per-storey decor parents into each
    // upper-storey group so it inherits the existing tier / focus
    // visibility (no extra wiring needed). The mansard is managed
    // separately because its Y depends on the current luxury tier.
    this.addParisExteriorDecor();
  }

  /** Build the 10×10 slab geometry with a rectangular stairwell hole
   * cut at the back-left corner — the spot where the staircase coming
   * up from the storey below emerges. The hole matches the stair
   * footprint (X∈[−4.4,−3.4], Z∈[−4.4,−1.4], a 1 × 3 m opening) and is
   * expressed in the slab's *local* coordinates (slab is then rotated
   * −π/2 around X and translated to (0.5, baseY, 0.5), exactly like
   * the original PlaneGeometry slab). */
  private makeSlabWithStairHole(W: number): THREE.ShapeGeometry {
    const half = W / 2;
    const shape = new THREE.Shape();
    shape.moveTo(-half, -half);
    shape.lineTo( half, -half);
    shape.lineTo( half,  half);
    shape.lineTo(-half,  half);
    shape.lineTo(-half, -half);
    // Hole in local coords. Slab origin sits at (0.5, baseY, 0.5) in
    // world; after the −π/2 rotation around X, shape-Y maps to world
    // −Z. So a world hole at X∈[−4.4,−3.4], Z∈[−4.4,−1.4] becomes:
    //   local X = world X − 0.5  → [−4.9, −3.9]
    //   local Y = 0.5 − world Z  → [4.9, 1.9]
    const hole = new THREE.Path();
    hole.moveTo(-4.9, 1.9);
    hole.lineTo(-4.9, 4.9);
    hole.lineTo(-3.9, 4.9);
    hole.lineTo(-3.9, 1.9);
    hole.lineTo(-4.9, 1.9);
    shape.holes.push(hole);
    return new THREE.ShapeGeometry(shape);
  }

  /** Build the empty white shell for each storey above the ground floor.
   * Each storey gets a floor plane (with a stairwell cut at the back-
   * left corner), four solid perimeter walls (tracked by direction so
   * the ghost pass can flip them), and the top of the stack carries a
   * separate roof cap. All hidden by default; the setLuxuryTier pass
   * toggles the appropriate ones on per tier. Staircases live in a
   * SECOND pass (further down) and are parented to the storey they
   * LEAVE FROM, so the player sees a flight going up when focused on
   * the floor below — matching how stairs read visually in real
   * architecture. */
  private addUpperStoreys(): void {
    const W = 10;                                          // footprint, same as ground floor
    const H = WorldScene.STOREY_HEIGHT;
    // T (wall thickness) and wallSpecs are no longer needed here — the
    // wall geometry comes from rebuildPerimeterWall / wallBoxFor /
    // wallSegmentPosition now, with this storey's parent + yOffset
    // wired through the per-floor PerimeterWallState entries below.
    // Slab materials — solid (blank-canvas off-white) and ghost (see-
    // through pale). Stored on the instance so applyUpperStoreyVisibility
    // can flip per-mesh references between them.
    this.slabMatSolid = new THREE.MeshStandardMaterial({
      color: 0xf6f4ef, roughness: 0.95, metalness: 0, side: THREE.DoubleSide,
    });
    this.roofMatSolid = new THREE.MeshStandardMaterial({
      color: 0xe8d8b8, roughness: 0.9, side: THREE.DoubleSide,
    });
    // First pass: build every storey's slab + walls. Slabs use
    // ShapeGeometry so we can cut a stairwell opening at the back-left
    // corner where the flight below emerges.
    for (let idx = 1; idx < WorldScene.NUM_STOREYS; idx += 1) {
      const group = new THREE.Group();
      group.visible = false;
      const baseY = idx * H;
      // Per-storey material clones so DecorModal can theme each upper
      // floor independently. Without this every floor would share the
      // ground floor's wall + slab colours.
      const slabMat = this.slabMatSolid.clone();
      const wallMatStorey = this.wallMat.clone();
      // Floor of this storey == ceiling of the storey below, with a
      // 1 × 3 m rectangular opening at the back-left corner so the
      // staircase rising from below can emerge through it.
      const slab = new THREE.Mesh(this.makeSlabWithStairHole(W), slabMat);
      slab.rotation.x = -Math.PI / 2;
      slab.position.set(0.5, baseY, 0.5);
      slab.receiveShadow = true;
      group.add(slab);
      // 4 perimeter walls — registered into the per-floor perimeterWalls
      // map so they get the same dynamic cut/lintel pipeline as the
      // ground floor. Without this, windows placed on Floor 1+ left no
      // hole in the wall (the player saw the window mesh from outside
      // but the inside view was a solid wall). yOffset = baseY shifts
      // every segment up to this storey's slab; parent = group means
      // they hide with the storey when it's out of focus; wallMatRef =
      // wallMatStorey ties them into the per-floor theme clone.
      const floorMap = new Map<WallDir, PerimeterWallState>([
        ["front", { meshes: [], currentMat: "ghost", doors: [], windows: [], yOffset: baseY, parent: group, wallMatRef: wallMatStorey }],
        ["back",  { meshes: [], currentMat: "solid", doors: [], windows: [], yOffset: baseY, parent: group, wallMatRef: wallMatStorey }],
        ["left",  { meshes: [], currentMat: "solid", doors: [], windows: [], yOffset: baseY, parent: group, wallMatRef: wallMatStorey }],
        ["right", { meshes: [], currentMat: "ghost", doors: [], windows: [], yOffset: baseY, parent: group, wallMatRef: wallMatStorey }],
      ]);
      this.perimeterWalls.set(idx, floorMap);
      // walls map is now populated lazily by the first rebuild call —
      // keep an empty placeholder so the upperStoreys interface stays
      // stable for callers (updateWallVisibility, etc.).
      const walls = new Map<WallDir, THREE.Mesh>();
      this.upperStoreys.set(idx, { group, slab, walls, slabMat, wallMat: wallMatStorey });
      this.threeScene.add(group);
      // Build the initial (no-opening) wall segments for this storey
      // right away — Engine will call rebuildAllPerimeterWalls later
      // when furniture is restored, but until then the slab needs walls
      // around it or the storey reads as a floating plate.
      this.rebuildPerimeterWall(idx, "front", [], []);
      this.rebuildPerimeterWall(idx, "back",  [], []);
      this.rebuildPerimeterWall(idx, "left",  [], []);
      this.rebuildPerimeterWall(idx, "right", [], []);
    }
    // Second pass: each flight is parented to the storey it LEAVES
    // FROM (idx-1), so revealing the lower floor reveals the stair
    // going up. For idx === 1 the parent is the main scene because
    // the ground floor lives directly in the scene, not in a group.
    for (let idx = 1; idx < WorldScene.NUM_STOREYS; idx += 1) {
      const baseY = idx * H;
      const stairGroup = new THREE.Group();
      this.addStaircaseSegment(stairGroup, baseY);
      this.stairFlights.set(idx, stairGroup);
      if (idx === 1) {
        // Ground → Floor 1: parent is the main scene. Always visible
        // when Floor 1 is unlocked, regardless of focus.
        stairGroup.visible = false;
        this.threeScene.add(stairGroup);
      } else {
        // Floor N → Floor N+1: parent is the lower storey's group.
        const lowerGroup = this.upperStoreys.get(idx - 1)!.group;
        lowerGroup.add(stairGroup);
      }
    }
    // Boarded-up placeholder stairwell in the same back-left footprint, at
    // ground level. Shown ONLY at tier 1 (setLuxuryTier); reserves those tiles
    // (BuildMenu.isStairwellCell) and, when clicked, opens Expand (Engine).
    const broken = new THREE.Group();
    buildBrokenStairwell(broken);
    broken.visible = false;   // setLuxuryTier sets the real state
    this.threeScene.add(broken);
    this.brokenStairwell = broken;
    // Roof at the top of the topmost storey. Replaced in
    // addParisExteriorDecor by a mansard whose Y tracks the player's
    // current luxury tier. This flat plane is kept invisible as a
    // legacy fallback; the mansard takes over visually.
    const roof = new THREE.Mesh(new THREE.PlaneGeometry(W, W), this.roofMatSolid);
    roof.rotation.x = -Math.PI / 2;
    roof.position.set(0.5, WorldScene.NUM_STOREYS * H, 0.5);
    roof.receiveShadow = true;
    roof.visible = false;
    this.threeScene.add(roof);
    this.buildingRoof = roof;
  }

  /** Slate-grey mansard roof that replaces the legacy flat cap.
   * Re-positioned in applyStoreyVisibility so it always sits on top
   * of the topmost UNLOCKED storey (tier 1 → on top of ground,
   * tier 5 → on top of floor 4). Visible only in exterior mode so
   * interior views can still look down through an open top. */
  private parisMansard?: THREE.Mesh;
  private parisMansardCap?: THREE.Mesh;
  private parisMansardChimney?: THREE.Mesh;
  /** Iron-balcony meshes (rails + balusters) per upper storey. Tracked
   * so applyStoreyVisibility can hide them in interior mode — when
   * the player has zoomed in for inside work, the spiky black rails
   * floating outside the wall just clutter the view. */
  private parisBalconies: THREE.Mesh[] = [];

  /** Add cornice bands between floors + iron balconies on upper
   * storeys + a tier-tracking mansard roof. Mirrors the makeBuildingShell
   * vocabulary so the player's restaurant reads as the same Paris
   * Haussmann style as the city's other plot shells. */
  private addParisExteriorDecor(): void {
    const W = 10;
    const H = WorldScene.STOREY_HEIGHT;
    // Building footprint: walls run from -4.5 to +5.5 (W=10 wide,
    // centred on x = 0.5). The +Z (south) face is the front.
    const cornerOffsetCx = 0.5;
    const cornerOffsetCz = 0.5;
    const corniceMat = new THREE.MeshStandardMaterial({
      color: 0xc8b888, roughness: 0.8,
    });
    const balconyMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a, roughness: 0.4, metalness: 0.7,
    });

    // ── Cornice bands ─────────────────────────────────────────────
    // Four thin perimeter strips per floor (not a full square plate —
    // a square would form an indoor ceiling and hide the upper-floor
    // interior from below). One band at the top of each storey
    // (y = (idx+1)*H). Ground-floor cornice lives at threeScene
    // root; upper-floor cornices parent into each storey's group so
    // they inherit the existing tier + focus visibility.
    const cBandH = 0.14;
    const cBandThick = 0.3;
    const cExtra = 0.3; // how far beyond the wall the band sticks out
    const cLong = W + cExtra * 2;
    for (let idx = 0; idx < WorldScene.NUM_STOREYS; idx += 1) {
      const y = (idx + 1) * H - cBandH / 2;
      const halfW = W / 2;
      // Four sides — south (+Z), north (-Z), east (+X), west (-X).
      const southZ = cornerOffsetCz + halfW + cBandThick / 2;
      const northZ = cornerOffsetCz - halfW - cBandThick / 2;
      const eastX = cornerOffsetCx + halfW + cBandThick / 2;
      const westX = cornerOffsetCx - halfW - cBandThick / 2;
      const sideGeo = new THREE.BoxGeometry(cLong, cBandH, cBandThick);
      const endGeo = new THREE.BoxGeometry(cBandThick, cBandH, cLong);
      const mkSide = (g: THREE.BufferGeometry, x: number, z: number) => {
        const m = new THREE.Mesh(g, corniceMat);
        m.position.set(x, y, z);
        m.castShadow = true;
        m.receiveShadow = true;
        return m;
      };
      const pieces = [
        mkSide(sideGeo, cornerOffsetCx, southZ),
        mkSide(sideGeo, cornerOffsetCx, northZ),
        mkSide(endGeo,  eastX, cornerOffsetCz),
        mkSide(endGeo,  westX, cornerOffsetCz),
      ];
      const parent: THREE.Object3D = idx === 0
        ? this.threeScene
        : (this.upperStoreys.get(idx)?.group ?? this.threeScene);
      for (const p of pieces) parent.add(p);
    }

    // ── Iron balconies on upper storey south walls ───────────────
    // For each upper storey, drop one balcony rail + balusters on
    // the +Z (front) face just below the mid-floor window line.
    // Mirrors makeBuildingShell's balcony for the city shells.
    for (let idx = 1; idx < WorldScene.NUM_STOREYS; idx += 1) {
      const storey = this.upperStoreys.get(idx);
      if (!storey) continue;
      const winY = idx * H + 1.5;             // window centre on upper floor
      const railY = winY - 0.85;              // rail sits ~85 cm below window
      const balusterTopY = winY - 0.4;        // baluster top reaches mid-window
      const southZ = cornerOffsetCz + W / 2 + 0.12; // outside front face
      // Horizontal rail.
      const rail = new THREE.Mesh(
        new THREE.BoxGeometry(W - 0.3, 0.06, 0.10),
        balconyMat,
      );
      rail.position.set(cornerOffsetCx, railY, southZ);
      storey.group.add(rail);
      this.parisBalconies.push(rail);
      // Balusters reduced to a SECOND horizontal bar instead of ~22
      // individual vertical posts per floor. The original loop was
      // 88 baluster meshes on the player's tower alone — at iso
      // distance the verticals were barely visible anyway. Single
      // extra rail gives the visual two-band fence look at 1/22nd
      // the draw-call cost.
      void balusterTopY; // kept for future detail-mode rendering
      const lowerRail = new THREE.Mesh(
        new THREE.BoxGeometry(W - 0.3, 0.04, 0.08),
        balconyMat,
      );
      lowerRail.position.set(cornerOffsetCx, railY - 0.35, southZ);
      storey.group.add(lowerRail);
      this.parisBalconies.push(lowerRail);
    }

    // ── Mansard roof ──────────────────────────────────────────────
    // Two pieces — slate-grey body + slightly inset darker cap — same
    // recipe as makeBuildingShell. Position.y is recomputed every
    // time setLuxuryTier / setExteriorMode runs so it always sits on
    // the topmost unlocked storey. Visibility is exterior-only.
    const mansardH = 1.2;
    const mansard = new THREE.Mesh(
      new THREE.BoxGeometry(W + 0.2, mansardH, W + 0.2),
      new THREE.MeshStandardMaterial({ color: 0x3a4048, roughness: 0.5, metalness: 0.05 }),
    );
    mansard.position.set(cornerOffsetCx, H + mansardH / 2, cornerOffsetCz);
    mansard.castShadow = true;
    mansard.visible = false;
    this.threeScene.add(mansard);
    this.parisMansard = mansard;
    const cap = new THREE.Mesh(
      new THREE.BoxGeometry(W - 0.6, 0.1, W - 0.6),
      new THREE.MeshStandardMaterial({ color: 0x2a3038, roughness: 0.55 }),
    );
    cap.position.set(cornerOffsetCx, H + mansardH + 0.05, cornerOffsetCz);
    cap.visible = false;
    this.threeScene.add(cap);
    this.parisMansardCap = cap;
    // Brick chimney on the back-right corner.
    const chimney = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 1.1, 0.4),
      new THREE.MeshStandardMaterial({ color: 0x9a6850, roughness: 0.9 }),
    );
    chimney.position.set(
      cornerOffsetCx + W / 2 - 0.8,
      H + mansardH + 0.55,
      cornerOffsetCz - W / 2 + 0.8,
    );
    chimney.castShadow = true;
    chimney.visible = false;
    this.threeScene.add(chimney);
    this.parisMansardChimney = chimney;

    // Also paint the wall material more limestone-cream so it matches
    // the shells' wallColors palette. Per-floor theming clones the
    // material so existing themed saves are unaffected; this only
    // updates the base default.
    if (this.wallMat) {
      this.wallMat.color.setHex(0xe6d5b5);
    }
    if (this.wallExteriorMat) {
      this.wallExteriorMat.color.setHex(0xe6d5b5);
    }
  }

  /** Build a single flight of stairs running from one storey UP TO the
   * next. Procedural box steps with a thin banister on the open
   * (interior-facing) side. Fixed position in the back-right corner
   * of the building so the flights stack cleanly on the same column
   * floor after floor.
   *
   * Phase 2 — visual only, no walking logic. Phase 6 will mark the
   * footprint cells as a traversable transit corridor for the
   * pathfinder. */
  private addStaircaseSegment(parent: THREE.Group, baseY: number): void {
    // Delegates to the shared interiorPieces.buildStaircaseFlight so
    // visit mode renders the identical flight when it climbs the same
    // multi-storey shell.
    buildStaircaseFlight(parent, baseY);
  }

  // === Dynamic perimeter walls ===============================================
  // All four perimeter walls are segmented now. Doors cut a full-height
  // gap with a lintel above; windows cut a sill + lintel partial gap so
  // the player can see through them from either side. Each wall has a
  // multi-material box geometry so the inside face wears the interior
  // colour and the outside face wears the building's exterior colour —
  // until the camera lands on the wall's outdoor side, at which point
  // the whole wall (every segment) flips to the ghost see-through.

  /** Outdoor-axis end coordinates for each wall (along the wall's main
   * axis). Together these give the building a 10×10 interior. */
  // Wall axis bounds + opening + sill/lintel constants moved to
  // src/scene/wallBuilder.ts so visit mode can render the same
  // perimeter geometry without WorldScene-private duplicates. The
  // host's rebuildPerimeterWall now calls into buildPerimeterWallSegments
  // for the segment math.

  /** Rebuild one perimeter wall from scratch around the supplied
   * openings. Door positions are along the wall's main axis (X for
   * front/back, Z for left/right) and produce a full-height gap with
   * a 1 m lintel above. Window positions produce a sill + window
   * opening + lintel, leaving the middle band see-through so the
   * actual window mesh placed on top is what the player looks
   * through. */
  rebuildPerimeterWall(floor: number, dir: WallDir, doorEdges: number[], windowEdges: number[]): void {
    const floorMap = this.perimeterWalls.get(floor);
    if (!floorMap) return;
    const state = floorMap.get(dir);
    if (!state) return;
    state.doors = [...doorEdges];
    state.windows = [...windowEdges];
    const parent = state.parent ?? this.threeScene;
    const yOffset = state.yOffset ?? 0;
    const wallMat = state.wallMatRef ?? this.wallMat;
    // Tear down every existing mesh + free its geometry. Remove from
    // whichever parent the segments live under (scene for floor 0,
    // storey group for upper floors).
    for (const m of state.meshes) {
      parent.remove(m);
      m.geometry.dispose();
    }
    state.meshes.length = 0;
    // Delegate the geometry + opening-loop math to the shared
    // wallBuilder so visit mode renders structurally identical walls
    // when it picks the same openings out of the save snapshot.
    // The host keeps ownership of the per-face material array
    // (interior cream + exterior beige + ghost transparency) and the
    // mesh-tracking on `state.meshes` for teardown / ghost-swap.
    const mats = this.materialsFor(dir, state.currentMat, wallMat);
    const meshes = buildPerimeterWallSegments(parent, dir, doorEdges, windowEdges, {
      yOffset,
      resolveMaterial: () => mats,
      castShadow: true,
      receiveShadow: true,
    });
    state.meshes.push(...meshes);
  }

  /** Per-face material array for a wall box. Inside face uses
   * wallMat, outside face uses wallExteriorMat, top / bottom / end-
   * caps fall back to wallMat. Returns six copies of wallGhostMat
   * when the wall is currently ghost-faded (camera on its outside). */
  private materialsFor(dir: WallDir, kind: "solid" | "ghost", wallMat?: THREE.MeshStandardMaterial): THREE.Material[] {
    if (kind === "ghost") {
      return [
        this.wallGhostMat, this.wallGhostMat, this.wallGhostMat,
        this.wallGhostMat, this.wallGhostMat, this.wallGhostMat,
      ];
    }
    // Per-storey clone if supplied (upper floors), shared wallMat otherwise.
    const int = wallMat ?? this.wallMat;
    const ext = this.wallExteriorMat;
    // BoxGeometry face order: [+X, -X, +Y, -Y, +Z, -Z].
    switch (dir) {
      case "front": return [int, int, int, int, ext, int];
      case "back":  return [int, int, int, int, int, ext];
      case "left":  return [int, ext, int, int, int, int];
      case "right": return [ext, int, int, int, int, int];
    }
  }

  /** Legacy entry-point — Engine still calls this when only doors
   * changed. Forwards into the new system with the wall's current
   * window list preserved. */
  rebuildFrontWall(doorXs: readonly number[]): void {
    const state = this.perimeterWalls.get(0)?.get("front");
    if (!state) return;
    this.rebuildPerimeterWall(0, "front", [...doorXs], state.windows);
  }

  /** Re-render every perimeter wall on EVERY floor from the supplied
   * openings. Engine groups openings by floor + direction so a window
   * placed on Floor 1 only rebuilds Floor 1's walls. Floors with no
   * matching state (storey not built yet) are silently skipped. */
  rebuildAllPerimeterWalls(openingsByFloor: Map<number, Record<WallDir, { doors: number[]; windows: number[] }>>): void {
    for (const [floor, byDir] of openingsByFloor) {
      if (!this.perimeterWalls.has(floor)) continue;
      this.rebuildPerimeterWall(floor, "front", byDir.front.doors, byDir.front.windows);
      this.rebuildPerimeterWall(floor, "back",  byDir.back.doors,  byDir.back.windows);
      this.rebuildPerimeterWall(floor, "left",  byDir.left.doors,  byDir.left.windows);
      this.rebuildPerimeterWall(floor, "right", byDir.right.doors, byDir.right.windows);
    }
    // Floors with state but no entry in the openings map need a rebuild
    // too — that's what fires when the last window on a storey is sold
    // (the floor drops out of the openings map entirely). Reset them to
    // empty so the cut is filled back in.
    for (const [floor] of this.perimeterWalls) {
      if (openingsByFloor.has(floor)) continue;
      this.rebuildPerimeterWall(floor, "front", [], []);
      this.rebuildPerimeterWall(floor, "back",  [], []);
      this.rebuildPerimeterWall(floor, "left",  [], []);
      this.rebuildPerimeterWall(floor, "right", [], []);
    }
  }

  /** Swap wall materials so the two walls closest to the camera become
   * the transparent ghost and the two far walls stay solid. Driven by
   * the dot product of each wall's outward normal with the camera's
   * world position relative to the building centre — positive means the
   * camera is on the wall's outer face. */
  updateWallVisibility(cameraPos: THREE.Vector3): void {
    if (!this.wallMat || !this.wallGhostMat) return;
    // In exterior mode (player zoomed out past 40%), every wall is
    // solid — the building reads as a closed box, no see-through to
    // the interior. Skip the camera-relative ghost calculation.
    const kindFor = (normalX: number, normalZ: number): "solid" | "ghost" => {
      if (this.exteriorMode) return "solid";
      const dot = normalX * cameraPos.x + normalZ * cameraPos.z;
      return dot > 0 ? "ghost" : "solid";
    };
    const backKind  = kindFor(0, -1);
    const leftKind  = kindFor(-1, 0);
    const rightKind = kindFor(1, 0);
    const frontKind = kindFor(0, 1);
    this.applyWallKind("back",  backKind);
    this.applyWallKind("left",  leftKind);
    this.applyWallKind("right", rightKind);
    this.applyWallKind("front", frontKind);
    // Visible upper-storey walls follow the same 2-camera-side-ghost /
    // 2-back-solid rule as the ground floor. Storeys ABOVE the focused
    // storey are already entirely hidden by applyStoreyVisibility, so
    // there's no slab/roof ghost work to do here — only the visible
    // storeys (focused + below) walk through this loop.
    const dirKinds: Record<WallDir, "solid" | "ghost"> = {
      back: backKind, left: leftKind, right: rightKind, front: frontKind,
    };
    void dirKinds; // applyWallKind already updates each storey's wall
                   // segments via the per-floor perimeterWalls map.
    for (const [, storey] of this.upperStoreys) {
      if (!storey.group.visible) continue;
      // Slab is solid (it's the floor of THIS storey, the ceiling of
      // the one below). Always solid because we only render at-or-
      // below focus. Use the STOREY's cloned material so the per-floor
      // theme picked in DecorModal sticks — without this we used to
      // reassign back to the shared slabMatSolid every frame and the
      // upper floors stayed off-white regardless of theme selection.
      storey.slab.material = storey.slabMat;
      // Walls used to be single static meshes managed here; now they're
      // the segmented per-floor perimeterWalls system rebuilt on the
      // floor's openings and ghosted by applyWallKind. The storey.walls
      // map stays present (empty) for interface compatibility but is no
      // longer the source of truth.
    }
    // Roof similarly: only renders when focused on the top storey;
    // always solid in that case.
    if (this.buildingRoof && this.buildingRoof.visible) {
      this.buildingRoof.material = this.roofMatSolid;
    }
  }

  /** Switch one wall's segments between solid (multi-mat) and ghost on
   * every floor. The same camera-relative dir → kind mapping applies to
   * every storey, so all floors get flipped together (e.g. when the
   * camera ends up on the +Z side of the building, the front wall on
   * the ground floor AND every upper floor goes ghost). */
  private applyWallKind(dir: WallDir, kind: "solid" | "ghost"): void {
    for (const [, floorMap] of this.perimeterWalls) {
      const state = floorMap.get(dir);
      if (!state || state.currentMat === kind) continue;
      state.currentMat = kind;
      const mats = this.materialsFor(dir, kind, state.wallMatRef);
      for (const m of state.meshes) m.material = mats;
    }
  }

  // === Exterior — grass, wildflowers, trees, rocks ============================
  //
  // Replaces the old "noisy vertex-coloured plane + 400 dark cones"
  // exterior with a richer system:
  //
  //   1. Ground plane uses a procedurally-painted canvas texture
  //      (multiple soft Perlin-style blobs across cool green tones)
  //      so the lawn reads as organic patches of varying density
  //      instead of pixelated noise.
  //   2. ~2500 instanced grass-blade "tufts" built from two
  //      perpendicular planes (so the blade is visible from any
  //      angle), each plane painted with a gradient blade.
  //   3. ~150 wildflowers — tiny brightly-coloured discs scattered
  //      sparsely to add pops of colour.
  //   4. ~22 low-poly trees (cone canopy + cylinder trunk) for
  //      vertical interest.
  //   5. ~30 small grey rocks for ground texture.
  //
  // Every prop skips the building interior + the sidewalk / road
  // strip in front so the player's view of the door stays clean.

  private addGrassyExterior(): void {
    this.addGroundPlane();
    this.addGrassBlades();
    this.addWildflowers();
    this.addLawnTrees();
    this.addRocks();
    // Legacy local pavement (addPavementAndRoad) is intentionally NOT
    // called — its z=13.5 road duplicates the SHARED city avenue at
    // the same position for plots in the middle row and Z-fights with
    // every other plot's nearest avenue. The world avenues now serve
    // as the player's main street regardless of plot row, with the
    // street-follow scenery dropping houses + shops along each one.
    this.addGardenArea();
  }

  /** Bounds of the future tier-6 garden — east of the building, mirror
   * dimensions of the building footprint along z. Exposed as a static
   * so the exclusion-zone check and the fence builder share one source
   * of truth. */
  private static readonly GARDEN_BOUNDS = {
    minX: 6.5, maxX: 14.5,
    minZ: -4.5, maxZ: 5.5,
  };

  /** Wooden picket fence + "Coming Soon" sign marking the area
   * reserved for the future tier-6 garden expansion. Pure decor for
   * now — when tier 6 unlocks, the east perimeter wall will accept
   * one door cut into it (similar to the windows pipeline) and a
   * gameplay garden will replace the placeholder. */
  private addGardenArea(): void {
    const b = WorldScene.GARDEN_BOUNDS;
    const fence = new THREE.Group();
    // Light cosy wood for the rails + posts, slightly darker for the
    // post caps so the silhouette reads as separate pieces.
    const postMat = new THREE.MeshStandardMaterial({ color: 0x8a5a30, roughness: 0.9 });
    const railMat = new THREE.MeshStandardMaterial({ color: 0xa07042, roughness: 0.9 });
    const capMat  = new THREE.MeshStandardMaterial({ color: 0x6a4220, roughness: 0.85 });
    const postH = 1.05, postW = 0.10;
    const railH = 0.06, railD = 0.06;
    const upperY = 0.78, lowerY = 0.30;
    const postSpacing = 1.0;  // one post per metre — reads as a real picket fence
    // Helper to drop a single post at (x, z).
    const addPost = (x: number, z: number): void => {
      const post = new THREE.Mesh(new THREE.BoxGeometry(postW, postH, postW), postMat);
      post.position.set(x, postH / 2, z);
      post.castShadow = true;
      post.receiveShadow = true;
      fence.add(post);
      const cap = new THREE.Mesh(new THREE.BoxGeometry(postW * 1.4, 0.07, postW * 1.4), capMat);
      cap.position.set(x, postH + 0.04, z);
      cap.castShadow = true;
      fence.add(cap);
    };
    // Posts along the four sides.
    for (let x = b.minX; x <= b.maxX; x += postSpacing) {
      addPost(x, b.minZ);
      addPost(x, b.maxZ);
    }
    for (let z = b.minZ + postSpacing; z < b.maxZ; z += postSpacing) {
      addPost(b.minX, z);
      addPost(b.maxX, z);
    }
    // Horizontal rails spanning each side at upper + lower heights.
    const addRail = (x: number, z: number, length: number, axis: "x" | "z", y: number): void => {
      const geom = axis === "x"
        ? new THREE.BoxGeometry(length, railH, railD)
        : new THREE.BoxGeometry(railD, railH, length);
      const rail = new THREE.Mesh(geom, railMat);
      rail.position.set(x, y, z);
      rail.castShadow = true;
      rail.receiveShadow = true;
      fence.add(rail);
    };
    const width = b.maxX - b.minX;
    const depth = b.maxZ - b.minZ;
    const cx = (b.minX + b.maxX) / 2;
    const cz = (b.minZ + b.maxZ) / 2;
    // North + south rails (along X). Leave a 1.2 m gap in the middle
    // of the WEST side (facing the building) — a future gate position
    // so the player can see it'll connect to the east wall.
    addRail(cx, b.minZ, width, "x", upperY);
    addRail(cx, b.minZ, width, "x", lowerY);
    addRail(cx, b.maxZ, width, "x", upperY);
    addRail(cx, b.maxZ, width, "x", lowerY);
    // East rail (along Z).
    addRail(b.maxX, cz, depth, "z", upperY);
    addRail(b.maxX, cz, depth, "z", lowerY);
    // West rail (along Z) — split with a 1.2 m centred gap for the
    // future door opening. Each side spans (depth - gap) / 2.
    const gateGap = 1.2;
    const halfSide = (depth - gateGap) / 2;
    const northHalfCz = b.minZ + halfSide / 2;
    const southHalfCz = b.maxZ - halfSide / 2;
    addRail(b.minX, northHalfCz, halfSide, "z", upperY);
    addRail(b.minX, northHalfCz, halfSide, "z", lowerY);
    addRail(b.minX, southHalfCz, halfSide, "z", upperY);
    addRail(b.minX, southHalfCz, halfSide, "z", lowerY);
    this.threeScene.add(fence);
    // === Coming Soon sign in the centre ===
    const signPostH = 1.4;
    const signPost = new THREE.Mesh(
      new THREE.BoxGeometry(0.10, signPostH, 0.10),
      new THREE.MeshStandardMaterial({ color: 0x5a3e1e, roughness: 0.9 }),
    );
    signPost.position.set(cx, signPostH / 2, cz);
    signPost.castShadow = true;
    this.threeScene.add(signPost);
    // Sign board — canvas-painted "COMING SOON / GARDEN EXPANSION".
    const signTex = WorldScene.makeComingSoonTexture();
    const signMat = new THREE.MeshStandardMaterial({
      map: signTex, transparent: false, roughness: 0.7,
      side: THREE.DoubleSide,
    });
    const signBoard = new THREE.Mesh(new THREE.PlaneGeometry(2.0, 1.0), signMat);
    signBoard.position.set(cx, 1.7, cz);
    // Face north (toward the building) so the player sees the writing
    // from inside the restaurant.
    signBoard.rotation.y = Math.PI;
    signBoard.castShadow = true;
    this.threeScene.add(signBoard);
    // Frame around the sign for some depth.
    const frameMat = new THREE.MeshStandardMaterial({ color: 0x3e2810, roughness: 0.85 });
    const frame = new THREE.Mesh(new THREE.BoxGeometry(2.1, 1.1, 0.06), frameMat);
    frame.position.set(cx, 1.7, cz + 0.02);
    frame.castShadow = true;
    this.threeScene.add(frame);
  }

  /** Procedural "COMING SOON" sign texture — warm cream background
   * with bold dark lettering. Read from across the restaurant. */
  private static makeComingSoonTexture(): THREE.CanvasTexture {
    const w = 512, h = 256;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#f4e8c8";
    ctx.fillRect(0, 0, w, h);
    // Inner border accent
    ctx.strokeStyle = "#7a4a20";
    ctx.lineWidth = 6;
    ctx.strokeRect(14, 14, w - 28, h - 28);
    ctx.fillStyle = "#3a1f08";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "900 70px Georgia, serif";
    ctx.fillText("COMING SOON", w / 2, h / 2 - 38);
    ctx.font = "700 36px Georgia, serif";
    ctx.fillStyle = "#5e3a10";
    ctx.fillText("Garden Expansion", w / 2, h / 2 + 38);
    // Small leaf accent — two little arcs to suggest greenery.
    ctx.fillStyle = "#3a7a3a";
    ctx.beginPath();
    ctx.ellipse(80, h - 50, 24, 12, -0.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(w - 80, h - 50, 24, 12, 0.4, 0, Math.PI * 2);
    ctx.fill();
    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  }

  /** Big ground plane painted with a canvas texture of soft green
   * patches. The texture repeats so the lawn fills the whole 90×90 m
   * area without revealing the seam. (Bumped from 70 to 90 so the
   * full-map-width road + pavements don't poke past the lawn edge.) */
  private addGroundPlane(): void {
    const tex = WorldScene.makeGrassTexture();
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    // Tile density matches what 360 m looked good at (22 tiles).
    // Scale to the bigger plane so each tile is still ~16 m square.
    tex.repeat.set(96, 96);
    // 1500×1500 grass — big enough that the camera's max zoom-out
    // (half-view 200, so a 16:9 viewport is ~712×400 m) PLUS any
    // pan + rotation can't see a hard plane edge. Visually the city
    // still ends around ±110 m (no scenery beyond that) and fog
    // takes over by ~250 m from the camera, so the player just sees
    // warm haze where the lawn would otherwise show its boundary.
    const grass = new THREE.Mesh(
      new THREE.PlaneGeometry(1500, 1500),
      new THREE.MeshStandardMaterial({ map: tex, roughness: 1.0, metalness: 0 }),
    );
    grass.rotation.x = -Math.PI / 2;
    grass.position.y = -0.01;
    grass.receiveShadow = true;
    this.worldRoot.add(grass);
    // City roads + scenery houses. Streets come first so the
    // building placement pass can avoid sitting on top of them.
    this.addCityStreets();
    // Pedestrian zebra crossings at every intersection — drawn on
    // top of the spots where one street's pavement currently
    // crosses the other street's road (which would otherwise
    // render as a confusing beige-on-asphalt patch). Placed
    // BEFORE scenery so any scenery overlap rule based on
    // visible elements is computed against the painted layer.
    this.addPedestrianCrossings();
    this.addCityScenery();
    // Pavement lamp posts run along every avenue, on BOTH pavements,
    // symmetrically spaced so the city reads as a planned grid at
    // night. Lights themselves are a small pool that follows the
    // camera — see updateStreetLamps for how the pool gets routed.
    this.addStreetLamps();
    // Street planters — concrete boxes / cylinders / troughs scattered
    // along the same pavements, slotted BETWEEN lamps. Three styles
    // cycled so the pavement reads as a curated little garden strip
    // instead of an empty grey slab.
    this.addStreetPlanters();
  }

  /** City avenue grid laid out so every plot row lines a street:
   *   - east-west at z=-36  (north service — z=-48 plots line it from north)
   *   - east-west at z=+13.5 (legacy main — player's restaurant lines it)
   *   - east-west at z=+62  (south service — z=+48 plots line it from north)
   *   - north-south at x=-70 (outer-ring west boundary)
   *   - north-south at x=+70 (outer-ring east boundary)
   * Three east-west "boulevards" feed the three plot rows; two
   * outer-ring N-S avenues bracket the city. Scenery houses line
   * the remaining street frontage on both sides of every avenue
   * (see addCityScenery — street-following placement, not a random
   * grid scan). */
  private addCityStreets(): void {
    // THREE EW avenues, one DIRECTLY in front of each plot row.
    // Plot rows centered at z=-48, 0, +48; plot height 12 m so the
    // south wall of each row sits at row_z + 6.  Pavement outer
    // edge is 8 m from the avenue centerline (5 m strip starting at
    // perp ±3), so placing the avenue at `row_z + 6 + 8 = row_z + 14`
    // makes the north pavement edge land EXACTLY on the plot's
    // south wall — restaurants step straight onto the curb instead
    // of crossing a 24 m grass strip first.
    //
    //   z=-34 — front curb of row 1 (north pavement at z=-42 = row1 south wall)
    //   z=+14 — front curb of row 2 (north pavement at z=+6  = row2 south wall)
    //   z=+62 — front curb of row 3 (north pavement at z=+54 = row3 south wall)
    //
    // Scenery is auto-suppressed on the NORTH side of each avenue
    // by the overlapsClaim check (it would land inside the plot
    // there), and placed normally on the SOUTH side facing the
    // next row's gardens.
    this.makeCityAvenue("ew", -34);
    this.makeCityAvenue("ew",  14);
    this.makeCityAvenue("ew",  62);
    // NS outer ring — keeps the city visually bounded on the east /
    // west edges and gives the corner / NS-aligned scenery houses
    // a road to follow.
    this.makeCityAvenue("ns", -70);
    this.makeCityAvenue("ns",  70);
  }

  /** Centerlines of every avenue in the city — kept in one place so
   * the scenery street-follow loop, the exclusion check, and any
   * future road-aware system (pedestrian spawner, sign placement)
   * all agree on what counts as a street. Public so PedestrianSpawner
   * can route walkers down them. */
  static readonly EW_AVENUES: readonly number[] = [-34, 14, 62];
  static readonly NS_AVENUES: readonly number[] = [-70, 70];
  /** Half-length of each avenue strip the pedestrian spawner is
   * allowed to walk on. The avenues themselves are drawn 260 m long
   * but the visible city only fills ±110 m before fog takes over —
   * walking outside that range just burns CPU on invisible peds. */
  static readonly AVENUE_WALK_HALF_LEN = 110;

  /** Static reference layout of the 12 seeded plots — kept in one
   * place so the scenery loop, the avenue exclusion check, and the
   * garden generator all agree on where the plots sit. The 13th
   * entry is the legacy player block (centered on the origin) +
   * its existing east-side garden, which is added separately. */
  private static readonly CITY_PLOTS: { x: number; z: number; w: number; h: number }[] = [
    { x: -48, z: -48, w: 8,  h: 8  }, { x: -24, z: -48, w: 10, h: 10 },
    { x:   0, z: -48, w: 12, h: 12 }, { x:  24, z: -48, w: 10, h: 10 },
    { x:  48, z: -48, w: 8,  h: 8  },
    { x: -48, z:   0, w: 10, h: 10 }, { x: -24, z:   0, w: 12, h: 12 },
    { x:  24, z:   0, w: 12, h: 12 }, { x:  48, z:   0, w: 10, h: 10 },
    { x: -24, z:  48, w: 8,  h: 8  }, { x:   0, z:  48, w: 10, h: 10 },
    { x:  24, z:  48, w: 8,  h: 8  },
  ];

  /** Compute the garden bounds for a plot. By default, the garden
   * sits to the EAST of the building (mirrors the legacy player's
   * fenced garden). For plots where the east-side garden would
   * overlap an avenue, the garden is moved to the WEST instead.
   * Garden footprint is 8×10 m (matches the legacy GARDEN_BOUNDS). */
  private static gardenBoundsForPlot(p: { x: number; z: number; w: number; h: number }): { minX: number; maxX: number; minZ: number; maxZ: number } {
    const GW = 8, GD = 10;
    const GAP = 1;
    // Default: east of the building.
    let minX = p.x + p.w / 2 + GAP;
    let maxX = minX + GW;
    let minZ = p.z - GD / 2;
    let maxZ = p.z + GD / 2;
    // If the east garden would hit the east outer-ring avenue (x=+70 ±5.5)
    // OR overlap the next plot to the east, mirror to the west side.
    const eastAvenueZone = minX < 75.5 && maxX > 64.5;
    if (eastAvenueZone) {
      const wMinX = p.x - p.w / 2 - GAP - GW;
      const wMaxX = p.x - p.w / 2 - GAP;
      minX = wMinX;
      maxX = wMaxX;
    }
    return { minX, maxX, minZ, maxZ };
  }

  /** Build one full asphalt avenue at the given axis offset. The
   * avenue runs the full 260 m city span and includes pavements +
   * curbs + lane dashes, matching the legacy main road. `orientation`
   * = "ew" means the strip runs along X (constant Z); "ns" means
   * along Z (constant X). */
  private makeCityAvenue(orientation: "ew" | "ns", offset: number, length = 260, alongCenter = 0): void {
    const STRIP_LEN = length;
    const pavementMat = new THREE.MeshStandardMaterial({ color: 0xb2a692, roughness: 0.9 });
    const roadMat = new THREE.MeshStandardMaterial({ color: 0x3a3a3c, roughness: 0.95 });
    const curbMat = new THREE.MeshStandardMaterial({ color: 0x807468, roughness: 0.9 });
    const laneMat = new THREE.MeshStandardMaterial({
      color: 0xe6e0c4, roughness: 0.85,
      emissive: 0xe6e0c4, emissiveIntensity: 0.05,
    });
    // Z layout when orientation === "ew" (X axis when "ns"):
    //   inner pavements at ±4.5 from the centre line
    //   curbs at ±3
    //   asphalt road spans ±3 (6 m wide, same as legacy)
    // For "ns" we just swap which axis is which. alongCenter shifts
    // the strip along its long axis so shorter avenues (e.g. a
    // middle-block NS link) can sit between two plot clusters.
    const place = (mesh: THREE.Mesh, perp: number): void => {
      mesh.rotation.x = -Math.PI / 2;
      if (orientation === "ew") mesh.position.set(alongCenter, 0, offset + perp);
      else                       mesh.position.set(offset + perp, 0, alongCenter);
    };

    const makePavement = (perp: number): void => {
      const geo = orientation === "ew"
        ? new THREE.PlaneGeometry(STRIP_LEN, 5)
        : new THREE.PlaneGeometry(5, STRIP_LEN);
      const p = new THREE.Mesh(geo, pavementMat);
      place(p, perp);
      p.receiveShadow = true;
      this.worldRoot.add(p);
    };
    const makeCurb = (perp: number): void => {
      const geo = orientation === "ew"
        ? new THREE.BoxGeometry(STRIP_LEN, 0.12, 0.18)
        : new THREE.BoxGeometry(0.18, 0.12, STRIP_LEN);
      const c = new THREE.Mesh(geo, curbMat);
      if (orientation === "ew") c.position.set(alongCenter, 0.06, offset + perp);
      else                       c.position.set(offset + perp, 0.06, alongCenter);
      c.castShadow = true;
      c.receiveShadow = true;
      this.worldRoot.add(c);
    };

    makePavement(-5.5);
    makeCurb(-3);
    const roadGeo = orientation === "ew"
      ? new THREE.PlaneGeometry(STRIP_LEN, 6)
      : new THREE.PlaneGeometry(6, STRIP_LEN);
    const road = new THREE.Mesh(roadGeo, roadMat);
    place(road, 0);
    road.receiveShadow = true;
    this.worldRoot.add(road);
    makeCurb(3);
    makePavement(5.5);

    // Lane dashes down the middle — t walks along the strip's free
    // axis, shifted by alongCenter so a short segment's dashes are
    // anchored at the same centre as its road / pavements.
    for (let t = -STRIP_LEN / 2 + 2; t <= STRIP_LEN / 2 - 2; t += 4) {
      const dashGeo = orientation === "ew"
        ? new THREE.PlaneGeometry(1.2, 0.18)
        : new THREE.PlaneGeometry(0.18, 1.2);
      const dash = new THREE.Mesh(dashGeo, laneMat);
      dash.rotation.x = -Math.PI / 2;
      if (orientation === "ew") dash.position.set(alongCenter + t, 0.005, offset);
      else                       dash.position.set(offset, 0.005, alongCenter + t);
      this.worldRoot.add(dash);
    }
  }

  /** Paint zebra-stripe pedestrian crossings at every EW × NS
   * intersection AND cover the beige pavement-crossing patches
   * underneath with asphalt-colored rectangles so the road reads
   * uniformly dark through the intersection.
   *
   * Without the asphalt patches the pavement plane is still
   * visible BETWEEN the white stripes (the pavement plane runs
   * continuously across the road in 5-m wide strips). Layer order
   * is: pavement (y=0) → asphalt patch (y=0.005, covers the beige)
   * → zebra stripes (y=0.025, the only paint that should show).
   *
   * Geometry strategy: ALL of it goes into two merged
   * BufferGeometries (one for asphalt patches, one for stripes),
   * each rendered as a single Mesh → two draw calls total for
   * every crosswalk in the city. */
  private addPedestrianCrossings(): void {
    const STRIPE_COUNT = 5;
    const STRIPE_WIDTH = 0.50;   // dimension PERPENDICULAR to walking direction
    const STRIPE_LENGTH = 4.6;   // dimension PARALLEL to walking direction (<5m pavement depth so it doesn't bleed into grass)
    const STRIPE_PITCH = 1.00;   // center-to-center spacing between stripes
    // Lift the stripes a hair above the road plane so they don't
    // z-fight with the asphalt below. 0.025 is below the curb top
    // (0.12), so the stripes still tuck under the visible curbing.
    const STRIPE_Y = 0.025;
    // Asphalt-patch layer — sits ABOVE the pavement plane (y=0)
    // so the beige doesn't bleed through, but BELOW the stripes
    // (y=0.025) so the white paint reads on top.
    const ASPHALT_Y = 0.005;
    // Dimensions of each arm's pavement-on-road patch. The patch
    // sits where EW pavement (5 m wide perp to EW road) crosses
    // the NS road (6 m wide along Z), and vice versa.
    const PATCH_ROAD_WIDTH = 6;     // road width at the crossing
    const PATCH_PAVEMENT_WIDTH = 5; // pavement strip width

    const asphaltGeos: THREE.BufferGeometry[] = [];
    const stripeGeos: THREE.BufferGeometry[] = [];

    /** Build STRIPE_COUNT stripe geometries for one arm of one
     * intersection. `stripeAxis` says which axis the stripe is LONG
     * along — that's parallel to the road being crossed.
     *
     * Centered at (centerX, centerZ): for the north arm of an
     * intersection, that's (ax, az + 5.5); the stripes are then
     * laid out symmetrically across the road they're painted on. */
    const buildArmStripes = (
      centerX: number, centerZ: number,
      stripeAxis: "alongX" | "alongZ",
    ): void => {
      for (let i = 0; i < STRIPE_COUNT; i += 1) {
        const offset = (i - (STRIPE_COUNT - 1) / 2) * STRIPE_PITCH;
        let geo: THREE.PlaneGeometry;
        let px: number, pz: number;
        if (stripeAxis === "alongX") {
          // Stripe long in X (parallel to EW road), thin in Z.
          // East/west arm of an EW×NS intersection.
          geo = new THREE.PlaneGeometry(STRIPE_LENGTH, STRIPE_WIDTH);
          px = centerX;
          pz = centerZ + offset;
        } else {
          // Stripe long in Z (parallel to NS road), thin in X.
          // North/south arm of an EW×NS intersection.
          geo = new THREE.PlaneGeometry(STRIPE_WIDTH, STRIPE_LENGTH);
          px = centerX + offset;
          pz = centerZ;
        }
        geo.rotateX(-Math.PI / 2);           // lay flat on XZ plane
        geo.translate(px, STRIPE_Y, pz);     // anchor in world coords
        stripeGeos.push(geo);
      }
    };

    /** Build the asphalt patch that covers ONE arm's beige pavement-
     * on-road rectangle. `armAxis` says which orientation the patch
     * has: "northSouth" arms (EW pavement crossing NS road) are 6 m
     * wide × 5 m deep; "eastWest" arms (NS pavement crossing EW
     * road) are 5 m wide × 6 m deep. */
    const buildArmPatch = (
      centerX: number, centerZ: number,
      armAxis: "northSouth" | "eastWest",
    ): void => {
      const w = armAxis === "northSouth" ? PATCH_ROAD_WIDTH : PATCH_PAVEMENT_WIDTH;
      const d = armAxis === "northSouth" ? PATCH_PAVEMENT_WIDTH : PATCH_ROAD_WIDTH;
      const geo = new THREE.PlaneGeometry(w, d);
      geo.rotateX(-Math.PI / 2);
      geo.translate(centerX, ASPHALT_Y, centerZ);
      asphaltGeos.push(geo);
    };

    for (const az of WorldScene.EW_AVENUES) {
      for (const ax of WorldScene.NS_AVENUES) {
        // The four arms of this intersection. Pavement-center perp
        // offset from each avenue's centerline is 5.5 m (matches the
        // pavement plane in makeCityAvenue).
        //
        // First: asphalt patch covers the beige pavement-on-road
        // rectangle at this arm. Then: zebra stripes on top.
        //
        // North/south arms: EW pavement crosses NS road. Patch is
        // 6 m wide (NS road) × 5 m deep (EW pavement strip).
        // Pedestrians cross east-west, so stripes are parallel to
        // the NS road (long in Z).
        buildArmPatch(ax, az + 5.5, "northSouth");
        buildArmStripes(ax, az + 5.5, "alongZ");
        buildArmPatch(ax, az - 5.5, "northSouth");
        buildArmStripes(ax, az - 5.5, "alongZ");
        // East/west arms: NS pavement crosses EW road. Patch is
        // 5 m wide (NS pavement strip) × 6 m deep (EW road).
        // Pedestrians cross north-south, so stripes are parallel
        // to the EW road (long in X).
        buildArmPatch(ax + 5.5, az, "eastWest");
        buildArmStripes(ax + 5.5, az, "alongX");
        buildArmPatch(ax - 5.5, az, "eastWest");
        buildArmStripes(ax - 5.5, az, "alongX");
      }
    }

    // === Layer 1 — asphalt patches that mask the beige pavement ===
    if (asphaltGeos.length > 0) {
      const merged = mergeBufferGeometries(asphaltGeos, false);
      for (const g of asphaltGeos) g.dispose();
      if (merged) {
        // Same dark grey + roughness as the road plane in
        // makeCityAvenue so the patch blends seamlessly. Lit
        // material (MeshStandardMaterial) so the patch responds
        // to sun + ambient identically to the surrounding asphalt.
        const asphaltMat = new THREE.MeshStandardMaterial({
          color: 0x3a3a3c, roughness: 0.95, metalness: 0,
        });
        const asphaltMesh = new THREE.Mesh(merged, asphaltMat);
        asphaltMesh.receiveShadow = true;
        this.worldRoot.add(asphaltMesh);
      } else {
        console.warn("[WorldScene] failed to merge intersection asphalt patches");
      }
    }

    // === Layer 2 — zebra stripes painted on top ===
    if (stripeGeos.length === 0) return;
    const stripeMerged = mergeBufferGeometries(stripeGeos, false);
    for (const g of stripeGeos) g.dispose();
    if (!stripeMerged) {
      console.warn("[WorldScene] failed to merge pedestrian-crossing stripes");
      return;
    }
    // MeshBasicMaterial — crosswalk paint is unlit (it'd look weird
    // for stripes to shade based on the sun angle). Slight off-white
    // so they read as weathered paint instead of clinical pure white.
    const stripeMat = new THREE.MeshBasicMaterial({ color: 0xf2ead6 });
    const stripeMesh = new THREE.Mesh(stripeMerged, stripeMat);
    stripeMesh.receiveShadow = false;
    // High render order so the stripes draw on top of everything
    // beneath (asphalt patch + pavement + road plane) without
    // z-fighting on the shared y-axis.
    stripeMesh.renderOrder = 1;
    this.worldRoot.add(stripeMesh);
  }

  /** Procedural NPC scenery — ~60 background houses + shops
   * filling the empty grass between the 12 claim plots. Each
   * piece is a small Greek-Island cube with the same vocabulary
   * as the claim-plot shells (whitewashed walls, terra-cotta or
   * blue-domed roofs, a coloured door) but smaller, randomised
   * colours, and never on a street.
   *
   * Stable pseudo-RNG (mulberry32 seeded from a fixed constant)
   * so the city layout is identical across reloads — players can
   * use neighbouring houses as landmarks. */
  private addCityScenery(): void {
    const sceneryGroup = new THREE.Group();
    this.worldRoot.add(sceneryGroup);

    // Plots + garden zones the scenery must NOT overlap. Same data
    // the populateCityBuildings + fence code uses, so the keep-outs
    // exactly match what the player will see on the ground.
    const claimPlots: { x: number; z: number; w: number; h: number }[] = WorldScene.CITY_PLOTS.slice();
    const gardenZones = claimPlots.map((p) => WorldScene.gardenBoundsForPlot(p));
    // Legacy player block keep-out — covers restaurant + east-side
    // garden. The old main avenue at z=+13.5 is gone (replaced by
    // the plot-driven grid z=-66, -24, +24, +66), so the keep-out
    // no longer needs to swallow a pavement strip. Tightened to
    // span only the restaurant footprint (~12×12 m centered on
    // origin) plus a 1 m buffer all around. This lets scenery
    // houses hug the new z=-24 and z=+24 roads' centerlines
    // without false overlap on the player's plot.
    claimPlots.push({ x: 0, z: 0, w: 14, h: 14 });

    const overlapsClaim = (x: number, z: number, size: number): boolean => {
      const halfS = size / 2 + 1.5;
      for (const c of claimPlots) {
        if (Math.abs(x - c.x) < (c.w / 2 + halfS) && Math.abs(z - c.z) < (c.h / 2 + halfS)) {
          return true;
        }
      }
      return false;
    };
    const overlapsGarden = (x: number, z: number, size: number): boolean => {
      const halfS = size / 2 + 0.8;
      for (const g of gardenZones) {
        if (x > g.minX - halfS && x < g.maxX + halfS &&
            z > g.minZ - halfS && z < g.maxZ + halfS) return true;
      }
      return false;
    };
    // Avenue keep-out — rejects house centres that would overlap
    // the pavement plane. Pavement extends ±5.5 m from each avenue
    // centerline; a house of width `size` has half-width `size/2`,
    // so a house whose CENTER is closer than 5.5 + size/2 would
    // partially overlap the pavement. The filter rejects that.
    //
    // OLD (buggy): buf = size/2 + 6.5 with a "safety margin" of 1 m
    // on top of the pavement half-width. That bug rejected EVERY
    // scenery house the EW/NS placement loops tried to create:
    // the loops position houses with their street-facing edge
    // exactly AT the curb (centre at PAVEMENT_HALF + size/2 from
    // the avenue), so distance == 5.5 + size/2, which was strictly
    // < 6.5 + size/2 → rejected. Result: every avenue stayed bare.
    //
    // FIXED: drop the unneeded safety margin so a house placed
    // flush against the curb (centre at exactly PAVEMENT_HALF +
    // size/2) passes the strict-less-than test by sitting at the
    // boundary. The size-aware placement above is the actual source
    // of truth for the position; this filter just stops candidates
    // that would land ON the asphalt.
    const onAvenue = (x: number, z: number, size: number): boolean => {
      const buf = size / 2 + 5.5;
      for (const az of WorldScene.EW_AVENUES) {
        if (Math.abs(z - az) < buf) return true;
      }
      for (const ax of WorldScene.NS_AVENUES) {
        if (Math.abs(x - ax) < buf) return true;
      }
      return false;
    };

    // Seeded RNG so the city layout is reproducible.
    const rng = mulberry32(0xC02FB157);
    const pick = <T,>(arr: readonly T[]): T => arr[Math.floor(rng() * arr.length)];

    // Paris palette — warm cream-limestone walls (subtle hue shifts so
    // the rue doesn't read as a uniform paint store), slate-grey
    // mansard roofs (3 variants for visual rhythm), dark wood doors.
    const wallColors = [0xe6d5b5, 0xddc9a8, 0xe8d8b8, 0xd6c2a0, 0xefe0c0, 0xddc9b0];
    const roofColors = [0x3a4048, 0x2e343c, 0x444a52, 0x363c44];
    const doorColors = [0x3a261a, 0x4a3020, 0x2a1a10, 0x5a4030, 0x301c0e];

    // Shop names — small fraction of buildings on the street get a
    // storefront sign. Mixed traditional Paris commerce vocabulary.
    const shopNames = [
      "Boulangerie", "Café", "Tabac", "Pâtisserie", "Épicerie",
      "Brasserie", "Fleuriste", "Librairie", "Pharmacie", "Charcuterie",
      "Fromagerie", "Boucherie", "Chocolaterie", "Bar",
    ];

    // ── Street-following placement ────────────────────────────────
    // Walk along each avenue and drop houses on BOTH sides, packed
    // tight against the pavement curb. Per-house setback is computed
    // from the house's own width so the street-facing edge always
    // sits exactly at the curb edge regardless of how wide the
    // randomly-rolled house is — see the size-aware perpendicular
    // offset inside the loop. Owner spec: "buildings must touch the
    // pavement, no grass strip between."
    //
    // Pavement extends ±PAVEMENT_HALF m from the avenue centerline.
    // House's street-facing edge sits at exactly PAVEMENT_HALF +
    // CURB_EPSILON (a hair off the curb so the polygon doesn't z-
    // fight with the pavement plane). House centre = PAVEMENT_HALF
    // + CURB_EPSILON + size/2 from centerline.
    const PAVEMENT_HALF = 5.5;
    const CURB_EPSILON = 0.05;
    const HOUSE_STEP = 7;          // dense spacing → houses fill every street
    const STREET_EXTENT = 130;     // walk ±this along each avenue
    let placed = 0;
    // Lifted cap from 180 → 300 to populate all three EW avenues +
    // both NS edges without running out of slots. Each scenery
    // house is ~15 meshes; 300 * 15 = ~4500 draw calls — within
    // the GPU budget on mid-range machines (was 320 originally,
    // 180 was a perf-reaction overshoot). 2026-08: trimmed to 255
    // (−15%, paired with the skip-roll bump below) as a gentle
    // first step toward a lighter map — revisit after profiling.
    const HARD_CAP = 255;

    // PHASE 1 — collect every house's PLAN as data (no geometry yet).
    // The merge pass at the end ingests the final, post-shop-conversion
    // list, so all randomness has to be locked in before we touch any
    // BufferGeometry. Without this split, the post-pass shop converter
    // would have to surgically remove a merged geometry segment from
    // the combined mesh, which is way more painful than just deferring
    // the merge.
    type HousePlan = {
      x: number; z: number; size: number; storeys: number; rotY: number;
      isShop: boolean; shopName?: string;
      wallColor: number; roofColor: number; doorColor: number;
    };
    const placedHouses: HousePlan[] = [];
    const placedShops: { x: number; z: number }[] = [];

    const tryPlace = (x: number, z: number, size: number, storeys: number, rotY: number, shopName?: string): boolean => {
      if (overlapsClaim(x, z, size)) return false;
      if (overlapsGarden(x, z, size)) return false;
      if (onAvenue(x, z, size)) return false;
      placedHouses.push({
        x, z, size, storeys, rotY,
        isShop: !!shopName, shopName,
        wallColor: pick(wallColors),
        roofColor: pick(roofColors),
        doorColor: pick(doorColors),
      });
      // Track footprint so the grass / wildflower / tree / rock scatter
      // passes know to skip this tile. halfSize = size/2 + small skin
      // so a tree right against a wall doesn't poke through the eaves.
      this.placedSceneryHouses.push({ x, z, halfSize: size / 2 + 0.4 });
      if (shopName) placedShops.push({ x, z });
      placed += 1;
      return true;
    };

    // House front (door + windows) sits on +Z by default. To face an
    // avenue you rotate so that the +Z local axis ends up pointing
    // toward the avenue's centreline.
    //
    // East-west avenues — north-side houses face south (rotY=0, +Z=+Z),
    // south-side houses face north (rotY=π, +Z→-Z).
    for (const az of WorldScene.EW_AVENUES) {
      for (const side of [-1, +1] as const) {
        const rotY = side > 0 ? Math.PI : 0;
        for (let x = -STREET_EXTENT; x <= STREET_EXTENT; x += HOUSE_STEP) {
          if (placed >= HARD_CAP) break;
          // Random skip so the row isn't a perfectly uniform wall.
          // 2026-08: bumped ~10% → ~22% gaps (with HARD_CAP 300→255)
          // to lighten the decorative-building load a notch.
          if (rng() < 0.22) continue;
          const size = 4 + Math.floor(rng() * 3); // 4..6 tiles wide
          const storeys = 1 + Math.floor(rng() * 2); // 1..2
          // Size-aware perpendicular offset: house's street-facing
          // edge sits exactly at the curb (PAVEMENT_HALF + small
          // epsilon to avoid z-fighting). NO perpendicular jitter
          // — that's what was leaving inconsistent grass strips.
          const baseZ = az + side * (PAVEMENT_HALF + CURB_EPSILON + size / 2);
          // Mild ALONG-street jitter only — keeps the row from
          // reading as a barcode without ever pulling a house off
          // the curb line.
          const jx = x + (rng() - 0.5) * 1.4;
          const isShop = rng() < 0.32;
          const shopName = isShop ? pick(shopNames) : undefined;
          tryPlace(jx, baseZ, size, storeys, rotY, shopName);
        }
      }
    }
    // North-south avenues — west-side houses (x < ax) should face
    // east (+X). Right-hand rule for Y-rotation: rotY=+π/2 rotates
    // +Z (default front) → +X. Mirror for east side.
    for (const ax of WorldScene.NS_AVENUES) {
      for (const side of [-1, +1] as const) {
        // side > 0 = EAST of avenue → face WEST → rotate +Z to -X → rotY=-π/2
        // side < 0 = WEST of avenue → face EAST → rotate +Z to +X → rotY=+π/2
        const rotY = side > 0 ? -Math.PI / 2 : Math.PI / 2;
        for (let z = -STREET_EXTENT; z <= STREET_EXTENT; z += HOUSE_STEP) {
          if (placed >= HARD_CAP) break;
          if (rng() < 0.22) continue; // matches the EW loop's lighter density
          const size = 4 + Math.floor(rng() * 3);
          const storeys = 1 + Math.floor(rng() * 2);
          // Same size-aware perpendicular offset as the EW loop —
          // street-facing edge of the house sits exactly at the
          // curb regardless of rolled house width.
          const baseX = ax + side * (PAVEMENT_HALF + CURB_EPSILON + size / 2);
          const jz = z + (rng() - 0.5) * 1.4;
          const isShop = rng() < 0.32;
          const shopName = isShop ? pick(shopNames) : undefined;
          tryPlace(baseX, jz, size, storeys, rotY, shopName);
        }
      }
    }

    // ── PHASE 2 — post-pass: ensure every plot has a shop nearby ───
    // For each claim plot (skipping the legacy player block), pick the
    // nearest scenery house within 30 m. If none of the houses in
    // that radius are shops, convert the closest house's PLAN to one.
    // Mutation happens BEFORE geometry construction so we don't have
    // to surgically remove anything from a merged BufferGeometry.
    const SHOP_NEAR_RADIUS = 30;
    for (const plot of WorldScene.CITY_PLOTS) {
      let hasShop = false;
      let closest: { house: HousePlan; dSq: number } | undefined;
      for (const h of placedHouses) {
        const dx = h.x - plot.x, dz = h.z - plot.z;
        const dSq = dx * dx + dz * dz;
        if (dSq > SHOP_NEAR_RADIUS * SHOP_NEAR_RADIUS) continue;
        if (h.isShop) { hasShop = true; break; }
        if (!closest || dSq < closest.dSq) closest = { house: h, dSq };
      }
      if (hasShop || !closest) continue;
      // Re-derive a rotation that still faces the nearest avenue so
      // the shop's sign points at the street, not into the plot.
      let bestAv = WorldScene.EW_AVENUES[0];
      let bestAxis: "ew" | "ns" = "ew";
      let bestDist = Infinity;
      for (const az of WorldScene.EW_AVENUES) {
        if (Math.abs(closest.house.z - az) < bestDist) { bestDist = Math.abs(closest.house.z - az); bestAv = az; bestAxis = "ew"; }
      }
      for (const ax of WorldScene.NS_AVENUES) {
        if (Math.abs(closest.house.x - ax) < bestDist) { bestDist = Math.abs(closest.house.x - ax); bestAv = ax; bestAxis = "ns"; }
      }
      let rotY = 0;
      if (bestAxis === "ew") {
        rotY = closest.house.z > bestAv ? Math.PI : 0;
      } else {
        rotY = closest.house.x > bestAv ? -Math.PI / 2 : Math.PI / 2;
      }
      // Mutate the plan in place. The geometry-builder below sees the
      // updated values.
      closest.house.isShop = true;
      closest.house.shopName = pick(shopNames);
      closest.house.size = 5;
      closest.house.storeys = 2;
      closest.house.rotY = rotY;
      placedShops.push({ x: closest.house.x, z: closest.house.z });
    }

    // ── PHASE 3 — build geometries from plans into per-part buckets ─
    // Each house gets ~10 boxes. With ~300 houses that's ~3000 draw
    // calls if rendered individually — way over budget. Instead we
    // collect every box into a bucket keyed by part type, bake per-
    // house colours into vertex-colour attributes where the colour
    // varies (walls, mansard, door), then merge each bucket into ONE
    // BufferGeometry at the end and draw it with a single Phong mesh.
    //
    // Phong instead of Standard: scenery houses are mostly matte and
    // far from the camera. The difference between Standard's PBR and
    // Phong's classic Blinn-Phong is invisible at iso distance and
    // Phong is ~30 % cheaper per fragment. With hundreds of houses
    // drawn every frame, that's a real win.
    type GeoBucket = { geos: THREE.BufferGeometry[]; usesVertexColor: boolean };
    const mkBucket = (vc: boolean): GeoBucket => ({ geos: [], usesVertexColor: vc });
    const wallsBucket    = mkBucket(true);   // per-house wallColor
    const corniceBucket  = mkBucket(false);  // fixed warm cream
    const windowsBucket  = mkBucket(false);  // fixed slate blue
    const balconyBucket  = mkBucket(false);  // fixed iron black
    const mansardBucket  = mkBucket(true);   // per-house roofColor
    const capBucket      = mkBucket(false);  // fixed slate
    const chimneyBucket  = mkBucket(false);  // fixed terracotta
    const doorsBucket    = mkBucket(true);   // per-house doorColor
    const shopFrameBucket = mkBucket(false); // fixed black

    // Per-house geometry builder. Replicates the layout of the old
    // makeSceneryHouse function but writes into buckets instead of
    // appending children to a Group.
    const pushBox = (
      bucket: GeoBucket, w: number, h: number, d: number,
      px: number, py: number, pz: number,
      hx: number, hy: number, hz: number, rotY: number,
      color?: number,
    ): void => {
      const g = new THREE.BoxGeometry(w, h, d);
      // Local position inside the house frame.
      g.translate(px, py, pz);
      // Apply per-house rotY around origin, then translate to world.
      g.rotateY(rotY);
      g.translate(hx, hy, hz);
      if (bucket.usesVertexColor && color !== undefined) {
        const c = new THREE.Color(color);
        const count = g.attributes.position.count;
        const arr = new Float32Array(count * 3);
        for (let i = 0; i < count; i += 1) {
          arr[i * 3] = c.r; arr[i * 3 + 1] = c.g; arr[i * 3 + 2] = c.b;
        }
        g.setAttribute("color", new THREE.BufferAttribute(arr, 3));
      }
      bucket.geos.push(g);
    };

    // Shop signs have UNIQUE textures (one canvas-painted texture per
    // shop name) so they can't be merged into a single material. Keep
    // them as individual meshes; with ~30 % of houses being shops ×
    // ~300 houses, that's ~100 shop sign meshes — a tiny fraction of
    // the ~3000 we just collapsed.
    for (const plan of placedHouses) {
      const { x, z, size, storeys, rotY, wallColor, roofColor, doorColor, isShop, shopName } = plan;
      const wallH = storeys * 2.8;
      // Walls — single big box, per-house wallColor via vertex colour.
      pushBox(wallsBucket, size, wallH, size,
        0, wallH / 2, 0,
        x, 0, z, rotY, wallColor);
      // Horizontal cornices between floors — shared cream.
      for (let s = 1; s < storeys; s += 1) {
        pushBox(corniceBucket, size + 0.15, 0.12, size + 0.15,
          0, s * 2.8, 0,
          x, 0, z, rotY);
      }
      // Tall narrow windows on the front (+Z) face.
      const numWin = Math.max(2, Math.floor(size / 1.4));
      const winSpacing = size / (numWin + 1);
      for (let s = 0; s < storeys; s += 1) {
        const winY = s * 2.8 + 1.55;
        for (let i = 0; i < numWin; i += 1) {
          const wx = -size / 2 + (i + 1) * winSpacing;
          // Skip the centre window on the ground floor — door goes there.
          if (s === 0 && Math.abs(wx) < 0.5) continue;
          pushBox(windowsBucket, 0.55, 1.25, 0.04,
            wx, winY, size / 2 + 0.025,
            x, 0, z, rotY);
        }
        // Thin iron balcony rail on upper floors.
        if (s > 0) {
          pushBox(balconyBucket, size - 0.2, 0.05, 0.08,
            0, winY - 0.7, size / 2 + 0.10,
            x, 0, z, rotY);
        }
      }
      // Top cornice band (thin warm cream sitting at the wall top).
      pushBox(corniceBucket, size + 0.25, 0.12, size + 0.25,
        0, wallH + 0.06, 0,
        x, 0, z, rotY);
      // Mansard roof body — per-house roofColor.
      pushBox(mansardBucket, size + 0.15, 0.7, size + 0.15,
        0, wallH + 0.12 + 0.35, 0,
        x, 0, z, rotY, roofColor);
      // Flat slate cap on top.
      pushBox(capBucket, size - 0.4, 0.08, size - 0.4,
        0, wallH + 0.12 + 0.7 + 0.04, 0,
        x, 0, z, rotY);
      // Chimney.
      pushBox(chimneyBucket, 0.32, 0.8, 0.32,
        size / 2 - 0.6, wallH + 0.12 + 0.7 + 0.4, -size / 2 + 0.6,
        x, 0, z, rotY);
      // Door — per-house doorColor.
      pushBox(doorsBucket, 0.85, 1.95, 0.05,
        0, 0.975, size / 2 + 0.03,
        x, 0, z, rotY, doorColor);
      // Shop sign + iron frame.
      if (isShop && shopName) {
        // Off-thread paint: worker draws the sign canvas in parallel
        // and the texture's `image` populates a frame or two later.
        // If the worker isn't available the service synchronously
        // calls the fallback painter and we get the same texture
        // we always had (just with the original main-thread cost).
        const tex = this.textureService.paintShopSign(
          shopName,
          () => WorldScene.makeShopSignTexture(shopName),
        );
        // Phong with emissiveMap so the sign reads at night.
        const signMat = new THREE.MeshPhongMaterial({
          map: tex, shininess: 8,
          emissive: 0x222018, emissiveMap: tex, emissiveIntensity: 0.18,
        });
        const sign = new THREE.Mesh(
          new THREE.PlaneGeometry(Math.min(size - 0.4, 2.4), 0.55),
          signMat,
        );
        // Apply local position then rotY then world position. Wrap in
        // a small Group so the sign rotates with the house.
        sign.position.set(0, 2.25, size / 2 + 0.04);
        const signWrap = new THREE.Group();
        signWrap.add(sign);
        signWrap.position.set(x, 0, z);
        signWrap.rotation.y = rotY;
        sceneryGroup.add(signWrap);
        // Iron frame under the sign — fixed black, can merge.
        pushBox(shopFrameBucket, Math.min(size - 0.3, 2.5), 0.06, 0.04,
          0, 2.55, size / 2 + 0.05,
          x, 0, z, rotY);
      }
    }

    // ── PHASE 4 — merge each bucket and add as a single mesh ───────
    // Shared Phong materials (one per fixed-colour part). Lit, low-
    // shininess so they read as matte stone / wood / iron.
    const wallsMat = new THREE.MeshPhongMaterial({
      vertexColors: true, shininess: 5, specular: 0x111111,
    });
    const corniceMat = new THREE.MeshPhongMaterial({
      color: 0xc8b888, shininess: 5, specular: 0x111111,
    });
    const windowsMat = new THREE.MeshPhongMaterial({
      color: 0x223040, shininess: 40, specular: 0x556677,
      emissive: 0x1a2538, emissiveIntensity: 0.05,
    });
    const balconyMat = new THREE.MeshPhongMaterial({
      color: 0x1a1a1a, shininess: 60, specular: 0x666666,
    });
    const mansardMat = new THREE.MeshPhongMaterial({
      vertexColors: true, shininess: 12, specular: 0x222222,
    });
    const capMat = new THREE.MeshPhongMaterial({
      color: 0x2a3038, shininess: 8, specular: 0x222222,
    });
    const chimneyMat = new THREE.MeshPhongMaterial({
      color: 0x9a6850, shininess: 3, specular: 0x111111,
    });
    const doorsMat = new THREE.MeshPhongMaterial({
      vertexColors: true, shininess: 8, specular: 0x111111,
    });
    const shopFrameMat = new THREE.MeshPhongMaterial({
      color: 0x1a1a1a, shininess: 40, specular: 0x555555,
    });

    const flushBucket = (bucket: GeoBucket, mat: THREE.Material, receive: boolean): void => {
      if (bucket.geos.length === 0) return;
      const merged = mergeBufferGeometries(bucket.geos, false);
      for (const g of bucket.geos) g.dispose();
      if (!merged) {
        console.warn("[WorldScene] failed to merge scenery house bucket");
        return;
      }
      const mesh = new THREE.Mesh(merged, mat);
      // Scenery houses never cast shadows: every scenery house sits at
      // z=±50+ along an avenue, outside the sun's ±20 m shadow frustum
      // around world origin. Casting was pure GPU waste — the result
      // was always clipped by the shadow camera.
      mesh.castShadow = false;
      mesh.receiveShadow = receive;
      sceneryGroup.add(mesh);
    };
    flushBucket(wallsBucket,    wallsMat,    true);
    flushBucket(corniceBucket,  corniceMat,  true);
    flushBucket(windowsBucket,  windowsMat,  false);
    flushBucket(balconyBucket,  balconyMat,  false);
    flushBucket(mansardBucket,  mansardMat,  true);
    flushBucket(capBucket,      capMat,      true);
    flushBucket(chimneyBucket,  chimneyMat,  true);
    flushBucket(doorsBucket,    doorsMat,    false);
    flushBucket(shopFrameBucket, shopFrameMat, false);
  }

  /** Canvas-painted storefront sign. Cream background with a thin
   * dark border + deep red script-style hand-painted name centered
   * inside. Compact enough to read at iso distance. */
  private static makeShopSignTexture(name: string): THREE.CanvasTexture {
    const w = 512, h = 128;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    // Background — warm cream, lightly aged.
    ctx.fillStyle = "#f4ead2";
    ctx.fillRect(0, 0, w, h);
    // Inner dark border for that traditional sign look.
    ctx.strokeStyle = "#3a261a";
    ctx.lineWidth = 5;
    ctx.strokeRect(8, 8, w - 16, h - 16);
    // Lettering — deep maroon, serif, slightly italic to read as
    // hand-painted commerce. Size scales down for long names so the
    // text never spills off the sign.
    const targetFs = name.length > 9 ? 56 : 72;
    ctx.fillStyle = "#5a2018";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `700 italic ${targetFs}px "Georgia", "Times New Roman", serif`;
    ctx.fillText(name, w / 2, h / 2 + 4);
    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    tex.anisotropy = 8;
    return tex;
  }

  /** Procedural grass texture — high-resolution layered noise in a
   * green palette so the lawn has natural-looking patches with fine
   * detail at the texture's tile size. */
  private static makeGrassTexture(): THREE.CanvasTexture {
    const sz = 512;
    const canvas = document.createElement("canvas");
    canvas.width = sz;
    canvas.height = sz;
    const ctx = canvas.getContext("2d")!;
    // Base fill — mid green.
    ctx.fillStyle = "#4f7836";
    ctx.fillRect(0, 0, sz, sz);
    // Layer 1: 60 large overlapping soft blobs (big colour patches).
    const bigPalette = [
      "rgba(95, 135, 70, 0.45)",    // lighter spring green
      "rgba(55, 92, 42, 0.40)",     // mid forest green
      "rgba(38, 72, 32, 0.40)",     // deep shadow green
      "rgba(120, 145, 60, 0.30)",   // dry yellow-grass
      "rgba(45, 88, 40, 0.30)",     // moss
    ];
    for (let i = 0; i < 60; i += 1) {
      const cx = Math.random() * sz;
      const cy = Math.random() * sz;
      const r = 40 + Math.random() * 90;
      const colour = bigPalette[Math.floor(Math.random() * bigPalette.length)];
      for (const dx of [-sz, 0, sz]) {
        for (const dy of [-sz, 0, sz]) {
          const grad = ctx.createRadialGradient(cx + dx, cy + dy, 0, cx + dx, cy + dy, r);
          grad.addColorStop(0, colour);
          grad.addColorStop(1, colour.replace(/0\.\d+\)$/, "0)"));
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, sz, sz);
        }
      }
    }
    // Layer 2: ~600 tiny dark and light flecks for fine-scale texture —
    // gives the lawn a "blades viewed from above" stippled feel instead
    // of one uniform gradient. Each fleck is a 1-3px filled circle.
    const fleckColours = [
      "rgba(32, 64, 28, 0.55)",
      "rgba(28, 56, 22, 0.55)",
      "rgba(110, 140, 70, 0.45)",
      "rgba(82, 115, 50, 0.45)",
      "rgba(140, 160, 75, 0.35)",
    ];
    for (let i = 0; i < 600; i += 1) {
      const cx = Math.random() * sz;
      const cy = Math.random() * sz;
      const r = 0.6 + Math.random() * 2.4;
      ctx.fillStyle = fleckColours[Math.floor(Math.random() * fleckColours.length)];
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }
    // Layer 3: 40 fine short dashes that suggest individual blades
    // viewed from above. Random orientation + length.
    ctx.lineCap = "round";
    for (let i = 0; i < 280; i += 1) {
      const cx = Math.random() * sz;
      const cy = Math.random() * sz;
      const len = 4 + Math.random() * 8;
      const angle = Math.random() * Math.PI * 2;
      ctx.strokeStyle = Math.random() < 0.5 ? "rgba(35, 70, 30, 0.55)" : "rgba(100, 135, 60, 0.45)";
      ctx.lineWidth = 0.8 + Math.random() * 0.6;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(angle) * len, cy + Math.sin(angle) * len);
      ctx.stroke();
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    tex.anisotropy = 8;
    return tex;
  }

  /** Instanced grass clumps — each instance carries a multi-blade
   * billboard quad cluster so the lawn reads as dense bushy turf
   * instead of lonely sticks. */
  private addGrassBlades(): void {
    const bladeTex = WorldScene.makeBladeTexture();
    const bladeMat = new THREE.MeshStandardMaterial({
      map: bladeTex, transparent: true, alphaTest: 0.25,
      side: THREE.DoubleSide, roughness: 0.95, metalness: 0,
    });
    // Each instance is a CROSS of 3 perpendicular planes (0°, 60°,
    // 120°) so the bush reads from any iso angle without obvious gaps.
    // Width bumped to 0.34 m so each clump covers more ground; height
    // 0.36 m keeps blades short enough that the camera path stays
    // readable.
    const w = 0.34, h = 0.36;
    const planes: THREE.PlaneGeometry[] = [];
    for (let i = 0; i < 3; i += 1) {
      const p = new THREE.PlaneGeometry(w, h);
      p.rotateY((i / 3) * Math.PI);
      planes.push(p);
    }
    const geom = mergeBufferGeometries(planes);
    geom.translate(0, h / 2, 0);
    // Big density bump + map-wide spread — 25000 instances scattered
    // across ±180m (360×360 = 130,000 m²) gives ~0.19 clumps/m². Sparse
    // enough that the GPU's happy with a single InstancedMesh draw,
    // dense enough that the player sees grass tufts anywhere they walk
    // (including the back streets and the empty corners of the city,
    // not just within 40m of their plot like before).
    const count = 25000;
    const blades = new THREE.InstancedMesh(geom, bladeMat, count);
    const tmp = new THREE.Object3D();
    let placed = 0;
    let attempts = 0;
    const maxAttempts = count * 4;
    while (placed < count && attempts < maxAttempts) {
      attempts += 1;
      const x = (Math.random() - 0.5) * 360;
      const z = (Math.random() - 0.5) * 360;
      if (this.isOnRoadOrBuildingForScatter(x, z, /* margin */ 0.4)) continue;
      tmp.position.set(x, 0, z);
      tmp.rotation.y = Math.random() * Math.PI * 2;
      // Per-instance scale jitter — some short, some tall, some wider.
      const sx = 0.75 + Math.random() * 0.55;
      const sy = 0.7 + Math.random() * 0.7;
      tmp.scale.set(sx, sy, sx);
      tmp.updateMatrix();
      blades.setMatrixAt(placed, tmp.matrix);
      placed += 1;
    }
    blades.count = placed;
    blades.castShadow = false;
    blades.receiveShadow = true;
    this.threeScene.add(blades);
  }

  /** Multi-blade billboard texture. Draws ~9 tapered grass blades
   * across the quad with varying heights and slight lean — each quad
   * already looks like a small bushy clump, so a single instance reads
   * as turf rather than one lonely stalk. */
  private static makeBladeTexture(): THREE.CanvasTexture {
    const w = 256, h = 256;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, w, h);
    // 11 blades distributed across the quad, each with its own height,
    // lean angle, and shade. Together they fill most of the quad with
    // visible grass silhouette.
    const palette = [
      ["rgba(36, 78, 30, 1.00)", "rgba(62, 112, 48, 1.00)", "rgba(110, 155, 70, 0.95)"],
      ["rgba(42, 88, 36, 1.00)", "rgba(72, 124, 56, 1.00)", "rgba(120, 165, 75, 0.95)"],
      ["rgba(30, 70, 28, 1.00)", "rgba(54, 100, 44, 1.00)", "rgba(95, 140, 60, 0.95)"],
      ["rgba(48, 95, 38, 1.00)", "rgba(80, 130, 60, 1.00)", "rgba(135, 175, 85, 0.95)"],
    ];
    const blades = 11;
    for (let i = 0; i < blades; i += 1) {
      const baseX = w * (0.05 + 0.90 * (i / (blades - 1)) + (Math.random() - 0.5) * 0.06);
      const top = h * (0.05 + Math.random() * 0.35);       // top of blade (lower = taller)
      const base = h;
      const baseHalf = 2 + Math.random() * 3;              // base width
      const tipOffset = (Math.random() - 0.5) * w * 0.10;  // horizontal lean
      const tipHalf = 0.5 + Math.random() * 1.0;
      const colours = palette[Math.floor(Math.random() * palette.length)];
      const grad = ctx.createLinearGradient(0, base, 0, top);
      grad.addColorStop(0,    colours[0]);
      grad.addColorStop(0.55, colours[1]);
      grad.addColorStop(1.0,  colours[2].replace(/0\.\d+\)$/, "0)"));
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(baseX - baseHalf, base);
      ctx.lineTo(baseX + baseHalf, base);
      ctx.lineTo(baseX + tipOffset + tipHalf, top);
      ctx.lineTo(baseX + tipOffset - tipHalf, top);
      ctx.closePath();
      ctx.fill();
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  }

  /** Sparse wildflowers — tiny coloured discs lying flat on the lawn.
   * Just a pop of colour, not a full bloom system. Spread map-wide
   * (±150m) so flowers show up even in the far corners of the city. */
  private addWildflowers(): void {
    const palette = [0xffe066, 0xff8aa6, 0xffffff, 0xf0b0e8, 0xffc46e];
    const flowers = new THREE.Group();
    let placed = 0;
    let attempts = 0;
    while (placed < 400 && attempts < 2000) {
      attempts += 1;
      const x = (Math.random() - 0.5) * 300;
      const z = (Math.random() - 0.5) * 300;
      if (this.isOnRoadOrBuildingForScatter(x, z, 0.6)) continue;
      const color = palette[Math.floor(Math.random() * palette.length)];
      const flower = new THREE.Mesh(
        new THREE.CircleGeometry(0.08 + Math.random() * 0.04, 8),
        new THREE.MeshStandardMaterial({ color, roughness: 0.6 }),
      );
      flower.rotation.x = -Math.PI / 2;
      flower.position.set(x, 0.02, z);
      flowers.add(flower);
      placed += 1;
    }
    this.threeScene.add(flowers);
  }

  /** Low-poly trees scattered map-wide — cone canopy on a cylinder
   * trunk. Used to be 22 trees in a 40m radius of the player plot;
   * now ~250 trees across ±180m so the back streets and city outskirts
   * read as parkland too. Performance: trees share one trunk geometry
   * + one canopy geometry per palette colour via InstancedMesh, so
   * the full forest is 4 draw calls (1 trunks + 3 canopies). */
  private addLawnTrees(): void {
    const trunkMat = new THREE.MeshStandardMaterial({ color: 0x5a3a22, roughness: 0.9 });
    const canopyMats = [
      new THREE.MeshStandardMaterial({ color: 0x3a7a3a, roughness: 0.85 }),
      new THREE.MeshStandardMaterial({ color: 0x4a8a4a, roughness: 0.85 }),
      new THREE.MeshStandardMaterial({ color: 0x2e6a2e, roughness: 0.85 }),
    ];

    // Roll positions first so we know the final count per canopy
    // palette before sizing the InstancedMeshes.
    type Plan = { x: number; z: number; rotY: number; trunkH: number; canopyH: number; canopyR: number; palette: number };
    const plans: Plan[] = [];
    let attempts = 0;
    const TARGET = 250;
    while (plans.length < TARGET && attempts < TARGET * 8) {
      attempts += 1;
      const x = (Math.random() - 0.5) * 360;
      const z = (Math.random() - 0.5) * 360;
      // Wider margin than grass — a tree canopy is ~1m across, so we
      // need a couple of metres of clearance to keep branches from
      // intersecting walls / cars / customers walking the pavement.
      if (this.isOnRoadOrBuildingForScatter(x, z, 1.8)) continue;
      // Keep the path between the player's front door and the legacy
      // main street clear so the camera always has a clean view of
      // the door from default zoom.
      if (Math.abs(x) < 6 && z > 5.5 && z < 12) continue;
      plans.push({
        x, z,
        rotY: Math.random() * Math.PI * 2,
        trunkH: 0.8 + Math.random() * 0.5,
        canopyH: 1.6 + Math.random() * 1.0,
        canopyR: 0.65 + Math.random() * 0.4,
        palette: Math.floor(Math.random() * canopyMats.length),
      });
    }

    // Shared geometries — sized for unit scale, per-instance matrix
    // applies the actual height + radius via non-uniform Y scale.
    const trunkGeo = new THREE.CylinderGeometry(0.10, 0.14, 1, 8);
    trunkGeo.translate(0, 0.5, 0); // base at y=0, top at y=1
    const canopyGeo = new THREE.ConeGeometry(1, 1, 8);
    canopyGeo.translate(0, 0.5, 0); // base at y=0, tip at y=1

    // One trunk InstancedMesh for everything — trunk colour is uniform.
    const trunks = new THREE.InstancedMesh(trunkGeo, trunkMat, plans.length);
    // Trees span ±180 m (way beyond the sun's ±20 m shadow frustum)
    // so the InstancedMesh's union bounding box drags ALL 250 trees
    // into the shadow render pass even though almost none of them
    // sit inside the frustum. Disabling castShadow keeps the trunks
    // out of the shadow pass entirely — they're decorative scenery,
    // a tiny shadow streak at iso angle is a fine trade for the GPU
    // time saved.
    trunks.castShadow = false;
    // One canopy InstancedMesh per palette colour. Build buckets first.
    const buckets: Plan[][] = canopyMats.map(() => []);
    plans.forEach((p) => buckets[p.palette].push(p));
    const tmp = new THREE.Object3D();
    plans.forEach((p, i) => {
      tmp.position.set(p.x, 0, p.z);
      tmp.rotation.set(0, p.rotY, 0);
      tmp.scale.set(1, p.trunkH, 1);
      tmp.updateMatrix();
      trunks.setMatrixAt(i, tmp.matrix);
    });
    this.threeScene.add(trunks);
    for (let pi = 0; pi < canopyMats.length; pi += 1) {
      const bucket = buckets[pi];
      if (bucket.length === 0) continue;
      const canopies = new THREE.InstancedMesh(canopyGeo, canopyMats[pi], bucket.length);
      // Same reasoning as trunks above — all 250 canopies span the
      // map and would otherwise enter the shadow pass for nothing.
      canopies.castShadow = false;
      bucket.forEach((p, i) => {
        // Canopy sits ON TOP of trunk with a slight overlap so trunk
        // disappears into the canopy base instead of poking through.
        tmp.position.set(p.x, p.trunkH - 0.15, p.z);
        tmp.rotation.set(0, p.rotY, 0);
        tmp.scale.set(p.canopyR, p.canopyH, p.canopyR);
        tmp.updateMatrix();
        canopies.setMatrixAt(i, tmp.matrix);
      });
      this.threeScene.add(canopies);
    }
  }

  /** Small instanced grey rocks scattered around to break up the
   * uniform green. Map-wide spread (±150m) to match the grass and
   * tree coverage. */
  private addRocks(): void {
    const rockGeo = new THREE.IcosahedronGeometry(0.18, 0);
    const rockMat = new THREE.MeshStandardMaterial({ color: 0x7e7672, roughness: 0.95 });
    const TARGET = 120;
    const rocks = new THREE.InstancedMesh(rockGeo, rockMat, TARGET);
    const tmp = new THREE.Object3D();
    let placed = 0;
    let attempts = 0;
    while (placed < TARGET && attempts < TARGET * 8) {
      attempts += 1;
      const x = (Math.random() - 0.5) * 300;
      const z = (Math.random() - 0.5) * 300;
      if (this.isOnRoadOrBuildingForScatter(x, z, 0.4)) continue;
      tmp.position.set(x, 0.06, z);
      tmp.rotation.set(Math.random() * 0.6, Math.random() * Math.PI * 2, Math.random() * 0.4);
      const sc = 0.6 + Math.random() * 0.9;
      tmp.scale.set(sc, sc * (0.5 + Math.random() * 0.4), sc);
      tmp.updateMatrix();
      rocks.setMatrixAt(placed, tmp.matrix);
      placed += 1;
    }
    rocks.count = placed;
    rocks.castShadow = true;
    rocks.receiveShadow = true;
    this.threeScene.add(rocks);
  }

  // @ts-expect-error — addPavementAndRoad kept on the class as a
  // reference implementation but no longer wired up (see addBuilding;
  // the world avenues handle the player's main street now).
  private addPavementAndRoad(): void {
    // The strip spans the full map width (lawn plane is 70×70 m, so
    // 80 m of strip overshoots into the grass margin and disappears
    // into the fog horizon without visible end caps). Z layout (north
    // to south):
    //   z = 5.5..10.5   near pavement (against the building's south wall)
    //   z = 10.5         near curb
    //   z = 10.5..16.5   road
    //   z = 16.5         far curb
    //   z = 16.5..21.5   far pavement (across the road)
    // Bumped 80 → 260 so the legacy main road actually spans the
    // expanded city instead of being abruptly chopped off east + west
    // of the player's plot.
    const STRIP_WIDTH = 260;
    const pavementMat = new THREE.MeshStandardMaterial({ color: 0xb2a692, roughness: 0.9 });
    const roadMat = new THREE.MeshStandardMaterial({ color: 0x3a3a3c, roughness: 0.95 });
    const curbMat = new THREE.MeshStandardMaterial({ color: 0x807468, roughness: 0.9 });
    const laneMat = new THREE.MeshStandardMaterial({
      color: 0xe6e0c4, roughness: 0.85,
      emissive: 0xe6e0c4, emissiveIntensity: 0.05,
    });

    const makePavement = (z: number): void => {
      const p = new THREE.Mesh(new THREE.PlaneGeometry(STRIP_WIDTH, 5), pavementMat);
      p.rotation.x = -Math.PI / 2;
      p.position.set(0, 0, z);
      p.receiveShadow = true;
      this.threeScene.add(p);
    };
    const makeCurb = (z: number): void => {
      const c = new THREE.Mesh(new THREE.BoxGeometry(STRIP_WIDTH, 0.12, 0.18), curbMat);
      c.position.set(0, 0.06, z);
      c.castShadow = true;
      c.receiveShadow = true;
      this.threeScene.add(c);
    };
    makePavement(8);              // near pavement centre, spans 5.5..10.5
    makeCurb(10.5);                // near curb
    const road = new THREE.Mesh(new THREE.PlaneGeometry(STRIP_WIDTH, 6), roadMat);
    road.rotation.x = -Math.PI / 2;
    road.position.set(0, 0, 13.5);  // spans 10.5..16.5
    road.receiveShadow = true;
    this.threeScene.add(road);
    makeCurb(16.5);                // far curb
    makePavement(19);              // far pavement, spans 16.5..21.5
    // Lane dashes down the middle of the road, spaced every 4 m
    // across the full road width.
    for (let x = -STRIP_WIDTH / 2 + 2; x <= STRIP_WIDTH / 2 - 2; x += 4) {
      const dash = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 0.18), laneMat);
      dash.rotation.x = -Math.PI / 2;
      dash.position.set(x, 0.005, 13.5);
      this.threeScene.add(dash);
    }
  }

  /** True when (x, z) sits on a road, pavement, plot building, garden,
   * or scenery house — the four scatter passes (grass blades,
   * wildflowers, trees, rocks) consult this so nothing spawns on
   * asphalt or inside a building. Margin is added outward from each
   * obstacle so e.g. a tree's 1m canopy doesn't poke through a wall.
   *
   * Replaces the old static isExclusionZone, which only knew about
   * the legacy player block + pavement strip and was useless once
   * the city grew to 12 plots + 5 avenues + ~300 scenery houses. */
  private isOnRoadOrBuildingForScatter(x: number, z: number, margin: number): boolean {
    // Avenue pavements — pavement strip extends PAVEMENT_HALF (5.5m)
    // out from each centerline on both sides. Reject anything within
    // (5.5 + margin) of any avenue line. Checking the 5 avenues first
    // (3 EW + 2 NS) culls the asphalt strips fastest.
    const PAVEMENT_HALF = 5.5;
    for (const az of WorldScene.EW_AVENUES) {
      if (Math.abs(z - az) < PAVEMENT_HALF + margin) return true;
    }
    for (const ax of WorldScene.NS_AVENUES) {
      if (Math.abs(x - ax) < PAVEMENT_HALF + margin) return true;
    }
    // City plots — exact building footprint + 1m + margin. Same data
    // the populateCityBuildings code uses, so the keep-out matches
    // what the player sees on the ground.
    for (const p of WorldScene.CITY_PLOTS) {
      if (Math.abs(x - p.x) < p.w / 2 + 1 + margin &&
          Math.abs(z - p.z) < p.h / 2 + 1 + margin) return true;
    }
    // Per-plot gardens (east-or-west of each plot).
    for (const p of WorldScene.CITY_PLOTS) {
      const g = WorldScene.gardenBoundsForPlot(p);
      if (x > g.minX - margin && x < g.maxX + margin &&
          z > g.minZ - margin && z < g.maxZ + margin) return true;
    }
    // Legacy player block (centered on origin, ~12×12 footprint) +
    // its east-side legacy garden.
    if (Math.abs(x) < 7 + margin && Math.abs(z) < 7 + margin) return true;
    const lg = WorldScene.GARDEN_BOUNDS;
    if (x > lg.minX - margin && x < lg.maxX + margin &&
        z > lg.minZ - margin && z < lg.maxZ + margin) return true;
    // Scenery houses placed by addCityScenery — must be last because
    // there can be ~300 of them, and most candidates are already culled
    // by the cheaper checks above. Each entry already includes a small
    // skin in halfSize, so a tight margin here is fine.
    for (const h of this.placedSceneryHouses) {
      if (Math.abs(x - h.x) < h.halfSize + margin &&
          Math.abs(z - h.z) < h.halfSize + margin) return true;
    }
    return false;
  }

  /** Wood-and-metal "back of house" counter where the errand helper
   * drops off groceries. Static — not registered, not sellable. */
  private buildSupplyCounter(): void {
    // Delegates to the shared interiorPieces.buildSupplyCounterMesh so
    // visit mode adds the same back-wall counter. stoveFurniturePos is
    // the canonical (0, -4) — the shared helper bakes that offset in.
    buildSupplyCounterMesh(this.threeScene);
  }

  // === Door plaque (restaurant name + rating stars below) ===

  /** The plaque face mesh — clickable target for the edit modal.
   * Engine raycasts against this to open the editor. */
  signPlaqueMesh?: THREE.Mesh;
  /** Canvas + texture used for the painted-on name. Re-drawn whenever
   * the player saves a new name or style. */
  private signCanvas?: HTMLCanvasElement;
  private signTexture?: THREE.CanvasTexture;
  private signFaceMat?: THREE.MeshStandardMaterial;
  /** Border frame mesh — recoloured per plaque-style choice. */
  private signFrameMesh?: THREE.Mesh;
  /** The 5 mounted star mini-meshes — each is "lit" (gold + emissive) or
   * "off" (slate). updateRatingSign sets which are lit based on the
   * current average rating. */
  private ratingStars: { mesh: THREE.Mesh; litMat: THREE.MeshStandardMaterial; offMat: THREE.MeshStandardMaterial }[] = [];

  /** Cached state passed in by Engine via setRestaurantSign; used so the
   * first applyDayNight tick has data even before the player edits. */
  private currentSignName = "Cozy Bistro";
  private currentSignStyle = { font: "serif", textColor: "cream", plaqueStyle: "dark" };

  private buildRatingSign(): void {
    // Frame: a slightly larger dark backplate that reads as the plaque
    // border. Coloured per the player's plaqueStyle in
    // applyRestaurantSign. Mounted just behind the face so it shows as
    // a thin trim around the painted name.
    const frameMat = new THREE.MeshStandardMaterial({ color: 0x2a1f17, roughness: 0.7 });
    const frame = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.70, 0.04), frameMat);
    frame.position.set(0, 2.55, 5.625);
    frame.castShadow = true;
    this.threeScene.add(frame);
    this.signFrameMesh = frame;
    // Face: the canvas texture lives here. Slightly proud of the frame
    // so the painted name appears to sit on top of the plaque.
    this.signCanvas = document.createElement("canvas");
    this.signCanvas.width = 768;
    this.signCanvas.height = 320;
    this.signTexture = new THREE.CanvasTexture(this.signCanvas);
    this.signTexture.minFilter = THREE.LinearFilter;
    this.signTexture.magFilter = THREE.LinearFilter;
    this.signFaceMat = new THREE.MeshStandardMaterial({
      map: this.signTexture,
      roughness: 0.65,
    });
    const face = new THREE.Mesh(new THREE.PlaneGeometry(1.36, 0.60), this.signFaceMat);
    face.position.set(0, 2.55, 5.65);
    face.castShadow = false;
    this.threeScene.add(face);
    this.signPlaqueMesh = face;
    // Initial paint with defaults so the plaque renders something even
    // before the player saves a new name.
    this.repaintSignCanvas();
    // 5 small rating stars BELOW the plaque on the lintel.
    const litMatTemplate = new THREE.MeshStandardMaterial({
      color: 0xf5c14a, roughness: 0.4, metalness: 0.4,
      emissive: 0xf5c14a, emissiveIntensity: 0.5,
    });
    const offMatTemplate = new THREE.MeshStandardMaterial({
      color: 0x474039, roughness: 0.85,
    });
    for (let i = 0; i < 5; i += 1) {
      const litMat = litMatTemplate.clone();
      const offMat = offMatTemplate.clone();
      const star = new THREE.Mesh(new THREE.CircleGeometry(0.05, 16), offMat);
      star.position.set(-0.24 + i * 0.12, 2.16, 5.66);
      this.threeScene.add(star);
      this.ratingStars.push({ mesh: star, litMat, offMat });
    }
  }

  /** Update the rating-sign lit-count from a 1-5 average. Called by
   * Engine on every HUD tick — cheap enough (5 ref swaps max). */
  updateRatingSign(rating: number): void {
    // A 4.3 rating lights 4 full stars + the 5th stays off (we treat
    // half-stars as off so the sign reads cleanly). Cap at 1..5.
    const litCount = Math.max(0, Math.min(5, Math.round(rating)));
    for (let i = 0; i < this.ratingStars.length; i += 1) {
      const s = this.ratingStars[i];
      const wantLit = i < litCount;
      const cur = s.mesh.material as THREE.Material;
      const want = wantLit ? s.litMat : s.offMat;
      if (cur !== want) s.mesh.material = want;
    }
  }

  /** Engine calls this when the player saves a new name / style. Caches
   * the state + re-renders the canvas texture + recolours the frame. */
  setRestaurantSign(name: string, style: { font: string; textColor: string; plaqueStyle: string }): void {
    this.currentSignName = name;
    this.currentSignStyle = { ...style };
    this.repaintSignCanvas();
    this.applyPlaqueFrameStyle();
  }

  /** Weekly-challenge champion badge on the plaque ("🏆×N" corner mark).
   * Engine pushes the count (from the leaderboard-derived champion tally)
   * at ~1 Hz; repaints only when it actually changes. */
  private championCount = 0;
  setChampionCount(n: number): void {
    if (n === this.championCount) return;
    this.championCount = n;
    this.repaintSignCanvas();
  }

  /** Re-draw the plaque's canvas with the current name + style and
   * push it to the texture. Cheap — runs only on save, not per frame. */
  private repaintSignCanvas(): void {
    if (!this.signCanvas || !this.signTexture) return;
    const ctx = this.signCanvas.getContext("2d");
    if (!ctx) return;
    const w = this.signCanvas.width;
    const h = this.signCanvas.height;
    // Background painted to match the plaque-style id so the texture
    // tint reads consistently with the frame around it.
    const bg = PLAQUE_BG[this.currentSignStyle.plaqueStyle] ?? PLAQUE_BG.dark;
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);
    // Thin inner border for a "framed plate" feel — same colour as the
    // text accent so the trim reads as gold-on-dark / etc.
    const accent = TEXT_COLORS[this.currentSignStyle.textColor] ?? TEXT_COLORS.cream;
    ctx.strokeStyle = accent;
    ctx.lineWidth = 6;
    ctx.strokeRect(20, 20, w - 40, h - 40);
    // Restaurant name. Auto-fit font size so a long name still reads.
    ctx.fillStyle = accent;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const fontFamily = FONT_FAMILIES[this.currentSignStyle.font] ?? FONT_FAMILIES.serif;
    const fontWeight = this.currentSignStyle.font === "display" ? "900" : "700";
    let size = 140;
    do {
      ctx.font = `${fontWeight} ${size}px ${fontFamily}`;
      if (ctx.measureText(this.currentSignName).width < w - 100) break;
      size -= 6;
    } while (size > 40);
    ctx.fillText(this.currentSignName, w / 2, h / 2 + 8);
    // 🏆 Weekly-champion badge — top-right corner, small enough to read
    // as a medal rather than shouting over the name.
    if (this.championCount > 0) {
      ctx.font = "700 64px system-ui, sans-serif";
      ctx.textAlign = "right";
      const label = this.championCount > 1 ? `🏆×${this.championCount}` : "🏆";
      ctx.fillText(label, w - 36, 76);
      ctx.textAlign = "center";
    }
    this.signTexture.needsUpdate = true;
  }

  private applyPlaqueFrameStyle(): void {
    if (!this.signFrameMesh) return;
    const mat = this.signFrameMesh.material as THREE.MeshStandardMaterial;
    const frame = PLAQUE_FRAME[this.currentSignStyle.plaqueStyle] ?? PLAQUE_FRAME.dark;
    mat.color.setHex(frame);
  }

  /** Apply a theme color set to the GROUND floor's wall + floor
   * materials. Used as the default / legacy entry point — Engine
   * calls this on startup with the player's saved Floor 0 theme. */
  setTheme(theme: { wallColor: number; floorColor: number }): void {
    if (this.wallMat) this.wallMat.color.setHex(theme.wallColor);
    if (this.floorMat) this.floorMat.color.setHex(theme.floorColor);
  }

  /** Apply a theme to a specific storey. Floor 0 → ground floor's
   * shared materials (same as `setTheme`). Floor 1..N → that storey's
   * cloned wall + slab materials so the other floors are unaffected. */
  setStoreyTheme(floor: number, theme: { wallColor: number; floorColor: number }): void {
    if (floor <= 0) {
      this.setTheme(theme);
      return;
    }
    const storey = this.upperStoreys.get(floor);
    if (!storey) return;
    storey.wallMat.color.setHex(theme.wallColor);
    storey.slabMat.color.setHex(theme.floorColor);
  }

  /** Map demo placements → their tier section so we can show / hide as
   * the player expands. Tier 0 = always visible (kitchen, door, decor).
   * Tier 1 = the starter dining (tables 1 + 2). Tier 2..4 = the
   * progressively-unlocked dining tables. */
  private readonly tierGroups = new Map<number, THREE.Object3D[]>();
  private currentTierVisible = 5;

  /** Render placeholder shells for the OTHER players' plots on the
   * shared city map. The player's own restaurant continues to use
   * the legacy origin-centered render path; this just adds visual
   * "other people's plots exist" feedback so the world reads as
   * a city rather than a single isolated building.
   *
   * Shells are simple Greek-Island-styled cubes (whitewashed walls,
   * terracotta roof, stone foundation) sized to match plot
   * dimensions. Each is positioned at the building's (plot_x,
   * plot_z) on the world grid. Player's own plot is skipped here
   * — the actual restaurant goes there via the existing pipeline.
   *
   * Called by Engine once the SpacetimeDB cache lands. Safe to
   * call again on cache updates (e.g. another player claims a
   * plot) — fully rebuilds the group. */
  populateCityBuildings(
    buildings: readonly { id: bigint; kind: string; plotX: number; plotZ: number; plotW: number; plotH: number; isMine: boolean; ownerIdentity?: { toHexString(): string }; ownerName?: string; lifecycle?: string; vacant?: boolean }[],
    skipMine: boolean = true,
  ): void {
    // Wipe + rebuild — buildings change rarely so the cost is fine.
    while (this.cityBuildings.children.length > 0) {
      const c = this.cityBuildings.children[0];
      this.cityBuildings.remove(c);
    }
    for (const b of buildings) {
      // Always add the garden fence, even for the player's own plot
      // (the legacy player block at origin has its own fence built
      // by addGardenArea(); this group adds fences for the OTHER 11
      // plots regardless of ownership).
      if (skipMine && b.isMine) continue;
      // Phase M.34 — a VACANT plot in the viewer's neighborhood shows just the
      // garden fence (an open lot), no shell + not visitable.
      if (b.vacant) {
        this.cityBuildings.add(this.makePlotGardenFence({ x: b.plotX, z: b.plotZ, w: b.plotW, h: b.plotH }));
        continue;
      }
      const shell = this.makeBuildingShell(b);
      // Phase M.34 — a COLD restaurant (owner away > 1 week) reads as dimmed.
      if (b.lifecycle === "cold") this.dimShell(shell);
      // Stamp the visit-mode metadata on the shell + every descendant
      // mesh so Engine's click raycast can identify which player owns
      // the plot the click landed on. Walking up the parent chain in
      // Engine yields the building info regardless of which mesh the
      // raycaster topped out on.
      shell.userData.visitPlot = {
        id: b.id,
        plotX: b.plotX,
        plotZ: b.plotZ,
        ownerHex: b.ownerIdentity?.toHexString?.() ?? "",
        ownerName: b.ownerName ?? "",
      };
      this.cityBuildings.add(shell);
      this.cityBuildings.add(this.makePlotGardenFence({
        x: b.plotX, z: b.plotZ, w: b.plotW, h: b.plotH,
      }));
    }
  }

  /** Phase M.34 — darken a shell in place (cloning materials so shared caches
   * aren't affected) to read a restaurant as "cold" — its owner is away. */
  private dimShell(shell: THREE.Group): void {
    shell.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.material) return;
      mesh.material = Array.isArray(mesh.material)
        ? mesh.material.map((m) => this.dimMat(m))
        : this.dimMat(mesh.material);
    });
  }

  private dimMat(mat: THREE.Material): THREE.Material {
    const c = mat.clone();
    const col = (c as THREE.MeshStandardMaterial).color;
    if (col) col.multiplyScalar(0.5);
    return c;
  }

  /** Build one Parisian Haussmann-style placeholder shell — cream
   * limestone walls with horizontal cornices between floors, tall
   * narrow shuttered windows on the street face, slim iron balconies
   * on upper storeys, a slate-grey mansard roof + chimney, and a
   * heavy dark-wood door. Same procedural box-and-band toolkit as
   * makeSceneryHouse just at plot scale. */
  private makeBuildingShell(b: { id?: bigint; kind: string; plotX: number; plotZ: number; plotW: number; plotH: number }): THREE.Group {
    const g = new THREE.Group();
    // Heights derived from kind — small = 1 storey, medium = 2,
    // large = 3. Each "storey" is 3 m (matches the player's own
    // building's STOREY_HEIGHT) so other plots read at the same
    // scale as the player's house when they visit later.
    const storeys = b.kind === "small" ? 1 : b.kind === "medium" ? 2 : 3;
    const wallH = storeys * 3.0;
    const w = b.plotW;
    const h = b.plotH;
    // Deterministic per-plot colour variation — use the plot id as
    // a seed so the same building always wears the same coat across
    // reloads / clients.
    const seed = Number((b.id ?? BigInt(Math.floor(b.plotX * 73 + b.plotZ * 41))) & BigInt(0xFFFFFFFF));
    const r = mulberry32(seed || 1);
    const wallShades = [0xe6d5b5, 0xddc9a8, 0xe8d8b8, 0xd6c2a0, 0xefe0c0, 0xddc9b0];
    const roofShades = [0x3a4048, 0x2e343c, 0x444a52, 0x363c44];
    const doorShades = [0x3a261a, 0x4a3020, 0x2a1a10, 0x5a4030];
    const pickFrom = <T,>(arr: readonly T[]): T => arr[Math.floor(r() * arr.length)];
    const wallColor = pickFrom(wallShades);
    const roofColor = pickFrom(roofShades);
    const doorColor = pickFrom(doorShades);
    // ── Cobblestone foundation (slightly larger than the building) ──
    const foundation = new THREE.Mesh(
      new THREE.BoxGeometry(w + 1, 0.2, h + 1),
      new THREE.MeshStandardMaterial({ color: 0x9a9088, roughness: 0.95, metalness: 0 }),
    );
    foundation.position.y = 0.1;
    foundation.receiveShadow = true;
    g.add(foundation);
    // ── Cream limestone walls ───────────────────────────────────────
    const walls = new THREE.Mesh(
      new THREE.BoxGeometry(w, wallH, h),
      new THREE.MeshStandardMaterial({ color: wallColor, roughness: 0.85, metalness: 0 }),
    );
    walls.position.y = 0.2 + wallH / 2;
    walls.castShadow = true;
    walls.receiveShadow = true;
    g.add(walls);
    // ── Horizontal cornices between floors ─────────────────────────
    const corniceMat = new THREE.MeshStandardMaterial({ color: 0xc8b888, roughness: 0.8 });
    for (let s = 1; s < storeys; s += 1) {
      const cornice = new THREE.Mesh(
        new THREE.BoxGeometry(w + 0.2, 0.14, h + 0.2),
        corniceMat,
      );
      cornice.position.y = 0.2 + s * 3.0;
      cornice.castShadow = true;
      g.add(cornice);
    }
    // ── Windows on the +Z (street) face ────────────────────────────
    const windowMat = new THREE.MeshStandardMaterial({
      color: 0x223040, roughness: 0.35, metalness: 0.3,
      emissive: 0x1a2538, emissiveIntensity: 0.05,
    });
    const numWin = Math.max(3, Math.floor(w / 1.6));
    const winSpacing = w / (numWin + 1);
    for (let s = 0; s < storeys; s += 1) {
      const winY = 0.2 + s * 3.0 + 1.7;
      for (let i = 0; i < numWin; i += 1) {
        const wx = -w / 2 + (i + 1) * winSpacing;
        // Centre window on the ground floor is the door slot.
        if (s === 0 && Math.abs(wx) < 0.55) continue;
        const win = new THREE.Mesh(
          new THREE.BoxGeometry(0.7, 1.45, 0.05),
          windowMat,
        );
        win.position.set(wx, winY, h / 2 + 0.028);
        g.add(win);
      }
      // ── Wrought-iron balcony rail on upper floors ────────────────
      // Single horizontal bar — was originally rail + ~6 individual
      // balusters per house. With 180 scenery houses that's >1000
      // baluster meshes for the whole city. At iso distance the
      // balusters were almost invisible anyway; dropping them is a
      // free perf win.
      if (s > 0) {
        const balconyMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.4, metalness: 0.7 });
        const rail = new THREE.Mesh(
          new THREE.BoxGeometry(w - 0.3, 0.06, 0.10),
          balconyMat,
        );
        rail.position.set(0, winY - 0.85, h / 2 + 0.12);
        g.add(rail);
      }
    }
    // ── Heavy dark-wood double door, centre-bottom of +Z face ──────
    const door = new THREE.Mesh(
      new THREE.BoxGeometry(1.05, 2.25, 0.06),
      new THREE.MeshStandardMaterial({ color: doorColor, roughness: 0.55, metalness: 0.05 }),
    );
    door.position.set(0, 0.2 + 1.125, h / 2 + 0.035);
    g.add(door);
    // Two small brass knockers (just decor cylinders) — a Parisian
    // signature on doors of this size.
    const brassMat = new THREE.MeshStandardMaterial({ color: 0xc8a248, roughness: 0.4, metalness: 0.75 });
    for (const dx of [-0.2, 0.2]) {
      const knocker = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.04, 8), brassMat);
      knocker.position.set(dx, 0.2 + 1.4, h / 2 + 0.07);
      knocker.rotation.x = Math.PI / 2;
      g.add(knocker);
    }
    // ── Top cornice + mansard roof ─────────────────────────────────
    const topCornice = new THREE.Mesh(
      new THREE.BoxGeometry(w + 0.3, 0.14, h + 0.3),
      corniceMat,
    );
    topCornice.position.y = 0.2 + wallH + 0.07;
    topCornice.castShadow = true;
    g.add(topCornice);
    const mansardH = 1.1;
    const mansard = new THREE.Mesh(
      new THREE.BoxGeometry(w + 0.2, mansardH, h + 0.2),
      new THREE.MeshStandardMaterial({ color: roofColor, roughness: 0.5, metalness: 0.05 }),
    );
    mansard.position.y = 0.2 + wallH + 0.14 + mansardH / 2;
    mansard.castShadow = true;
    g.add(mansard);
    const cap = new THREE.Mesh(
      new THREE.BoxGeometry(w - 0.6, 0.1, h - 0.6),
      new THREE.MeshStandardMaterial({ color: 0x2a3038, roughness: 0.55 }),
    );
    cap.position.y = 0.2 + wallH + 0.14 + mansardH + 0.05;
    g.add(cap);
    // ── Chimney (terracotta brick) ─────────────────────────────────
    const chimneyMat = new THREE.MeshStandardMaterial({ color: 0x9a6850, roughness: 0.9 });
    const chimney = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 1.1, 0.4),
      chimneyMat,
    );
    chimney.position.set(w / 2 - 0.8, 0.2 + wallH + 0.14 + mansardH + 0.55, -h / 2 + 0.8);
    chimney.castShadow = true;
    g.add(chimney);
    // ── Tiny "address number" plate just left of the door ──────────
    g.position.set(b.plotX, 0, b.plotZ);
    return g;
  }

  /** Build a wooden picket fence around the plot's adjacent garden
   * (computed by gardenBoundsForPlot). Mirrors the player's legacy
   * garden style so every plot in the city reads as "house + fenced
   * garden" rather than the player being uniquely privileged. Pure
   * decor — no gameplay interaction yet. */
  private makePlotGardenFence(plot: { x: number; z: number; w: number; h: number }): THREE.Group {
    const g = new THREE.Group();
    const b = WorldScene.gardenBoundsForPlot(plot);
    const postMat = new THREE.MeshStandardMaterial({ color: 0x8a5a30, roughness: 0.9 });
    const railMat = new THREE.MeshStandardMaterial({ color: 0xa07042, roughness: 0.9 });
    const capMat  = new THREE.MeshStandardMaterial({ color: 0x6a4220, roughness: 0.85 });
    const postH = 1.0, postW = 0.09;
    const railH = 0.05, railD = 0.05;
    const upperY = 0.74, lowerY = 0.28;
    const spacing = 1.0;
    const addPost = (x: number, z: number): void => {
      const post = new THREE.Mesh(new THREE.BoxGeometry(postW, postH, postW), postMat);
      post.position.set(x, postH / 2, z);
      post.castShadow = true;
      g.add(post);
      const cap = new THREE.Mesh(new THREE.BoxGeometry(postW * 1.4, 0.06, postW * 1.4), capMat);
      cap.position.set(x, postH + 0.03, z);
      g.add(cap);
    };
    for (let x = b.minX; x <= b.maxX; x += spacing) {
      addPost(x, b.minZ);
      addPost(x, b.maxZ);
    }
    for (let z = b.minZ + spacing; z < b.maxZ; z += spacing) {
      addPost(b.minX, z);
      addPost(b.maxX, z);
    }
    const addRail = (x: number, z: number, length: number, axis: "x" | "z", y: number): void => {
      const geom = axis === "x"
        ? new THREE.BoxGeometry(length, railH, railD)
        : new THREE.BoxGeometry(railD, railH, length);
      const rail = new THREE.Mesh(geom, railMat);
      rail.position.set(x, y, z);
      rail.castShadow = true;
      g.add(rail);
    };
    const cx = (b.minX + b.maxX) / 2;
    const cz = (b.minZ + b.maxZ) / 2;
    const width = b.maxX - b.minX;
    const depth = b.maxZ - b.minZ;
    addRail(cx, b.minZ, width, "x", upperY);
    addRail(cx, b.minZ, width, "x", lowerY);
    addRail(cx, b.maxZ, width, "x", upperY);
    addRail(cx, b.maxZ, width, "x", lowerY);
    addRail(b.minX, cz, depth, "z", upperY);
    addRail(b.minX, cz, depth, "z", lowerY);
    addRail(b.maxX, cz, depth, "z", upperY);
    addRail(b.maxX, cz, depth, "z", lowerY);
    // A subtle grass-only patch sits inside the fence — the lawn plane
    // already provides the green, so no need to overlay another plane.
    return g;
  }

  private async populateDemoRestaurant(): Promise<void> {
    // NOTE: demoReady/staffReady promises are created in the constructor
    // — see the field declarations. We only RESOLVE them here, never
    // re-create them, so the engine's `.then(...)` references stay live.
    // A brand-new restaurant is EMPTY — nothing but the front door.
    //
    // The tutorial hands the player the keys to a bare room and walks them
    // through furnishing it themselves (table → chairs → stove → counter →
    // appliance → fridge → sink) and hiring their first crew, funded by the
    // $10,000 opening grant (claim_starter_grant's first-grant branch). Pre-
    // placing a stove/sink/table would step on every one of those lessons and
    // contradict the chef's opening line ("nothing and nobody inside").
    //
    // The DOOR stays: guests spawn on the pavement and walk in through it, so
    // without one the restaurant simply can't take customers — that's plumbing,
    // not furniture, and it isn't something we ask the player to discover.
    //
    // NB: this only affects BRAND-NEW saves. Engine registers these demo
    // placements solely when the restored save has no furniture, so existing
    // players keep everything they own.
    const placements: { id: string; x: number; z: number; rotY?: number; tier?: number }[] = [
      // Front door (entrance — guests spawn outside and walk through it).
      // z=5.5 lines up with the (+0.5, +0.5)-shifted front wall.
      // Anchored at half-integer here because the door mesh's frame is
      // centered at its local origin and needs to sit ON the wall plane
      // rather than half a tile inside the building.
      { id: "door",         x:  0, z:  5.5, rotY: Math.PI },
    ];

    // Boot diagnostic — list every starter placement id the build is
    // actually running with. Player can grep DevTools for this to
    // confirm whether a missing piece (e.g. bar-counter) is because
    // they're on an old cached bundle vs. a load failure.
    console.log(`[WorldScene] starter placements: ${placements.map((p) => p.id).join(", ")}`);
    await Promise.all(placements.map(async (p) => {
      const def = getFurnitureDef(p.id);
      if (!def) {
        console.warn(`Unknown furniture id: ${p.id}`);
        return;
      }
      try {
        const model = await this.loader.load(def.modelPath);
        fitFurniture(model, def);
        model.position.set(p.x, model.position.y, p.z);
        if (p.rotY != null) model.rotation.y = p.rotY;
        // Wall-hug pass — same one BuildMenu + FurnitureRegistry.restore
        // use, so the demo placements match player + load behaviour.
        snapToAdjacentWall(model, def);
        this.threeScene.add(model);
        this.demoPlacements.push({ defId: p.id, x: p.x, z: p.z, rotY: p.rotY ?? 0, model });
        // Group by tier so we can hide locked sections.
        const tier = p.tier ?? 0;
        if (!this.tierGroups.has(tier)) this.tierGroups.set(tier, []);
        this.tierGroups.get(tier)!.push(model);
        // Phase M.31 — the front door is now animated per-door by
        // Engine.updateInteriorDoorways (reading model.userData.panel keyed by
        // uid), same as interior doorways. We deliberately do NOT capture
        // this.doorPanel anymore: leaving it null keeps the legacy single-door
        // lerp in update() a no-op, so the two mechanisms can't fight.
        // Per-stove flames are reconciled every frame by the Engine via
        // syncStoveFlames(registry.getCookingStoves()) — no need to pin
        // one here. Demo-stove placements show up in the registry just
        // like player placements and are picked up on the next frame.
      } catch (err) {
        console.error(`Failed to load ${def.id} (${def.modelPath})`, err);
      }
    }));

    this.buildTierMarkers();
    this.resolveDemoReady();
    await this.populateCharacters();
  }

  /** Tier-lock markers (the colored discs + glowing pillars) were removed
   * in the starter-restaurant revamp — they only made sense alongside
   * the pre-placed demo dining sections, which no longer exist. The
   * field is kept (empty) for save-time backwards compatibility, but
   * nothing is rendered. */
  private buildTierMarkers(): void {
    // Intentionally empty.
  }

  /** Show/hide tier-locked dining sections based on the player's current
   * expansion. Called by Engine on init and after Game.buyExpansion.
   * Also drives the multi-storey reveal — each tier past 1 unlocks one
   * upper storey (T2 → storey 1, T3 → storey 2, T4 → storey 3,
   * T5 → storey 4). The roof shows as long as ANY upper storey is up. */
  setLuxuryTier(tier: number): void {
    this.currentTierVisible = tier;
    for (const [tierKey, items] of this.tierGroups) {
      if (tierKey === 0) continue;
      const visible = tierKey <= tier;
      for (const obj of items) obj.visible = visible;
    }
    this.applyStoreyVisibility();
  }

  /** Switch the camera-focus floor. Triggers a visibility re-pass so
   * the storeys ABOVE the new focus disappear (guarantees zero
   * obstruction of the focused floor) and at-or-below ones show. */
  setFocusedStorey(idx: number): void {
    const clamped = Math.max(0, Math.min(WorldScene.NUM_STOREYS - 1, idx));
    if (clamped === this.focusedStorey) return;
    this.focusedStorey = clamped;
    this.applyStoreyVisibility();
  }

  /** Recompute which upper storeys + roof render based on the current
   * tier (which ones are UNLOCKED), the focused storey (anything
   * above is HIDDEN so it can't obscure the focused floor), and
   * whether the player is in exterior mode (zoomed out past 40%,
   * in which case we show every UNLOCKED storey + the roof so the
   * building reads as a closed structure). Called from
   * setLuxuryTier, setFocusedStorey, and setExteriorMode. */
  private applyStoreyVisibility(): void {
    const tier = this.currentTierVisible;
    for (const [storeyIdx, storey] of this.upperStoreys) {
      const unlocked = tier >= storeyIdx + 1;
      const atOrBelow = storeyIdx <= this.focusedStorey;
      // Exterior: every unlocked storey visible.
      // Interior: only at-or-below the focused storey.
      storey.group.visible = unlocked && (this.exteriorMode || atOrBelow);
    }
    // Staircases: each flight is parented to the storey it LEAVES
    // FROM, so the lower storey's group visibility already gates focus
    // for flights 2..N. We just need to gate by tier here — a flight
    // only makes sense if its destination storey exists. For flight 1
    // (ground → Floor 1) the parent is the main scene, so this is the
    // only visibility control it gets.
    for (const [stairIdx, stairGroup] of this.stairFlights) {
      stairGroup.visible = tier >= stairIdx + 1;
    }
    // Boarded-up placeholder is the INVERSE of real flight-1: shown only while
    // Floor 1 is still locked (tier 1). At tier≥2 the real flight takes over.
    if (this.brokenStairwell) this.brokenStairwell.visible = tier < 2;
    // Legacy flat roof — kept hidden; the Paris mansard below takes
    // over the visual role.
    if (this.buildingRoof) {
      this.buildingRoof.visible = false;
    }
    // Mansard roof + cap + chimney sit on top of the topmost UNLOCKED
    // storey so the building visibly grows as the player buys
    // expansions. Exterior-only — in interior view the camera looks
    // down into the focused floor, so no roof should occlude it.
    const mansardBaseY = tier * WorldScene.STOREY_HEIGHT;
    const mansardH = 1.2;
    const showMansard = this.exteriorMode && tier > 0;
    if (this.parisMansard) {
      this.parisMansard.visible = showMansard;
      this.parisMansard.position.y = mansardBaseY + mansardH / 2;
    }
    if (this.parisMansardCap) {
      this.parisMansardCap.visible = showMansard;
      this.parisMansardCap.position.y = mansardBaseY + mansardH + 0.05;
    }
    if (this.parisMansardChimney) {
      this.parisMansardChimney.visible = showMansard;
      this.parisMansardChimney.position.y = mansardBaseY + mansardH + 0.55;
    }
    // Iron balconies — only show in exterior mode. In interior view
    // they read as spiky black noise outside the wall ghost (no value
    // when the player is doing close-up build work). Each balcony's
    // OWN storey-group visibility still gates them additionally, so
    // they only render when the storey they belong to is on-screen.
    for (const b of this.parisBalconies) {
      b.visible = this.exteriorMode;
    }
  }

  /** Toggle the sun's shadow casting wholesale. Cheaper than keeping
   * shadows on with no casters — three.js still pays render-target
   * setup costs even when no objects opt in to castShadow. Engine
   * calls this from the quality preset AND every frame based on
   * camera zoom (no shadows when zoomed out past the exterior
   * threshold; the player can't see them at that scale anyway). */
  setSunShadowsEnabled(enabled: boolean): void {
    if (!this.sunLight) return;
    if (this.sunLight.castShadow === enabled) return;
    this.sunLight.castShadow = enabled;
  }

  /** Walk a single model + its descendants and toggle every mesh's
   * castShadow flag. Used by Engine.applyGraphicsQuality to flip
   * furniture (loaded via ModelLoader, which defaults castShadow
   * to true on every mesh) without disturbing the hand-built
   * scene geometry whose shadow policies were tuned in their
   * own factory functions. */
  setShadowCastingOnSubtree(root: THREE.Object3D, enabled: boolean): void {
    root.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) {
        (obj as THREE.Mesh).castShadow = enabled;
      }
    });
  }

  /** Force Three.js to compile shader programs for every material in
   * the scene RIGHT NOW, including objects that are currently hidden
   * (upper storeys, the mansard roof, balconies, etc).
   *
   * Three.js compiles a fragment+vertex program lazily the first time
   * a given material/geometry combo is actually rendered. Upper
   * storeys start with `.visible = false`, so their materials never
   * get compiled at startup — and the first time the player clicks
   * a floor button to reveal Floor 1+, the renderer stalls for
   * ~50-300 ms compiling every fresh program. That's the "laggy"
   * feel when clicking floor buttons.
   *
   * Workaround: briefly flip every storey + balcony + roof visible,
   * gather their materials, restore visibility, and let the GPU build the
   * programs in the BACKGROUND via renderer.compileAsync (three r152+'s
   * KHR_parallel_shader_compile path) so the main thread never blocks.
   * Materials are gathered synchronously while flipped-visible, then
   * visibility is restored before the next render, so nothing flickers on
   * screen even though the programs finish compiling a few ms later.
   * Called once at startup AND anticipatorily as the player zooms out
   * toward the exterior threshold (Engine.tick), so the "reveal every
   * storey at once" exterior flip is already warm; re-compiling an
   * already-compiled program is a cache no-op, so re-firing is cheap. */
  precompileShaders(renderer: THREE.WebGLRenderer, camera: THREE.Camera): void {
    // Snapshot current visibility of everything we'll flip.
    type Snap = { obj: THREE.Object3D; visible: boolean };
    const snaps: Snap[] = [];
    const flip = (obj: THREE.Object3D | undefined | null): void => {
      if (!obj) return;
      snaps.push({ obj, visible: obj.visible });
      obj.visible = true;
    };
    for (const [, storey] of this.upperStoreys) flip(storey.group);
    for (const [, stair] of this.stairFlights) flip(stair);
    flip(this.parisMansard);
    flip(this.parisMansardCap);
    flip(this.parisMansardChimney);
    flip(this.buildingRoof);
    for (const b of this.parisBalconies) flip(b);
    try {
      // Async compile: gathers materials now (synchronously, while the
      // storeys above are flipped visible) but lets the GPU driver build
      // the programs in parallel, off the main thread. Restore happens
      // right below, before any render, so the flip is never seen.
      void renderer.compileAsync(this.threeScene, camera).catch((e) => {
        console.warn("[WorldScene] async precompile failed:", e);
      });
    } catch (e) {
      // Hard to imagine the synchronous gather failing, but a partial
      // compile leaves valid programs cached so the next render works.
      console.warn("[WorldScene] precompile failed:", e);
    }
    // Restore exactly what was visible before (synchronous — pre-render).
    for (const s of snaps) s.obj.visible = s.visible;
  }

  /** Switch the world between interior view (default; see-through walls,
   * focus-only storey visibility, hidden roof) and exterior view
   * (closed walls on every side, all unlocked storeys + roof visible
   * regardless of focus). Engine.tick toggles this based on the
   * camera's current zoom percentage. */
  setExteriorMode(on: boolean): void {
    if (this.exteriorMode === on) return;
    this.exteriorMode = on;
    // Re-apply storey/roof visibility immediately. Walls update on the
    // next frame via updateWallVisibility, which already reads the
    // mode flag.
    this.applyStoreyVisibility();
  }

  /** Whether the world is currently in exterior-only view (see
   * setExteriorMode). Used by Engine to keep the SFX bus in sync —
   * interior sounds are muted while exterior mode is active. */
  isExteriorMode(): boolean {
    return this.exteriorMode;
  }

  /** Position the shared city so the player's claimed plot lines up
   * with the restaurant's local origin. The player's tower stays at
   * (0, 0) in local coordinates; worldRoot is offset by (-plotX,
   * -plotZ) so neighbouring plot shells, avenues, and scenery sit at
   * their correct positions RELATIVE to the player. From a shared-
   * map perspective each player IS at their claimed coordinates;
   * each client renders the same absolute layout with a different
   * origin under the camera. */
  setOwnedPlotOffset(plotX: number, plotZ: number): void {
    this.worldRoot.position.set(-plotX, 0, -plotZ);
    this.ownedPlotAnchor.set(plotX, plotZ);
  }

  /** Current applied tier (used by the door animator). */
  getLuxuryTier(): number {
    return this.currentTierVisible;
  }

  /** Currently-focused storey (0 = ground). Used by FloorSelector to
   * know which button is "active". */
  getFocusedStorey(): number {
    return this.focusedStorey;
  }

  /** Constant — meters between adjacent floor slabs. The floor selector
   * uses this to compute the camera look-at lift per storey. */
  static getStoreyHeight(): number {
    return WorldScene.STOREY_HEIGHT;
  }

  /** Constant — how many storeys the building can grow to (ground + 4
   * upper). FloorSelector renders one button per storey index in
   * [0, NUM_STOREYS). */
  static getNumStoreys(): number {
    return WorldScene.NUM_STOREYS;
  }

  /** Return the THREE container a furniture item placed on `floor`
   * should be parented to. Ground floor (0) lives in the main scene
   * and is always visible; upper floors live in their storey group so
   * focus + tier visibility apply automatically. Used by the registry
   * + BuildMenu to mount per-floor placements into the right subtree. */
  getStoreyMount(floor: number): THREE.Object3D {
    if (floor <= 0) return this.threeScene;
    return this.upperStoreys.get(floor)?.group ?? this.threeScene;
  }

  /** Spawn an extra staff character at runtime (when player hires another).
   * Slots them in just to the right of the existing crew so they don't
   * overlap. `homeFloor` (default 0) parents the model into that
   * storey's mount so visibility tracks the focused floor. Returns the
   * AnimatedCharacter, or null if the GLB failed to load. */
  async spawnExtraStaff(role: "chef" | "waiter" | "errand" | "barman", offsetSlot: number, homeFloor = 0): Promise<AnimatedCharacter | null> {
    // Matches the new starter homes in populateCharacters — further south
    // so the walking distance to the kitchen line is large enough to read.
    // Barman spawns alongside the waiter line on the kitchen side; the
    // router immediately routes them to their bar counter via the idle
    // picker, so this is just a temporary "landing pad" pose.
    const homeByRole: Record<"chef" | "waiter" | "errand" | "barman", { x: number; z: number; facingY: number; action: CharacterAction }> = {
      // Reverted to original facingY values — the formula tweaks made
      // crab walking worse instead of better.
      chef:   { x: -1.5, z: -1.0, facingY: 0,            action: "idle" },
      waiter: { x:  1.5, z: -1.0, facingY: 0,            action: "idle" },
      barman: { x:  2.5, z: -1.0, facingY: 0,            action: "idle" },
      errand: { x:  3.5, z: -1.0, facingY: -Math.PI / 2, action: "idle" },
    };
    const base = homeByRole[role];
    // Lay hires out in ROWS that stay INSIDE the walls. The old single line
    // (base.x + slot*0.6) marched staff out PAST the +X wall (~5.2) at high
    // slot counts — they spawned AND homed outside, then clamped to the wall
    // edge and read as "staff standing outside / in the wall" (more visible
    // once the Pass-6 cutover stopped the client overriding the position).
    // Wrap to a new row (toward the back) once a row reaches the interior
    // margin; clamp z so deep rows don't punch the -Z wall.
    const MAX_X = 4.6; // body-width margin inside the +X wall (5.2)
    const SPACING = 0.6;
    const perRow = Math.max(1, Math.floor((MAX_X - base.x) / SPACING) + 1);
    const col = offsetSlot % perRow;
    const rowIdx = Math.floor(offsetSlot / perRow);
    const x = base.x + col * SPACING;
    const z = Math.max(-3.8, base.z - rowIdx * 0.75);
    try {
      // Staff now use their rigged GLB models (chef/waiter/errand); the barman
      // has no model yet, so it keeps the placeholder.
      const staffModel = riggedStaffModel(role);
      let model: THREE.Object3D;
      let skeletal: SkeletalDriver | undefined;
      if (staffModel) {
        const inst = await sharedRiggedLoader.createInstance(staffModel);
        model = inst.root; skeletal = inst.controller;
      } else {
        model = await this.characterLoader.load(role);
      }
      const animated: AnimatedCharacter = {
        root: model,
        groundPos: new THREE.Vector2(x, z),
        facingY: base.facingY,
        action: base.action,
        phase: Math.random() * 5,
        seatHeight: 0.5,
        skeletal,
      };
      // Lift the model's Y by the home-floor's slab so an upper-floor
      // chef stands ON Floor 1+ instead of floating at y=0. Parent it
      // into the storey mount so the storey-visibility rules apply.
      model.position.y += homeFloor * WorldScene.STOREY_HEIGHT;
      this.getStoreyMount(homeFloor).add(model);
      this.animator.add(animated);
      return animated;
    } catch (err) {
      console.warn(`Failed to spawn extra ${role}:`, err);
      return null;
    }
  }

  /** Re-parent a previously spawned staff character to a different
   * floor. Adjusts the model's Y by the storey-height delta and moves
   * it under the new storey's mount. Called by Engine when the player
   * changes a member's homeFloor in StaffPanel. */
  relocateStaff(character: AnimatedCharacter, fromFloor: number, toFloor: number): void {
    if (fromFloor === toFloor) return;
    const dy = (toFloor - fromFloor) * WorldScene.STOREY_HEIGHT;
    character.root.position.y += dy;
    // ALSO bump _baseY by the same delta so the CharacterAnimator's
    // per-frame "reset position.y = _baseY" doesn't snap the body back
    // to the storey we just moved them off of. For IDLE staff (mover
    // isn't running, so it can't keep _baseY in sync via the multi-
    // floor walk logic) this is the only place _baseY gets updated
    // for a floor change.
    if (character._baseY != null) character._baseY += dy;
    const newMount = this.getStoreyMount(toFloor);
    if (character.root.parent !== newMount) newMount.add(character.root);
  }

  /** Re-parent a character to a different storey's mount without
   * touching its Y. Used by StaffRouter / GuestSpawner when an actor
   * crosses the staircase: the mover has already set the body's world Y
   * to the new slab via the multi-floor path's stair interpolation, we
   * just need the model to live under the new floor's group so storey
   * focus visibility shows it on the right floor. */
  reparentCharacterToFloor(character: AnimatedCharacter, toFloor: number): void {
    const newMount = this.getStoreyMount(toFloor);
    if (character.root.parent !== newMount) {
      // THREE.Object3D.add preserves local position, which is what we
      // want — caller has already set world Y via the move interp.
      newMount.add(character.root);
    }
  }

  /** Place the static staff models (chef + waiter + errand) at the kitchen
   * line. Guests are spawned dynamically by GuestSpawner — they're not
   * placed here. */
  private async populateCharacters(): Promise<void> {
    // Spread the kitchen crew further apart and out into the dining-side
    // of the room. With everyone clustered at z=-2.6 (one row away from
    // the stove at z=-3) the chef's working walks were ~0.6 units, easy
    // to miss. New home positions put the chef ~2 units south of the
    // stove and the waiter ~2.5 from the pickup point — every ticket now
    // forces a clearly visible cross-kitchen trip.
    const staff: { id: string; x: number; z: number; facingY: number; action: CharacterAction }[] = [
      // Reverted to original facingY values — see spawnExtraStaff above.
      { id: "chef",   x: -1.5, z: -1.0, facingY: 0,            action: "idle"  },
      { id: "waiter", x:  1.5, z: -1.0, facingY: 0,            action: "idle"  },
      { id: "errand", x:  3.5, z: -1.0, facingY: -Math.PI / 2, action: "idle"  },
    ];

    // staffReady promise is created in the constructor (above) so any
    // synchronous consumer that grabbed the reference up-front waits for
    // this resolution rather than the initial sentinel. We only resolve
    // it here.

    await Promise.all(staff.map(async (c) => {
      try {
        const staffModel = riggedStaffModel(c.id);
        let model: THREE.Object3D;
        let skeletal: SkeletalDriver | undefined;
        if (staffModel) {
          const inst = await sharedRiggedLoader.createInstance(staffModel);
          model = inst.root; skeletal = inst.controller;
        } else {
          model = await this.characterLoader.load(c.id);
        }
        const animated: AnimatedCharacter = {
          root: model,
          groundPos: new THREE.Vector2(c.x, c.z),
          facingY: c.facingY,
          action: c.action,
          phase: Math.random() * 5,
          seatHeight: 0.5,
          skeletal,
        };
        this.threeScene.add(model);
        this.animator.add(animated);
        if (c.id === "chef") this.chefChar = animated;
        if (c.id === "waiter") this.waiterChar = animated;
        if (c.id === "errand") this.errandChar = animated;
      } catch (err) {
        console.warn(`Character ${c.id} unavailable:`, err);
      }
    }));
    console.log(`[WorldScene] populateCharacters done: chef=${this.chefChar ? "OK" : "MISSING"} waiter=${this.waiterChar ? "OK" : "MISSING"} errand=${this.errandChar ? "OK" : "MISSING"} — resolving staffReady`);
    this.resolveStaffReady();
  }

  /**
   * Whether each primary staff body is "owned" — i.e. it's attached to the
   * router as roster member #0 of its role. True for a loaded save that has
   * that role; FALSE for a brand-new restaurant whose roster is empty. Engine
   * sets these once the roster is known (staffReady). Default true so nothing
   * regresses before they're set.
   */
  chefBodyOwned = true;
  waiterBodyOwned = true;
  errandBodyOwned = true;

  /**
   * Keep an UNOWNED primary staff body hidden.
   *
   * populateCharacters builds chef/waiter/errand unconditionally — they're
   * singletons the seat-count gate and the server lerp reference, so they can't
   * simply not exist. For a loaded save each base body IS member #0 and renders
   * normally (this method leaves it alone). For a brand-new restaurant the
   * roster is empty, the base body is nobody, and the first hire spawns its own
   * body — so the base one must stay hidden. Called every frame because the
   * animator can re-show a model; three boolean checks, negligible.
   *
   * Only ever force-hides; never shows. That way it never fights the existing
   * benched/despawn visibility logic on owned bodies.
   */
  syncPrimaryStaffVisibility(): void {
    // Force-hide via `_keepHidden`, NOT a bare `root.visible = false`. The
    // CharacterAnimator's per-frame cull re-shows any in-view body (sets
    // root.visible = true, CharacterAnimator.ts:400) AFTER this runs — so a
    // plain visibility flag was overwritten every single frame, which is why
    // the base chef/waiter/errand kept standing in a fresh, empty-roster
    // restaurant no matter what. `_keepHidden` is the flag the animator honors
    // (forces show=false, CharacterAnimator.ts:369). Only ever force-HIDES an
    // unowned body; an owned body is left untouched so the ErrandRouter /
    // GuestSpawner visibility logic on it is never fought.
    if (!this.chefBodyOwned && this.chefChar) { this.chefChar._keepHidden = true; this.chefChar.root.visible = false; }
    if (!this.waiterBodyOwned && this.waiterChar) { this.waiterChar._keepHidden = true; this.waiterChar.root.visible = false; }
    if (!this.errandBodyOwned && this.errandChar) { this.errandChar._keepHidden = true; this.errandChar.root.visible = false; }
  }
}
